import React, { useState, useEffect, useMemo, useRef } from 'react';
import { managerIntelAPI, playersAPI } from '../../utils/api';
import DynastyYoYSection from './DynastyYoYSection';
import { getKTCPickIdFromPick } from '../../utils/ktcPickIds';
import { useCountUp, useReveal } from '../../shell/motion';

/**
 * DossierSection — "The Brief": a tabbed dossier for a Sleeper manager.
 *
 * Tabs: Read · Drafts · Network · History · Portfolio.
 *  - Read     — dark verdict hero + 6-axis radar, vitals, tells, the play,
 *               situation per league, and NFL-team exposure.
 *  - Drafts   — a career draft profile band + per-draft board GRID (picks lit
 *               in their real snake slots) + on-the-clock trades + current class.
 *  - Network  — top trade partners (deduped by Sleeper userId, backend-side) +
 *               concentration donut.
 *  - History  — monthly trade chart (in-season vs offseason, hover counts) +
 *               full scrollable trades list.
 *  - Portfolio— the broader all-leagues Dynasty YoY view (career band, league
 *               history timeline, year-by-year).
 *
 * Data wiring:
 *  - getDossier(username)    → drives Read/Drafts/Network (hud, archetype,
 *                              fingerprint, traits, situation, timing, paperTrail,
 *                              gates, exploit, h2h).
 *  - `leagues` prop          → leagues[].currentRoster (for NFL exposure), passed
 *                              down from the parent's own getProfile() fetch
 *                              rather than re-fetched here (avoids a second,
 *                              redundant call to that expensive endpoint).
 *  - getTrades(username)     → full trades list for History.
 *  - getDynastyYoY(username) → Portfolio tab (rendered via <DynastyYoYSection>).
 *  - playersAPI.getBatch     → resolve Sleeper player names/teams/positions.
 *
 * Doctrine: never invent. Every value comes from a real field; absent data
 * renders an honest empty state, never a fabricated number. On dossier fetch
 * error we render nothing rather than break the host page. The secondary
 * fetches (trades, profile, dynasty) fail soft — their tabs show empty states.
 */

// ---------------------------------------------------------------------------
// Scoped styles — translated from the v3-brief mockup into the .ftd namespace,
// using the shell's --ft-* tokens with hardcoded fallbacks.
// ---------------------------------------------------------------------------

const CSS = `
.ftd{--b:var(--ft-brand,#FF7A00);--bs:var(--ft-brand-soft,rgba(255,122,0,.13));--bi:var(--ft-brand-ink,#C25E00);
  --ink:var(--ft-ink,#1D1D1F);--i2:var(--ft-ink-2,#6E6E73);--i3:var(--ft-ink-3,#86868B);
  --surf:var(--ft-surface,#F5F5F7);--surf2:#EBEBED;--hair:var(--ft-hairline,#EBEBED);
  --green:var(--ft-green,#34C759);--gsoft:var(--ft-green-soft,rgba(52,199,89,.14));--gi:var(--ft-green-ink,#1B873F);
  --red:var(--ft-red,#FF3B30);font-variant-numeric:tabular-nums;margin-top:8px}
.ftd .num{font-variant-numeric:tabular-nums}
.ftd.rail{margin-top:0}
.ftd .jl{display:flex;width:100%;text-align:left;padding:9px 11px;border-radius:10px;font-size:13px;font-weight:500;color:var(--i2);background:none;border:none;cursor:pointer;font:inherit}
.ftd .jl:hover{background:var(--surf);color:var(--ink)}
.ftd .conf{display:flex;align-items:center;gap:5px;margin-top:11px;font-size:10.5px;letter-spacing:.06em;color:rgba(255,255,255,.5);text-transform:uppercase}
.ftd .conf i{width:7px;height:7px;border-radius:50%;background:rgba(255,255,255,.22)}
.ftd .conf i.f{background:var(--b)}
.ftd .card{background:var(--surf);border-radius:16px;padding:22px}
.ftd .card.dark{background:var(--ink);color:#fff}
/* Rail scout-card fill */
.ftd .rail-sec{margin-top:18px}
.ftd .rail-k{display:block;font-size:10px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:var(--i3);margin-bottom:9px}
.ftd .rail-chans{display:flex;flex-wrap:wrap;gap:6px}
.ftd .rail-chan{font-size:11.5px;font-weight:600;color:var(--bi);background:var(--bs);border-radius:980px;padding:4px 11px}
.ftd .rail-read{font-size:13px;line-height:1.45;color:var(--ink);font-weight:500;background:var(--surf);border-radius:12px;padding:12px 14px;border-left:3px solid var(--b)}
.ftd .rail-glance{display:grid;grid-template-columns:1fr 1fr;gap:8px}
.ftd .rail-stat{background:var(--surf);border-radius:11px;padding:10px 12px;display:flex;flex-direction:column;gap:1px}
.ftd .rail-stat b{font-size:18px;font-weight:600;letter-spacing:-.02em;color:var(--ink)}
.ftd .rail-stat span{font-size:11px;color:var(--i3)}

/* kicker */
.ftd .kicker{font-size:11px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:var(--bi);display:flex;align-items:center;gap:8px;margin:0 0 12px}
.ftd .kicker .seq{color:var(--b)}
.ftd .kicker .ln{flex:1;height:1px;background:var(--hair)}

/* verdict hero */
.ftd .verdict{background:var(--ink);color:#fff;border-radius:18px;overflow:hidden;display:grid;grid-template-columns:1.45fr .9fr;gap:0}
.ftd .verdict .vleft{padding:28px 30px 26px}
.ftd .verdict .vright{padding:22px 24px;display:flex;align-items:center;justify-content:center;border-left:1px solid rgba(255,255,255,.09);position:relative}
.ftd .vkick{font-size:11px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:var(--b);margin-bottom:14px;display:flex;gap:8px}
.ftd .vkick .seq{color:rgba(255,255,255,.5)}
.ftd .archetype{font-size:42px;font-weight:600;letter-spacing:-.03em;line-height:1}
.ftd .tagline{font-size:16px;color:rgba(255,255,255,.66);margin-top:12px;max-width:38ch;line-height:1.42}
.ftd .conf-row{display:flex;align-items:center;gap:12px;margin-top:20px;flex-wrap:wrap}
.ftd .conf-label{font-size:11px;font-weight:600;letter-spacing:.1em;text-transform:uppercase;color:rgba(255,255,255,.52)}
.ftd .pips{display:flex;gap:5px}
.ftd .pip{width:22px;height:5px;border-radius:3px;background:rgba(255,255,255,.18)}
.ftd .pip.on{background:var(--b)}
.ftd .conf-word{font-size:12.5px;font-weight:600;color:#fff;letter-spacing:.02em}
.ftd .fchips{display:flex;flex-wrap:wrap;gap:8px;margin-top:22px}
.ftd .fchip{display:inline-flex;align-items:center;gap:6px;padding:7px 12px;border-radius:980px;background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.1);font-size:12.5px;font-weight:600;letter-spacing:-.01em;color:rgba(255,255,255,.92)}
.ftd .fchip .d{width:5px;height:5px;border-radius:50%;background:var(--b)}
.ftd .radar-cap{position:absolute;top:18px;right:22px;font-size:10.5px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:rgba(255,255,255,.4)}
@media(max-width:760px){.ftd .verdict{grid-template-columns:1fr}.ftd .verdict .vright{border-left:none;border-top:1px solid rgba(255,255,255,.09)}.ftd .archetype{font-size:34px}}

/* The Blueprint (bodygraph card) */
.ftd .bp{background:var(--ink);color:#fff;border-radius:18px;overflow:hidden;margin-top:22px}
.ftd .bp-cover{display:grid;grid-template-columns:.9fr 1.1fr}
.ftd .bp-graph{padding:24px 10px;display:flex;flex-direction:column;align-items:center;justify-content:center;border-right:1px solid rgba(255,255,255,.08)}
.ftd .bp-legend{display:flex;gap:18px;margin-top:6px}
.ftd .bp-legend span{display:inline-flex;align-items:center;gap:8px;font-size:11px;font-weight:600;color:rgba(255,255,255,.6)}
.ftd .bp-legend .sw{width:13px;height:13px;border-radius:4px}
.ftd .bp-legend .sw.def{background:var(--b)}
.ftd .bp-legend .sw.open{background:transparent;border:1.5px solid rgba(255,255,255,.45)}
.ftd .bp-figcap{font-size:11.5px;line-height:1.5;color:rgba(255,255,255,.5);margin-top:14px;max-width:34ch}
.ftd .bp-figcap b{color:rgba(255,255,255,.78);font-weight:600}
.ftd .bp-read{padding:30px 32px 28px;display:flex;flex-direction:column;justify-content:center}
.ftd .bp-kick{font-size:11px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:var(--b);display:flex;gap:9px;margin-bottom:16px}
.ftd .bp-kick .seq{color:rgba(255,255,255,.42)}
.ftd .bp-arch{font-size:40px;font-weight:600;letter-spacing:-.03em;line-height:1}
.ftd .bp-arch .sec{color:rgba(255,255,255,.4);font-weight:500}
.ftd .bp-tag{font-size:15px;color:rgba(255,255,255,.82);margin-top:12px;line-height:1.4;max-width:42ch;font-weight:500}
.ftd .bp-tag2{font-size:13px;color:rgba(255,255,255,.5);margin-top:7px;line-height:1.45;max-width:44ch}
.ftd .bp-conf{display:flex;align-items:center;gap:11px;margin-top:16px}
.ftd .bp-conf .lab{font-size:10.5px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:rgba(255,255,255,.45)}
.ftd .bp-conf .pips{display:flex;gap:5px}
.ftd .bp-conf .pip{width:24px;height:5px;border-radius:3px;background:rgba(255,255,255,.16)}
.ftd .bp-conf .pip.on{background:var(--b)}
.ftd .bp-conf .w{font-size:12px;font-weight:600;color:#fff}
.ftd .bp-rarity{font-size:12px;color:rgba(255,255,255,.38);margin-top:10px;line-height:1.4}
.ftd .bp-rarity-combo{margin-left:4px}
.ftd .bp-sect{font-size:10.5px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:rgba(255,255,255,.42);margin:22px 0 12px}
.ftd .bp-sect-note{font-weight:500;letter-spacing:.02em;text-transform:none;color:rgba(255,255,255,.32)}
.ftd .bp-list{display:flex;flex-direction:column;gap:9px}
.ftd .bp-row{display:flex;align-items:flex-start;gap:12px}
.ftd .bp-row .mk{width:11px;height:11px;border-radius:3px;flex:none;margin-top:4px}
.ftd .bp-row.def .mk{background:var(--b)}
.ftd .bp-row.open .mk{background:transparent;border:1.5px solid rgba(255,255,255,.4)}
.ftd .bp-row .nm{font-size:13.5px;font-weight:600;color:#fff;min-width:96px;flex:none;line-height:1.45}
.ftd .bp-row .ds{font-size:12.5px;color:rgba(255,255,255,.62);line-height:1.5;flex:1}
.ftd .bp-row.open .nm{color:rgba(255,255,255,.8)}
.ftd .bp-chs{display:flex;flex-wrap:wrap;gap:8px}
.ftd .bp-ch{display:inline-flex;align-items:center;gap:7px;padding:7px 13px;border-radius:980px;background:rgba(255,122,0,.16);border:1px solid rgba(255,122,0,.4);font-size:12px;font-weight:600;color:#FFB36B}
.ftd .bp-ch .l{width:14px;height:2px;background:var(--b);border-radius:2px}
@media(max-width:760px){.ftd .bp-cover{grid-template-columns:1fr}.ftd .bp-graph{border-right:none;border-bottom:1px solid rgba(255,255,255,.08)}.ftd .bp-arch{font-size:32px}}

/* The Blueprint — the nine trait readouts, in-card, two larger-text columns */
.ftd .bp-body{padding:18px 32px 28px;border-top:1px solid rgba(255,255,255,.08)}
/* The Blueprint reader — flip through one piece at a time */
.ftd .bp-rkick{font-size:10.5px;font-weight:700;letter-spacing:.13em;text-transform:uppercase;color:rgba(255,255,255,.42);margin-bottom:12px}
.ftd .bp-rchips{display:flex;flex-wrap:wrap;gap:6px}
.ftd .bp-rchip{appearance:none;border:none;cursor:pointer;font:inherit;font-size:12px;font-weight:600;letter-spacing:-.01em;padding:6px 11px;border-radius:980px;background:rgba(255,255,255,.07);color:rgba(255,255,255,.7);display:inline-flex;align-items:center;gap:6px;transition:background .2s,color .2s}
.ftd .bp-rchip:hover{background:rgba(255,255,255,.13);color:#fff}
.ftd .bp-rchip .d{width:8px;height:8px;border-radius:2px;flex:none}
.ftd .bp-rchip .d.defined{background:var(--b)}
.ftd .bp-rchip .d.open{background:transparent;border:1.4px solid rgba(255,255,255,.5)}
.ftd .bp-rchip .d.unknown{background:rgba(255,255,255,.2)}
.ftd .bp-rchip .d.type{background:rgba(255,255,255,.6);border-radius:50%}
.ftd .bp-rchip.on{background:#fff;color:#161617}
.ftd .bp-rchip.on .d.open{border-color:#161617}
.ftd .bp-rchip.on .d.type{background:#161617}
.ftd .bp-rdetail{margin-top:16px;background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.09);border-radius:14px;padding:20px 22px;min-height:150px}
.ftd .bp-rstate{display:inline-flex;align-items:center;gap:8px;font-size:10px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;padding:5px 10px;border-radius:980px}
.ftd .bp-rstate .d{width:9px;height:9px;border-radius:2px}
.ftd .bp-rstate.defined{background:var(--bs);color:#FFB36B}.ftd .bp-rstate.defined .d{background:var(--b)}
.ftd .bp-rstate.open{background:rgba(255,255,255,.08);color:rgba(255,255,255,.7)}.ftd .bp-rstate.open .d{background:transparent;border:1.4px solid rgba(255,255,255,.6)}
.ftd .bp-rstate.unknown,.ftd .bp-rstate.type{background:rgba(255,255,255,.08);color:rgba(255,255,255,.75)}.ftd .bp-rstate.unknown .d,.ftd .bp-rstate.type .d{background:rgba(255,255,255,.6);border-radius:50%}
.ftd .bp-rname{font-size:22px;font-weight:600;letter-spacing:-.02em;margin-top:13px}
.ftd .bp-rname .sec{color:rgba(255,255,255,.4);font-weight:500}
.ftd .bp-rbody{font-size:14.5px;color:rgba(255,255,255,.78);line-height:1.6;margin-top:11px}
.ftd .bp-rbody b{color:#fff;font-weight:600}
.ftd .bp-rnav{display:flex;align-items:center;justify-content:space-between;margin-top:14px}
.ftd .bp-rarrow{appearance:none;border:1px solid rgba(255,255,255,.16);background:rgba(255,255,255,.05);color:#fff;cursor:pointer;font:inherit;font-size:13px;font-weight:600;padding:7px 15px;border-radius:980px}
.ftd .bp-rarrow:hover{background:rgba(255,255,255,.12)}
.ftd .bp-rcount{font-size:12px;color:rgba(255,255,255,.4)}
/* The record — win/fair/loss spine */
.ftd .record{background:var(--ink);color:#fff;border-radius:18px;padding:24px 26px;margin-top:18px}
.ftd .rec-kick{font-size:10.5px;font-weight:700;letter-spacing:.13em;text-transform:uppercase;color:var(--b);margin-bottom:10px}
.ftd .rec-head{font-size:26px;font-weight:600;letter-spacing:-.025em;line-height:1.1}
.ftd .rec-bar{display:flex;height:40px;border-radius:9px;overflow:hidden;margin-top:20px}
.ftd .rec-seg{display:flex;flex-direction:column;align-items:center;justify-content:center;font-weight:700;gap:1px;min-width:38px}
.ftd .rec-seg .p{font-size:14px}
.ftd .rec-seg .l{font-size:9.5px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;opacity:.85}
.ftd .rec-seg.won{background:var(--green);color:#06351a}
.ftd .rec-seg.fair{background:rgba(255,255,255,.18);color:#fff}
.ftd .rec-seg.lost{background:#E2493F;color:#3a0b08}
.ftd .rec-note{font-size:12.5px;line-height:1.5;color:rgba(255,255,255,.6);margin-top:14px;max-width:62ch}
.ftd .rec-note b{color:#fff;font-weight:600}
.ftd .rec-foot{display:flex;gap:26px;margin-top:16px}
.ftd .rec-stat .v{font-size:17px;font-weight:600;letter-spacing:-.02em}
.ftd .rec-stat .k{font-size:11.5px;color:rgba(255,255,255,.5);margin-top:2px}
@media(max-width:600px){.ftd .rec-bar{height:52px}.ftd .rec-head{font-size:21px}}
.ftd .bp-cols{display:grid;grid-template-columns:1fr 1fr;gap:30px;padding-top:22px;border-top:1px solid rgba(255,255,255,.08)}
.ftd .bp-col h4{font-size:10.5px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:rgba(255,255,255,.42);margin:0 0 12px;display:flex;align-items:center;gap:9px}
.ftd .bp-col.def h4{color:var(--b)}
.ftd .bp-col h4 .sw{width:12px;height:12px;border-radius:4px;flex:none}
.ftd .bp-col h4 .sw.def{background:var(--b)}
.ftd .bp-col h4 .sw.open{background:transparent;border:1.6px solid rgba(255,255,255,.45)}
.ftd .bp-drow{display:flex;align-items:flex-start;gap:11px;padding:13px 0;border-top:1px solid rgba(255,255,255,.07)}
.ftd .bp-drow:first-of-type{border-top:none;padding-top:2px}
.ftd .bp-drow .mk{width:12px;height:12px;border-radius:3px;flex:none;margin-top:4px}
.ftd .bp-drow .mk.def{background:var(--b)}
.ftd .bp-drow .mk.open{background:transparent;border:1.6px solid rgba(255,255,255,.42)}
.ftd .bp-dtxt{font-size:14px;color:rgba(255,255,255,.66);line-height:1.6}
.ftd .bp-dtxt b{color:#fff;font-weight:600}
.ftd .bp-howto{margin-top:22px;padding-top:18px;border-top:1px solid rgba(255,255,255,.08)}
.ftd .bp-howto-k{font-size:10.5px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:rgba(255,255,255,.42)}
.ftd .bp-howto-row{display:grid;grid-template-columns:repeat(3,1fr);gap:18px;margin-top:13px}
.ftd .bp-howto-i{font-size:12.5px;color:rgba(255,255,255,.6);line-height:1.5;display:flex;gap:10px;align-items:flex-start}
.ftd .bp-howto-i b{color:#fff;font-weight:600}
.ftd .bp-key{flex:none;margin-top:3px}
.ftd .bp-key.filled{width:13px;height:13px;border-radius:3px;background:var(--b)}
.ftd .bp-key.hollow{width:13px;height:13px;border-radius:3px;background:transparent;border:1.6px solid rgba(255,255,255,.45)}
.ftd .bp-key.line{width:17px;height:3px;border-radius:2px;background:var(--b);margin-top:8px}
@media(max-width:760px){.ftd .bp-cols{grid-template-columns:1fr;gap:4px}.ftd .bp-howto-row{grid-template-columns:1fr;gap:11px}}

/* tabs */
.ftd .tabbar{display:flex;align-items:center;gap:4px;margin:26px 0 0;border-bottom:1px solid var(--hair);position:relative;overflow-x:auto}
.ftd .tab{appearance:none;background:none;border:none;cursor:pointer;font:inherit;padding:12px 16px 14px;font-size:14px;font-weight:600;letter-spacing:-.01em;color:var(--i3);position:relative;white-space:nowrap;transition:color .25s}
.ftd .tab:hover{color:var(--i2)}
.ftd .tab.on{color:var(--ink)}
.ftd .tab .ct{font-size:11px;color:var(--i3);font-weight:600;margin-left:5px;padding:1px 5px;border-radius:6px;background:var(--surf)}
.ftd .tab.on .ct{background:var(--bs);color:var(--bi)}
.ftd .tab.on::after{content:"";position:absolute;left:8px;right:8px;bottom:-1px;height:2px;background:var(--ink);border-radius:2px}
@keyframes ftd-shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}
.ftd .sk{background:linear-gradient(90deg,var(--surf) 25%,var(--surf2) 50%,var(--surf) 75%);background-size:200% 100%;animation:ftd-shimmer 1.6s ease-in-out infinite;border-radius:10px}
.ftd .panel{margin-top:22px}

/* vitals */
.ftd .vitals{display:grid;grid-template-columns:repeat(2,1fr);gap:16px;margin-top:18px}
.ftd .vtile{background:var(--surf);border-radius:18px;padding:24px 24px 20px;min-height:160px;display:flex;flex-direction:column}
.ftd .vtile .vlab{font-size:12px;font-weight:600;color:var(--i2);display:flex;align-items:center;gap:5px}
.ftd .vtile .vbig{font-size:38px;font-weight:600;letter-spacing:-.03em;line-height:1;margin-top:auto}
.ftd .vtile .vbig.green{color:var(--gi)}
.ftd .vtile .vbig.brand{color:var(--bi)}
.ftd .vtile .vsub{font-size:13px;color:var(--i3);margin-top:8px;line-height:1.35}
.ftd .vtile .vtrack{height:6px;border-radius:980px;background:#fff;margin-top:11px;position:relative;overflow:hidden}
.ftd .vtile .vtrack i{position:absolute;top:0;bottom:0;border-radius:980px;background:var(--ink)}
.ftd .vtile .vtrack i.b{background:var(--b)}
.ftd .vtile .vtrack .mid{position:absolute;left:50%;top:-2px;bottom:-2px;width:1.5px;background:var(--i3);opacity:.4}
@media(max-width:600px){.ftd .vitals{grid-template-columns:repeat(2,1fr)}.ftd .vtile .vbig{font-size:28px}.ftd .vtile{padding:18px 18px 16px;min-height:140px}}
/* compact "numbers" strip — the figures + methodology tooltips, no big tiles/tracks (folds the old "How they play") */
.ftd .vitals.strip{gap:10px}
.ftd .vitals.strip .vtile{min-height:0;padding:14px 16px 13px}
.ftd .vitals.strip .vbig{font-size:23px;margin-top:9px}
.ftd .vitals.strip .vtrack{display:none}
.ftd .vitals.strip .vsub{margin-top:7px;font-size:11.5px}
@media(max-width:1080px){.ftd .vitals.strip{grid-template-columns:repeat(3,1fr)}}
@media(max-width:600px){.ftd .vitals.strip{grid-template-columns:repeat(2,1fr)}}
/* Consolidation breakdown segmented bar */
.ftd .cons-seg-bar{display:flex;height:7px;border-radius:980px;overflow:hidden;gap:2px;margin-top:11px}
.ftd .cons-seg{border-radius:980px;min-width:3px;transition:width .3s cubic-bezier(.16,1,.3,1)}
.ftd .cons-seg.spread{background:#FF9500}
.ftd .cons-seg.even{background:var(--i4,#D2D2D7)}
.ftd .cons-seg.consol{background:var(--ink)}
.ftd .cons-counts{display:flex;gap:10px;margin-top:6px;font-size:10.5px;color:var(--i3)}
.ftd .cons-counts span{display:inline-flex;align-items:center;gap:3px;white-space:nowrap}
.ftd .cons-dot{width:6px;height:6px;border-radius:50%;flex:none}
/* Picks tile (vtile variant) */
.ftd .picks-rows{display:flex;flex-direction:column;gap:8px;margin-top:auto}
.ftd .pick-row{display:flex;align-items:center;gap:8px}
.ftd .pick-row .pr-bar{height:6px;border-radius:980px;background:var(--ink);transition:width .3s cubic-bezier(.16,1,.3,1)}
.ftd .pick-row .pr-bar.gave{background:var(--i4,#D2D2D7)}
.ftd .pick-row .pr-ct{font-size:13px;font-weight:600;color:var(--i2);min-width:24px;text-align:right}
.ftd .picks-net-row{font-size:14px;font-weight:600;margin-top:8px}
.ftd .picks-net-row.pos{color:var(--gi)}
.ftd .picks-net-row.neg{color:#C0392B}
.ftd .picks-net-row.zero{color:var(--i3)}

/* info tooltip */
.ftd .info{width:14px;height:14px;border-radius:50%;border:1px solid var(--i3);color:var(--i3);font-size:9px;font-weight:700;display:inline-flex;align-items:center;justify-content:center;cursor:help;flex:none;position:relative}
.ftd .info:hover{border-color:var(--ink);color:var(--ink)}
.ftd .info .tip{position:absolute;bottom:calc(100% + 8px);left:50%;transform:translateX(-50%) translateY(4px);background:var(--ink);color:#fff;padding:9px 11px;border-radius:9px;width:188px;font-size:11.5px;font-weight:400;line-height:1.45;text-align:left;opacity:0;pointer-events:none;transition:opacity .2s,transform .2s;z-index:50;box-shadow:0 8px 28px rgba(0,0,0,.22)}
.ftd .info:hover .tip{opacity:1;transform:translateX(-50%) translateY(0)}
.ftd .info .tip b{color:var(--b);font-weight:600}

/* tells */
.ftd .tells{display:grid;grid-template-columns:repeat(3,1fr);gap:14px;margin-top:18px}
.ftd .tcard{background:var(--surf);border-radius:14px;padding:18px 18px 16px;display:flex;flex-direction:column}
.ftd .tcard h3{font-size:13px;font-weight:600;color:var(--ink);display:flex;align-items:center;gap:6px;margin-bottom:14px}
.ftd .tcap{font-size:12.5px;color:var(--i2);margin-top:14px;line-height:1.4}
.ftd .tcap b{color:var(--ink);font-weight:600}
@media(max-width:760px){.ftd .tells{grid-template-columns:1fr}}

/* diverging bars */
.ftd .dbar{display:flex;align-items:center;gap:10px;margin:9px 0;font-size:12.5px}
.ftd .dbar .dlab{width:26px;font-weight:600;color:var(--i2);flex:none}
.ftd .dbar .dtrack{flex:1;height:18px;position:relative;background:linear-gradient(90deg,transparent 0,transparent calc(50% - .5px),var(--hair) calc(50% - .5px),var(--hair) calc(50% + .5px),transparent calc(50% + .5px))}
.ftd .dbar .dfill{position:absolute;top:3px;height:12px;border-radius:3px}
.ftd .dbar .dval{width:46px;text-align:right;font-weight:600;flex:none;color:var(--i3)}
.ftd .pb2-row{display:grid;grid-template-columns:30px 1fr auto;gap:12px;align-items:center;padding:9px 0;border-top:1px solid var(--hair);font-size:14px}
.ftd .pb2-row:first-of-type{border-top:0}
.ftd .pb2-pos{font-weight:700;color:var(--i2)}
.ftd .pb2-say{color:var(--ink);letter-spacing:-.01em}
.ftd .pb2-val{color:var(--i3);font-size:12px}
/* Rail panel: flex column so a margin-top:auto child pins to the very bottom. */
.ft-railpanel{display:flex;flex-direction:column;min-height:100%}
.ftd .pbias-q .pbias-v{font-variant-numeric:tabular-nums;color:var(--i2);margin-left:5px}
.ftd .pbias-q.hi b{color:var(--bi)}
.ftd .pbias-q.lo b{color:var(--ink)}
.ftd .pbias-q.mid b{color:var(--i3)}
.ftd .tcap.sub{font-size:11px;color:var(--i3);margin-top:8px;line-height:1.5}
/* NFL exposure — stack cards + tile grid */
.ftd .nflx-cards{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:18px}
.ftd .nflx-card{border-radius:16px;padding:18px;background:var(--surf)}
.ftd .nflx-card.strong{background:linear-gradient(160deg,#FFF3E9,#FFE7D3);box-shadow:inset 0 0 0 1px #FFD9BD}
.ftd .nflx-card-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:6px}
.ftd .nflx-team{font-size:22px;font-weight:700;letter-spacing:-.02em}
.ftd .nflx-angle{font-size:9.5px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;padding:3px 8px;border-radius:6px}
.ftd .nflx-angle.s{background:var(--b);color:#fff}
.ftd .nflx-angle.w{background:var(--i3);color:#fff}
.ftd .nflx-cnt{font-size:13px;color:var(--i2);margin-bottom:10px}
.ftd .nflx-cnt b{color:var(--ink);font-weight:700;font-size:15px}
.ftd .nflx-pips{display:flex;gap:4px;margin-bottom:11px;flex-wrap:wrap}
.ftd .nflx-pip{width:12px;height:12px;border-radius:4px;background:#fff;box-shadow:inset 0 0 0 1px var(--hair)}
.ftd .nflx-pip.on{background:var(--b);box-shadow:none}
.ftd .nflx-pip.on.w{background:var(--i3)}
.ftd .nflx-read{font-size:12px;line-height:1.45;color:var(--i2)}
.ftd .nflx-rest-h{font-size:10.5px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--i3);margin:6px 0 10px}
.ftd .nflx-tiles{display:grid;grid-template-columns:repeat(auto-fill,minmax(104px,1fr));gap:8px}
.ftd .nflx-tile{background:var(--surf);border-radius:11px;padding:11px 13px;display:flex;flex-direction:column;gap:5px}
.ftd .nflx-tile-top{display:flex;align-items:center;justify-content:space-between}
.ftd .nflx-tm{font-size:14px;font-weight:700}
.ftd .nflx-tile-cnt{font-size:11.5px;color:var(--i2)}
.ftd .nflx-tile-cnt b{color:var(--ink);font-weight:600}
.ftd .nflx-dot{width:8px;height:8px;border-radius:50%;flex:none}
.ftd .nflx-dot.s{background:var(--b)} .ftd .nflx-dot.w{background:var(--i3)}
.ftd .nflx-legend{display:flex;gap:18px;margin-top:18px;padding-top:16px;border-top:1px solid var(--hair);font-size:11.5px;color:var(--i2)}
.ftd .nflx-legend span{display:inline-flex;align-items:center;gap:7px}
@media(max-width:760px){.ftd .nflx-cards{grid-template-columns:1fr}}

/* heat strip */
.ftd .heat{display:flex;gap:6px;margin-top:2px}
.ftd .heat .hcol{flex:1;display:flex;flex-direction:column;align-items:center;gap:6px}
.ftd .heat .hbar{width:100%;border-radius:5px;background:var(--b)}
.ftd .heat .hday{font-size:10px;font-weight:600;color:var(--i3)}
.ftd .heat .hnum{font-size:10px;font-weight:600;color:var(--i3)}
.ftd .heat .hcol.hot .hday,.ftd .heat .hcol.hot .hnum{color:var(--bi)}

/* zone 4 */
.ftd .zone4{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-top:18px}
.ftd .play{background:var(--ink);color:#fff;border-radius:16px;padding:22px 24px}
.ftd .play .pkick{font-size:11px;font-weight:700;letter-spacing:.13em;text-transform:uppercase;color:var(--b);margin-bottom:4px}
.ftd .play .pmirror{font-size:12px;color:rgba(255,255,255,.5);margin-bottom:18px}
.ftd .play h3{font-size:18px;font-weight:600;letter-spacing:-.01em;line-height:1.3;margin-bottom:6px}
.ftd .pgrid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-top:14px}
.ftd .pblock .ph{font-size:11px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:rgba(255,255,255,.42);margin-bottom:6px}
.ftd .pblock .pt{font-size:14px;font-weight:500;line-height:1.4;color:rgba(255,255,255,.94)}
.ftd .gatechips{display:flex;flex-wrap:wrap;gap:8px;margin-top:18px;padding-top:16px;border-top:1px solid rgba(255,255,255,.1)}
.ftd .gchip{display:inline-flex;align-items:center;gap:6px;font-size:11px;font-weight:600;color:rgba(255,255,255,.6)}
.ftd .gchip .gd{width:5px;height:5px;border-radius:50%;background:var(--green)}
.ftd .gchip .gd.no{background:var(--red)}
@media(max-width:760px){.ftd .zone4{grid-template-columns:1fr}.ftd .pgrid{grid-template-columns:1fr}}

/* situation */
.ftd .situation{background:var(--surf);border-radius:16px;padding:22px 22px 18px}
.ftd .situation .skick{font-size:11px;font-weight:700;letter-spacing:.13em;text-transform:uppercase;color:var(--bi);margin-bottom:4px}
.ftd .situation .ssub{font-size:12px;color:var(--i3);margin-bottom:16px;line-height:1.45}
.ftd .lrow{display:grid;grid-template-columns:1.5fr auto 1fr;align-items:center;gap:12px;padding:11px 0;border-top:1px solid var(--hair)}
.ftd .lrow:first-of-type{border-top:none}
.ftd .lrow .lname{font-size:13.5px;font-weight:600;letter-spacing:-.01em;color:var(--ink)}
.ftd .lrow .lmeta{font-size:11px;color:var(--i3);margin-top:2px}
.ftd .stancepill{justify-self:start;font-size:11px;font-weight:600;padding:4px 10px;border-radius:980px}
.ftd .stancepill.win{background:var(--bs);color:var(--bi)}
.ftd .stancepill.reb{background:var(--gsoft);color:var(--gi)}
.ftd .stancepill.neu{background:var(--surf2);color:var(--i2)}
.ftd .vbarwrap{display:flex;align-items:center;gap:9px}
.ftd .vbar{flex:1;height:7px;border-radius:4px;background:var(--surf2);overflow:hidden;position:relative}
.ftd .vbar i{display:block;height:100%;border-radius:4px;background:var(--i3)}
.ftd .vpct{font-size:11px;font-weight:600;color:var(--i2);width:62px;text-align:right}
.ftd .snote{font-size:11.5px;color:var(--i3);margin-top:14px;padding-top:12px;border-top:1px solid var(--hair)}
.ftd .snote b{color:var(--bi);font-weight:600}

/* network */
/* Portfolio — Empire signature (dark), additive above DynastyYoYSection */
.ftd .pf-sig{background:var(--ink);color:#fff;border-radius:18px;margin-top:22px;margin-bottom:20px;padding:24px 26px}
.ftd .pf-sig-kick{font-size:10.5px;font-weight:700;letter-spacing:.13em;text-transform:uppercase;color:var(--b);margin-bottom:8px}
.ftd .pf-sig-title{font-size:24px;font-weight:600;letter-spacing:-.02em}
.ftd .pf-sig-sub{font-size:13.5px;color:rgba(255,255,255,.6);margin-top:8px;line-height:1.5;max-width:66ch}
/* Network — relationship-graph signature (dark), additive above the list+donut */
.ftd .net-sig{background:var(--ink);color:#fff;border-radius:18px;overflow:hidden;margin-top:22px;padding:22px 26px 16px}
.ftd .net-sig-kick{font-size:10.5px;font-weight:700;letter-spacing:.13em;text-transform:uppercase;color:var(--b);margin-bottom:8px}
.ftd .net-sig-title{font-size:24px;font-weight:600;letter-spacing:-.02em}
.ftd .net-graphwrap{display:flex;justify-content:center;padding:10px 0 4px}
.ftd .net-graphwrap svg{width:100%;max-width:560px;height:auto;overflow:visible}
.ftd .net-sig-note{font-size:11.5px;color:rgba(255,255,255,.45);text-align:center;margin-top:4px}
.ftd .net-legend{display:flex;justify-content:center;gap:18px;margin:2px 0 14px;font-size:11px;color:rgba(255,255,255,.5)}
.ftd .net-leg{display:inline-flex;align-items:center;gap:7px}
.ftd .net-leg-dot{width:12px;height:12px;border-radius:50%;background:rgba(255,255,255,.3)}
.ftd .net-leg-ring{width:12px;height:12px;border-radius:50%;border:2px dashed #FF7A00}
.ftd .net-list{border-top:1px solid rgba(255,255,255,.08)}
.ftd .net-row{display:grid;grid-template-columns:1fr auto auto;gap:12px;align-items:center;padding:8px 0;border-bottom:1px solid rgba(255,255,255,.06);font-size:12.5px}
.ftd .net-row-n{color:#fff;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ftd .net-row-t{color:rgba(255,255,255,.6);white-space:nowrap}
.ftd .net-row-t b{color:#fff;font-weight:600}
.ftd .net-row-lg{font-size:11px;font-weight:600;color:rgba(255,255,255,.55);background:rgba(255,255,255,.08);border-radius:980px;padding:3px 10px;white-space:nowrap}
.ftd .net-row-lg.multi{background:rgba(255,122,0,.18);color:#FFB877}
.ftd .net-insight{font-size:12.5px;line-height:1.5;color:rgba(255,255,255,.6);margin-top:14px}
.ftd .net-insight b{color:#fff;font-weight:600}
.ftd .net{display:grid;grid-template-columns:1fr 320px;gap:14px;margin-top:18px}
.ftd .partners{background:var(--surf);border-radius:16px;padding:20px 22px}
.ftd .partners h4{font-size:13px;font-weight:600;margin-bottom:16px;display:flex;align-items:center;gap:6px;color:var(--ink)}
.ftd .prow{display:grid;grid-template-columns:18px 1fr auto;align-items:center;gap:12px;padding:8px 0}
.ftd .prow .prk{font-size:11px;font-weight:700;color:var(--i3)}
.ftd .prow .pmid2{min-width:0}
.ftd .prow .pnm{font-size:13px;font-weight:600;letter-spacing:-.01em;color:var(--ink);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ftd .prow .psub{font-size:11px;color:var(--i3);margin-top:1px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ftd .prow .ptrack{height:6px;border-radius:4px;background:var(--surf2);margin-top:6px;overflow:hidden}
.ftd .prow .ptrack i{display:block;height:100%;border-radius:4px;background:var(--i3)}
.ftd .prow.top .ptrack i{background:var(--b)}
.ftd .prow .pct{font-size:12.5px;font-weight:600;color:var(--i2);white-space:nowrap}
.ftd .donutcard{background:var(--ink);color:#fff;border-radius:16px;padding:22px 24px;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center}
.ftd .donutcard .dk{font-size:11px;font-weight:700;letter-spacing:.13em;text-transform:uppercase;color:var(--b);margin-bottom:16px;align-self:flex-start}
.ftd .donutcard .dcap{font-size:12.5px;color:rgba(255,255,255,.6);margin-top:14px;line-height:1.45}
.ftd .donutcard .dcap b{color:#fff;font-weight:600}
@media(max-width:1080px){.ftd .net{grid-template-columns:1fr}}

/* honesty */
.ftd .honesty{margin-top:24px;font-size:11.5px;color:var(--i3);line-height:1.5;max-width:80ch}
.ftd .honesty b{color:var(--i2);font-weight:600}
.ftd .empty{background:var(--surf);border-radius:14px;padding:18px;font-size:12.5px;color:var(--i3);line-height:1.5}

/* ===== Drafts ===== */
/* Drafts — dark grade signature (re-skin); grade relocated here from the career grid */
.ftd .dr-sig{background:var(--ink);color:#fff;border-radius:18px;padding:24px 26px;margin-top:22px;margin-bottom:20px}
.ftd .dr-sig-kick{font-size:10.5px;font-weight:700;letter-spacing:.13em;text-transform:uppercase;color:var(--b);margin-bottom:14px}
.ftd .dr-sig-row{display:flex;align-items:center;gap:24px}
.ftd .dr-sig-grade{font-size:64px;font-weight:600;letter-spacing:-.04em;line-height:1;flex:none}
.ftd .dr-sig-title{font-size:20px;font-weight:600;letter-spacing:-.02em}
.ftd .dr-sig-note{font-size:13.5px;color:rgba(255,255,255,.6);margin-top:8px;line-height:1.5}
.ftd .dr-career{background:var(--surf);border-radius:16px;padding:20px 22px;margin-bottom:28px}
.ftd .dr-kicker{font-size:11px;font-weight:600;letter-spacing:.14em;text-transform:uppercase;color:var(--i3);margin-bottom:14px}
.ftd .dr-career-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;align-items:start}
.ftd .dr-card{background:#fff;border-radius:12px;padding:18px}
.ftd .dr-card-lbl{font-size:11px;font-weight:600;letter-spacing:.1em;text-transform:uppercase;color:var(--i3);margin-bottom:12px}
.ftd .dr-grade-row{display:flex;align-items:baseline;gap:14px}
.ftd .dr-grade-big{font-size:52px;line-height:.9;font-weight:600;letter-spacing:-.03em;color:var(--ink)}
.ftd .dr-grade-note{font-size:13px;line-height:1.4;color:var(--i2)}
.ftd .dr-dna-big{font-size:26px;font-weight:600;letter-spacing:-.02em;color:var(--bi)}
.ftd .dr-dna-note{font-size:13px;line-height:1.45;color:var(--i2);margin-top:8px}
.ftd .dr-imp-big{font-size:16px;font-weight:600;color:var(--ink);line-height:1.35}
.ftd .dr-receipts{margin-top:14px;display:flex;flex-direction:column}
.ftd .dr-receipt{display:flex;align-items:center;gap:10px;padding:9px 0;border-top:1px solid var(--hair)}
.ftd .dr-receipt:first-child{border-top:none}
.ftd .dr-r-name{font-size:13px;font-weight:500;color:var(--ink);flex:1;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ftd .dr-r-meta{font-size:12px;color:var(--i3);white-space:nowrap}
.ftd .dr-r-tag{font-size:10px;font-weight:600;padding:2px 7px;border-radius:980px;white-space:nowrap;background:var(--bs);color:var(--bi)}
.ftd .dr-kill{margin-top:16px;background:var(--ink);color:#fff;border-radius:12px;padding:16px 18px}
.ftd .dr-kill-lbl{font-size:10px;font-weight:600;letter-spacing:.14em;text-transform:uppercase;color:rgba(255,255,255,.55);margin-bottom:4px}
.ftd .dr-kill-where{font-size:11.5px;font-weight:600;color:rgba(255,255,255,.6);margin-bottom:9px}
.ftd .dr-kill-line{font-size:13.5px;line-height:1.5;color:rgba(255,255,255,.92)}
.ftd .dr-kill-took{color:#FF6B5E;font-weight:600}
.ftd .dr-kill-pass{color:#5FE08A;font-weight:600}
.ftd .dr-sec-hd{font-size:11px;font-weight:600;letter-spacing:.14em;text-transform:uppercase;color:var(--i3);margin:0 0 14px}
.ftd .dr-chips{display:flex;flex-wrap:wrap;gap:10px;margin-bottom:28px}
.ftd .dr-group{margin-bottom:20px}
.ftd .dr-group:last-child{margin-bottom:28px}
.ftd .dr-group-h{font-size:10.5px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:var(--i3);margin-bottom:10px}
.ftd .dr-group .dr-chips{margin-bottom:0}
.ftd .dr-chip{display:flex;align-items:center;gap:10px;background:var(--surf);border:1.5px solid transparent;border-radius:980px;padding:8px 16px 8px 8px;cursor:pointer;min-height:44px;font:inherit}
.ftd .dr-chip:hover{background:var(--surf2)}
.ftd .dr-chip.on{background:#fff;border-color:var(--ink)}
.ftd .dr-chip-g{width:28px;height:28px;border-radius:50%;flex:none;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:600}
.ftd .dr-chip-mid{display:flex;flex-direction:column;line-height:1.2;text-align:left}
.ftd .dr-chip-lg{font-size:14px;font-weight:600;color:var(--ink);white-space:nowrap}
.ftd .dr-chip-sub{font-size:11px;color:var(--i3);white-space:nowrap}
.ftd .dr-chip-tag{font-size:9px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:var(--i3);background:var(--surf2);padding:2px 6px;border-radius:980px}
.ftd .dr-bhead{display:flex;align-items:center;flex-wrap:wrap;gap:14px;margin-bottom:20px}
.ftd .dr-gpill{width:52px;height:52px;border-radius:13px;display:flex;align-items:center;justify-content:center;font-size:28px;font-weight:700;letter-spacing:-.02em;color:#fff;flex:none;box-shadow:0 6px 16px -6px rgba(0,0,0,.4)}
.ftd .dr-bhead-title{font-size:18px;font-weight:600;letter-spacing:-.01em;color:var(--ink)}
.ftd .dr-bhead-sub{font-size:13px;color:var(--i3);margin-top:1px}
.ftd .dr-pill{font-size:12px;font-weight:600;padding:5px 11px;border-radius:980px;white-space:nowrap}
.ftd .dr-pill-up{background:var(--gsoft);color:var(--gi)}
.ftd .dr-pill-down{background:var(--bs);color:var(--bi)}
.ftd .dr-pill-flat{background:var(--surf);color:var(--i3)}
.ftd .dr-pill-hit{background:var(--surf);color:var(--ink)}
.ftd .dr-spacer{flex:1}
.ftd .dr-clock-lbl{font-size:11px;font-weight:600;letter-spacing:.14em;text-transform:uppercase;color:var(--i3);margin-bottom:12px}
.ftd .dr-clock-scroll{display:flex;gap:16px;overflow-x:auto;padding-bottom:8px;margin-bottom:28px}
.ftd .dr-trade{flex:none;width:400px;max-width:88vw;background:var(--surf);border-radius:16px;padding:22px}
.ftd .dr-trade-body{display:flex;align-items:stretch;gap:14px}
.ftd .dr-trade-side{flex:1;min-width:0}
.ftd .dr-trade-side-lbl{font-size:10px;font-weight:600;letter-spacing:.12em;text-transform:uppercase;color:var(--i3);margin-bottom:12px}
.ftd .dr-asset{display:flex;align-items:center;gap:10px;margin-bottom:12px}
.ftd .dr-asset:last-child{margin-bottom:0}
.ftd .dr-asset-pickchip{width:38px;height:38px;border-radius:10px;flex:none;display:flex;align-items:center;justify-content:center;background:#fff;color:var(--ink);font-size:12px;font-weight:600}
.ftd .dr-asset-txt{min-width:0;line-height:1.25}
.ftd .dr-asset-name{font-size:14px;font-weight:500;color:var(--ink);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ftd .dr-asset-sub{font-size:11px;color:var(--i3);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ftd .dr-swap{flex:none;width:24px;display:flex;align-items:center;justify-content:center;color:var(--i3)}
.ftd .dr-trade-foot{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-top:18px;padding-top:14px;border-top:1px solid var(--hair)}
.ftd .dr-trade-date{font-size:12px;color:var(--i3)}
.ftd .dr-ktc{font-size:12px;font-weight:600;color:var(--ink);text-decoration:none}
.ftd .dr-ktc:hover{color:var(--bi)}
.ftd .dr-board-scroll{overflow-x:auto;padding-bottom:8px}
.ftd .dr-grid{display:grid;gap:6px}
.ftd .dr-rowlbl{display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:600;color:var(--i3)}
.ftd .dr-cell{border-radius:10px;min-height:64px;padding:7px;display:flex;flex-direction:column;overflow:hidden}
.ftd .dr-cell-empty{background:var(--surf);align-items:center;justify-content:center}
.ftd .dr-cell-empty .dr-cell-slot{color:var(--i3);opacity:.5}
.ftd .dr-cell-slot{font-size:10px;font-weight:600}
.ftd .dr-cell-top{display:flex;align-items:center;gap:6px;margin-bottom:5px}
.ftd .dr-cell-name{font-size:12px;font-weight:600;line-height:1.15;color:var(--ink);overflow:hidden;text-overflow:ellipsis;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical}
.ftd .dr-cell-verdict{font-size:9px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;margin-top:auto}
.ftd .dr-cell-ghost{font-size:9px;color:var(--i3);margin-top:3px;line-height:1.2;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ftd .dr-cell-pos{font-size:9px;font-weight:600;letter-spacing:.06em;color:var(--i3);margin-top:auto}
/* other teams' picks — muted so the manager's own picks stand out */
.ftd .dr-cell-other{background:#fff;box-shadow:inset 0 0 0 1px var(--hair)}
.ftd .dr-cell-other .dr-cell-name{color:var(--i2);font-weight:500}
.ftd .dr-cell-other .dr-cell-slot{color:var(--i3)}
.ftd .dr-cell-other .pav{opacity:.85}
/* the manager's own picks — a soft ring on top of the verdict color */
.ftd .dr-cell-mine{box-shadow:inset 0 0 0 1.5px rgba(0,0,0,.16)}
.ftd .dr-cell-mine.dr-v-steal{box-shadow:inset 0 0 0 1.5px rgba(52,199,89,.45)}
/* ungraded board (current class) — neutral surface, no verdict color */
.ftd .dr-cell-ungraded{background:var(--surf)}
.ftd .dr-cell-ungraded .dr-cell-slot{color:var(--i2)}
.ftd .dr-v-hit{background:var(--gsoft)}.ftd .dr-v-hit .dr-cell-verdict,.ftd .dr-v-hit .dr-cell-slot{color:var(--gi)}
.ftd .dr-v-steal{background:var(--gsoft);box-shadow:inset 0 0 0 1.5px rgba(52,199,89,.35)}.ftd .dr-v-steal .dr-cell-verdict,.ftd .dr-v-steal .dr-cell-slot{color:var(--gi)}
.ftd .dr-v-fair{background:var(--surf2)}.ftd .dr-v-fair .dr-cell-verdict,.ftd .dr-v-fair .dr-cell-slot{color:var(--i2)}
.ftd .dr-v-reach{background:var(--bs)}.ftd .dr-v-reach .dr-cell-verdict,.ftd .dr-v-reach .dr-cell-slot{color:var(--bi)}
.ftd .dr-v-bust{background:rgba(255,59,48,.10)}.ftd .dr-v-bust .dr-cell-verdict,.ftd .dr-v-bust .dr-cell-slot{color:var(--red)}
.ftd .dr-legend{display:flex;flex-wrap:wrap;gap:16px;margin-top:16px}
.ftd .dr-leg{display:flex;align-items:center;gap:7px;font-size:12px;color:var(--i2)}
.ftd .dr-leg-sw{width:14px;height:14px;border-radius:5px;flex:none}
/* current class is now surfaced as ungraded boards (selector + grid); only the
   one-line note remains — the round-pill styles were removed with the block. */
.ftd .dr-cc-note{font-size:12.5px;color:var(--i3);margin-top:18px;padding-top:14px;border-top:1px solid var(--hair);line-height:1.5}
.ftd .dr-honesty{font-size:13px;color:var(--i3);text-align:center;margin-top:36px;padding-top:24px;border-top:1px solid var(--hair)}
/* Board / list view toggle */
.ftd .dr-view-toggle{display:flex;align-items:center;gap:6px;margin:16px 0 12px}
.ftd .dr-view-btn{appearance:none;border:none;cursor:pointer;font:inherit;font-size:13px;font-weight:500;color:var(--i2);background:var(--surf);border-radius:980px;padding:6px 14px;transition:background .18s,color .18s}
.ftd .dr-view-btn:hover{background:var(--surf2);color:var(--ink)}
.ftd .dr-view-btn.on{background:var(--ink);color:#fff}
/* Draft pick list */
.ftd .dr-list{margin-top:4px}
.ftd .dr-list-row{display:flex;align-items:center;gap:10px;padding:9px 0;border-bottom:1px solid var(--hair)}
.ftd .dr-list-row:last-child{border-bottom:none}
.ftd .dr-list-slot{font-size:12px;font-weight:600;color:var(--i3);width:30px;flex:none;font-variant-numeric:tabular-nums}
.ftd .dr-list-pos{font-size:10px;font-weight:700;letter-spacing:.06em;background:var(--surf2);border-radius:5px;padding:2px 5px;flex:none;color:var(--i2)}
.ftd .dr-list-name{font-size:14px;font-weight:500;color:var(--ink);flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ftd .dr-list-v{font-size:10px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;border-radius:6px;padding:3px 8px;flex:none}
.ftd .dr-list-v.hit,.ftd .dr-list-v.steal{background:var(--gsoft);color:var(--gi)}
.ftd .dr-list-v.fair{background:var(--surf2);color:var(--i2)}
.ftd .dr-list-v.reach{background:var(--bs);color:var(--bi)}
.ftd .dr-list-v.bust{background:rgba(255,59,48,.10);color:var(--red)}
.ftd .dr-list-val{font-size:12px;color:var(--i3);font-variant-numeric:tabular-nums;flex:none;min-width:34px;text-align:right}
.ftd .dr-list-av{width:32px;height:32px;border-radius:50%;object-fit:cover;flex:none;background:var(--surf2)}
.ftd .dr-list-av-ph{width:32px;height:32px;border-radius:50%;background:var(--surf2);flex:none}
@media(max-width:760px){.ftd .dr-career-grid{grid-template-columns:1fr}}

/* ===== History ===== */
.ftd .th-head{margin-bottom:28px}
.ftd .th-head h2{margin:0;font-size:34px;font-weight:600;letter-spacing:-.02em;line-height:1.08;color:var(--ink)}
.ftd .th-head h2 .th-accent{color:var(--b)}
.ftd .th-sub{margin:12px 0 0;font-size:15px;font-weight:400;color:var(--i2)}
/* History — dark signature card + at-the-date verdict layer (re-skin, additive) */
.ftd .th-sig{background:var(--ink);color:#fff;border-radius:18px;padding:24px 26px;margin-bottom:20px}
.ftd .th-sig-kick{font-size:10.5px;font-weight:700;letter-spacing:.13em;text-transform:uppercase;color:var(--b);margin-bottom:8px}
.ftd .th-sig-title{font-size:24px;font-weight:600;letter-spacing:-.02em}
.ftd .th-sig-sub{font-size:13.5px;color:rgba(255,255,255,.6);margin-top:8px;line-height:1.5;max-width:64ch}
.ftd .th-sig-meta{font-size:12px;color:rgba(255,255,255,.4);margin-top:8px}
.ftd .th-statband{display:flex;margin-top:20px;flex-wrap:wrap;gap:14px 0}
.ftd .th-stat{padding:0 22px;border-left:1px solid rgba(255,255,255,.1)}
.ftd .th-stat:first-child{padding-left:0;border-left:none}
.ftd .th-stat .v{font-size:26px;font-weight:600;letter-spacing:-.025em}
.ftd .th-stat .v.green{color:#5BE584}
.ftd .th-stat .l{font-size:11.5px;color:rgba(255,255,255,.55);margin-top:4px}
.ftd .th-vpill{display:inline-flex;align-items:center;gap:5px;font-size:13.5px;font-weight:800;letter-spacing:-.01em;padding:6px 14px;border-radius:980px;margin-left:auto;cursor:default}
.ftd .th-vpill::before{content:"";width:7px;height:7px;border-radius:50%;background:currentColor;opacity:.9}
.ftd .th-vpill.won{background:var(--gsoft);color:var(--gi);box-shadow:inset 0 0 0 1px rgba(52,199,89,.28)}
.ftd .th-vpill.fair{background:var(--surf2);color:var(--i2);box-shadow:inset 0 0 0 1px var(--hair)}
.ftd .th-vpill.lost{background:rgba(255,59,48,.10);color:var(--red);box-shadow:inset 0 0 0 1px rgba(255,59,48,.26)}
.ftd .th-approx{display:inline-flex;align-items:center;font-size:10.5px;font-weight:700;letter-spacing:.02em;color:var(--i3);background:var(--surf2);padding:3px 8px;border-radius:980px;cursor:default}
.ftd .th-aged{font-size:12px;color:var(--i3);margin-top:12px;padding-top:11px;border-top:1px solid var(--hair)}
.ftd .th-aged b{font-weight:600}.ftd .th-aged b.up{color:var(--gi)}.ftd .th-aged b.dn{color:var(--red)}
/* Leagues tab — visual league cards, minimal text */
.ftd .lgs{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:12px}
.ftd .lgc{background:var(--surf);border-radius:14px;padding:16px 18px}
.ftd .lgc-top{display:flex;align-items:center;gap:9px}
.ftd .lgc-n{font-size:15px;font-weight:600;letter-spacing:-.01em}
.ftd .lgc-sf{font-size:10px;font-weight:700;letter-spacing:.04em;color:var(--bi);background:var(--bs);padding:2px 6px;border-radius:6px}
.ftd .lgc-fin{display:flex;align-items:center;gap:9px;margin-top:13px}
.ftd .lgc-medal{width:11px;height:11px;border-radius:50%;flex:0 0 auto}
.ftd .lgc-medal.gold{background:linear-gradient(180deg,#FFD876,#D99A28)}
.ftd .lgc-medal.silver{background:linear-gradient(180deg,#E6E8EC,#A9AEB7)}
.ftd .lgc-medal.bronze{background:linear-gradient(180deg,#E2A36A,#A66A33)}
.ftd .lgc-medal.po{background:var(--bi)}
.ftd .lgc-medal.miss{background:transparent;box-shadow:inset 0 0 0 1.5px var(--i3)}
.ftd .lgc-medal.active{background:repeating-linear-gradient(135deg,#FF7A00,#FF7A00 3px,#FFD3AC 3px,#FFD3AC 6px)}
/* League rows — avatar + clickable per-season history timeline */
.ftd .lav{display:inline-flex;align-items:center;justify-content:center;flex:none;object-fit:cover;background:var(--surf2);color:var(--i2);font-weight:700;overflow:hidden}
.ftd .lgrs{display:flex;flex-direction:column;gap:10px}
.ftd .lgr{background:var(--surf);border-radius:14px;overflow:hidden}
.ftd .lgr-head{display:flex;align-items:center;gap:12px;width:100%;padding:13px 16px;background:none;border:0;cursor:pointer;text-align:left;font:inherit}
.ftd .lgr-head:disabled{cursor:default}
.ftd .lgr-head:hover:not(:disabled){background:var(--surf2)}
.ftd .lgr-main{flex:1;min-width:0}
.ftd .lgr-name{font-size:14.5px;font-weight:600;letter-spacing:-.01em;color:var(--ink);display:flex;align-items:center;gap:7px}
.ftd .lgr-sub{font-size:12.5px;color:var(--i2);margin-top:3px;display:flex;align-items:center;gap:7px;flex-wrap:wrap}
.ftd .lgr-fin{color:var(--ink);font-weight:500}
.ftd .lgr-dim{color:var(--i3)}
.ftd .lgr-chev{font-size:9px;color:var(--i3);flex:none}
.ftd .lgr-tl{margin:0 16px 14px 26px;padding:4px 0 4px 18px;border-left:2px solid var(--hair)}
.ftd .lgr-tl-row{display:grid;grid-template-columns:40px 12px 1fr auto;gap:11px;align-items:center;padding:7px 0;font-size:13px}
.ftd .lgr-tl-yr{color:var(--i3);font-weight:600}
.ftd .lgr-tl-fin{color:var(--ink)}
.ftd .lgr-tl-rec{color:var(--i3)}
.ftd .lgc-fin-lab{font-size:14.5px;font-weight:600;letter-spacing:-.01em}
.ftd .lgc-yr{font-size:11px;font-weight:600;color:var(--i3);background:var(--surf2);padding:2px 7px;border-radius:6px;margin-left:auto}
.ftd .lgc-sub{margin-top:8px;font-size:12px;color:var(--i2);display:flex;align-items:center;gap:6px;flex-wrap:wrap}
.ftd .lgc-orphan{font-size:9.5px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:var(--bi);background:var(--bs);padding:2px 6px;border-radius:6px}
.ftd .lgc-new{color:var(--i3)}
.ftd .lg-exit{font-size:12.5px;color:var(--i2);margin-top:14px}
.ftd .lg-exit b{color:var(--ink);font-weight:600}
/* Portfolio timeline (Gantt) */
.ftd .pf{background:var(--surf);border-radius:18px;padding:24px 26px}
.ftd .pf-head{display:flex;align-items:baseline;justify-content:space-between;flex-wrap:wrap;gap:8px}
.ftd .pf-h1{font-size:14px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:var(--i2)}
.ftd .pf-meta{font-size:13px;color:var(--i2)}
.ftd .pf-meta b{color:var(--ink);font-weight:600}
.ftd .pf-growth{display:flex;align-items:center;gap:6px;font-size:12px;color:var(--i3);margin:12px 0 20px}
.ftd .pf-growth-l{margin-right:4px}
.ftd .pf-growth .g{display:inline-flex;align-items:center;justify-content:center;min-width:22px;height:22px;border-radius:6px;background:#fff;font-weight:700;color:var(--ink);font-size:12px}
.ftd .pf-growth .g.now{background:var(--b);color:#fff}
.ftd .pf-growth .ar{color:var(--i3)}
.ftd .gantt{display:grid;gap:0 8px;align-items:center}
.ftd .gantt .g-yr{font-size:11px;font-weight:700;color:var(--i3);text-align:center;padding-bottom:10px}
.ftd .gantt .g-yr.now{color:var(--b)}
.ftd .gantt .g-lg{font-size:13px;font-weight:600;letter-spacing:-.01em;padding:6px 8px 6px 0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;display:flex;align-items:center;gap:6px}
.ftd .gantt .g-sf{font-size:9px;font-weight:700;color:var(--bi);background:var(--bs);padding:1px 5px;border-radius:5px;flex:none}
.ftd .g-cell{height:34px;display:flex;align-items:center;margin:4px 0}
.ftd .g-seg{height:34px;width:100%;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:#fff;position:relative}
.ftd .g-seg.gold{background:linear-gradient(180deg,#E7B24B,#C98E24)}
.ftd .g-seg.silver{background:linear-gradient(180deg,#B9BdC6,#969BA4);color:#2b2d31}
.ftd .g-seg.bronze{background:linear-gradient(180deg,#C08247,#9A6432)}
.ftd .g-seg.po{background:rgba(255,122,0,.55)}
.ftd .g-seg.miss{background:var(--surf2);color:var(--i2);font-weight:600}
.ftd .g-seg.active{background:repeating-linear-gradient(135deg,#FFE7CF,#FFE7CF 7px,#FFF1E6 7px,#FFF1E6 14px);color:var(--bi);box-shadow:inset 0 0 0 1.5px #FFD3AC}
.ftd .g-seg.rec{background:var(--surf2);color:var(--i2);font-weight:600}
.ftd .g-seg.left{background:rgba(255,59,48,.1);color:#FF3B30;font-weight:700}
.ftd .g-seg.folded{background:var(--surf2);color:var(--i3);font-weight:600}
.ftd .g-cap{position:absolute;top:-9px;left:6px;font-size:8px;font-weight:800;letter-spacing:.05em;text-transform:uppercase;padding:2px 6px;border-radius:5px;background:var(--b);color:#fff;box-shadow:0 1px 4px rgba(0,0,0,.18)}
.ftd .g-cap.orphan{background:var(--ink)}
.ftd .g-empty{display:block;width:60%;margin:0 auto;height:2px;background:var(--hair);border-radius:2px;opacity:.6}
.ftd .pf-legend{display:flex;flex-wrap:wrap;gap:14px;margin-top:18px;font-size:11px;color:var(--i3)}
.ftd .pf-key{display:inline-flex;align-items:center;gap:6px}
.ftd .pf-kd{width:10px;height:10px;border-radius:3px;display:inline-block}
.ftd .pf-kd.gold{background:linear-gradient(180deg,#E7B24B,#C98E24)}
.ftd .pf-kd.silver{background:#A9AEB7}
.ftd .pf-kd.bronze{background:#B0703A}
.ftd .pf-kd.po{background:rgba(255,122,0,.55)}
.ftd .pf-kd.miss{background:var(--surf2)}
.ftd .pf-kd.active{background:repeating-linear-gradient(135deg,#FFD3AC,#FFD3AC 4px,#FFF1E6 4px,#FFF1E6 8px)}
.ftd .pf-kd.left{background:rgba(255,59,48,.55)}
.ftd .pf-kd.folded{background:var(--surf2);box-shadow:inset 0 0 0 1px var(--hair)}
/* Trophy case — shareable Lombardi card */
.ftd .trophy{position:relative;background:radial-gradient(120% 130% at 50% -8%,#262b33 0%,#0b0c0e 60%);color:#fff;border-radius:20px;padding:26px 28px 22px;margin-top:8px;box-shadow:inset 0 0 0 1px rgba(255,255,255,.08),0 30px 60px -30px rgba(0,0,0,.7);overflow:hidden;text-align:center}
.ftd .trophy-glow{position:absolute;top:-40%;left:50%;transform:translateX(-50%);width:340px;height:340px;background:radial-gradient(circle,rgba(255,194,75,.14),transparent 62%);pointer-events:none}
.ftd .trophy-head,.ftd .trophy-id,.ftd .trophy-hero,.ftd .trophy-stats,.ftd .trophy-foot{position:relative}
.ftd .trophy-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:22px}
.ftd .trophy-brand{display:inline-flex;align-items:center;gap:8px;font-size:11px;font-weight:700;letter-spacing:.13em;text-transform:uppercase;color:rgba(255,255,255,.72)}
.ftd .trophy-brand .tg{display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:7px;background:#fff}
.ftd .trophy-share{appearance:none;border:0;cursor:pointer;font:inherit;font-size:11.5px;font-weight:600;letter-spacing:.01em;color:rgba(255,255,255,.62);background:rgba(255,255,255,.08);border-radius:980px;padding:6px 14px;transition:background .2s,color .2s}
.ftd .trophy-share:hover{background:rgba(255,255,255,.16);color:#fff}
.ftd .trophy-id{display:flex;flex-direction:column;align-items:center;gap:5px;margin-bottom:14px}
.ftd .trophy-user{font-size:24px;font-weight:600;letter-spacing:-.02em;line-height:1}
.ftd .trophy-arch{font-size:11px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#FFC24B}
.ftd .trophy-hero{display:flex;align-items:center;justify-content:center;gap:6px}
.ftd .trophy-stage{display:flex;flex-direction:column;align-items:center;flex:0 0 auto}
.ftd .lombardi{display:block;filter:drop-shadow(0 14px 18px rgba(0,0,0,.5))}
.ftd .trophy-cg{font-size:11.5px;letter-spacing:.04em;color:rgba(255,255,255,.55);margin-top:2px}
.ftd .trophy-cg b{color:#fff;font-weight:600}
.ftd .fin{display:flex;flex-direction:column;align-items:center;gap:3px;min-width:64px}
.ftd .fcount{font-size:34px;font-weight:600;letter-spacing:-.03em;line-height:1}
.ftd .fin.gold .fcount{color:#FFD876;text-shadow:0 0 18px rgba(255,194,75,.35)}
.ftd .fin.silver .fcount{color:#D9DCE1}
.ftd .flab{font-size:10px;font-weight:700;letter-spacing:.13em;text-transform:uppercase;color:rgba(255,255,255,.46)}
.ftd .trophy-stats{font-size:12.5px;color:rgba(255,255,255,.6);margin-top:18px;line-height:1.5}
.ftd .trophy-stats .num{color:#fff;font-weight:600}
.ftd .trophy-stats .dot{margin:0 8px;color:rgba(255,255,255,.3)}
.ftd .trophy-foot{font-size:10px;font-weight:600;letter-spacing:.14em;text-transform:uppercase;color:rgba(255,255,255,.34);margin-top:18px}
@media(max-width:760px){.ftd .trophy{padding:24px 20px 20px}.ftd .trophy-hero{gap:2px}.ftd .fin{min-width:54px}.ftd .fcount{font-size:28px}.ftd .lombardi{width:88px;height:150px}}
/* Picks balance card */
/* picks-card styles moved to vitals block above */
.ftd .th-card{background:var(--surf);border-radius:16px;padding:24px;margin-bottom:24px}
.ftd .th-card-top{display:flex;align-items:baseline;justify-content:space-between;flex-wrap:wrap;gap:12px;margin-bottom:20px}
.ftd .th-card-title{font-size:12px;font-weight:600;letter-spacing:.14em;text-transform:uppercase;color:var(--i3)}
.ftd .th-legend{display:flex;gap:18px;align-items:center}
.ftd .th-legend-item{display:flex;align-items:center;gap:7px;font-size:13px;color:var(--i2)}
.ftd .th-swatch{width:11px;height:11px;border-radius:3px}
.ftd .th-swatch--in{background:var(--ink)}
.ftd .th-swatch--off{background:var(--bs);border:1px solid var(--b)}
.ftd .th-chart{position:relative;width:100%}
.ftd .th-chart svg{display:block;width:100%;height:auto;overflow:visible}
.ftd .th-bar{cursor:default}
.ftd .th-bar--in{fill:var(--ink)}
.ftd .th-bar--off{fill:var(--bs);stroke:var(--b);stroke-width:1}
.ftd .th-tip{position:absolute;pointer-events:none;background:var(--ink);color:#fff;font-size:13px;font-weight:500;padding:7px 11px;border-radius:9px;white-space:nowrap;transform:translate(-50%,-4px);z-index:5}
.ftd .th-filters{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:18px}
.ftd .th-chip{appearance:none;border:none;background:var(--surf);color:var(--i2);font:inherit;font-size:14px;font-weight:500;padding:9px 16px;border-radius:980px;cursor:pointer}
.ftd .th-chip:hover{background:var(--surf2);color:var(--ink)}
.ftd .th-chip.on{background:var(--ink);color:#fff}
.ftd .th-chip-ct{opacity:.55;margin-left:6px;font-weight:400}
.ftd .th-list{max-height:560px;overflow-y:auto;padding-right:4px}
.ftd .th-trade{background:var(--surf);border-radius:16px;padding:18px 20px;margin-bottom:12px}
.ftd .th-trade-meta{display:flex;align-items:center;gap:10px;margin-bottom:14px;flex-wrap:wrap}
.ftd .th-date{font-size:14px;font-weight:500;color:var(--ink)}
.ftd .th-dot{width:3px;height:3px;border-radius:50%;background:var(--i3)}
.ftd .th-league{font-size:14px;font-weight:400;color:var(--i2)}
.ftd .th-sf{font-size:11px;font-weight:600;letter-spacing:.5px;color:var(--bi);background:var(--bs);padding:2px 7px;border-radius:6px}
.ftd .th-ktc{margin-left:auto;font-size:12px;font-weight:600;color:var(--bi);text-decoration:none;border-radius:7px;padding:2px 4px;outline:none}
.ftd .th-ktc:hover{text-decoration:underline}
.ftd .th-ktc:focus-visible{box-shadow:0 0 0 2px var(--bs),0 0 0 3px var(--bi)}
.ftd .th-flow{display:grid;grid-template-columns:1fr auto 1fr;gap:14px;align-items:start}
.ftd .th-side-lbl{font-size:11px;font-weight:600;letter-spacing:1px;text-transform:uppercase;margin-bottom:9px}
.ftd .th-side-lbl--got{color:var(--gi)}
.ftd .th-side-lbl--gave{color:var(--i3)}
.ftd .th-assets{display:flex;flex-direction:column;gap:8px}
.ftd .th-asset{display:flex;align-items:center;gap:9px}
.ftd .th-asset-name{font-size:14px;font-weight:500;color:var(--ink);line-height:1.2}
.ftd .th-asset-pos{font-size:12px;color:var(--i3)}
.ftd .th-pick{display:inline-flex;align-items:center;font-size:12px;font-weight:600;color:var(--bi);background:var(--bs);padding:5px 10px;border-radius:8px;align-self:flex-start}
.ftd .th-rate-note{display:flex;align-items:center;gap:8px;margin-top:12px;font-size:12.5px;color:var(--i3)}
.ftd .th-rate-note b{color:var(--ink)}
.ftd .th-rate-sep{color:var(--i3);font-size:11px}
.ftd .th-arrow{display:flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:50%;background:#fff;color:var(--i3);font-size:15px;flex:none}
.ftd .th-empty{text-align:center;color:var(--i3);font-size:15px;padding:40px 0}
@media(max-width:600px){.ftd .th-head h2{font-size:28px}.ftd .th-flow{grid-template-columns:1fr;gap:10px}.ftd .th-arrow{transform:rotate(90deg)}}

/* shared avatar */
.ftd .pav{border-radius:50%;object-fit:cover;background:var(--surf2);flex:none;display:inline-flex;align-items:center;justify-content:center;color:var(--i3);font-weight:600;overflow:hidden}
.ftd .pchip-sm{display:inline-block;background:#fff;border-radius:980px;padding:3px 9px;font-size:11px;font-weight:600;color:var(--i2)}
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const GRADE_BG = { A: '#34C759', B: '#7CC067', C: '#86868B', D: '#CC9999', F: '#FF3B30' };
const DRAFT_READ = { A: 'An elite drafter', B: 'A strong drafter', C: 'A middling drafter', D: 'A shaky drafter', F: 'A poor drafter' };
const gradeColor = (g) => GRADE_BG[(g || '').charAt(0).toUpperCase()] || '#86868B';
const ordinal = (n) => { const s = ['th', 'st', 'nd', 'rd']; const v = n % 100; return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`; };
const signed = (n, suf = '') => (n == null ? null : `${n > 0 ? '+' : ''}${n}${suf}`);
const nf = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US'));
const CONF_FILL = { high: 4, medium: 3, low: 2, insufficient: 1 };

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// NFL season runs Sep–Jan; offseason is Feb–Aug.
const isInSeason = (m0) => m0 >= 8 || m0 === 0;

// Build a "R.SS" slot label from a real pick object (round + slotInRound, snake-aware).
const slotLabel = (p) => {
  const s = p.slotInRound != null ? p.slotInRound : p.draftSlot;
  if (p.round != null && s != null) return `${p.round}.${String(s).padStart(2, '0')}`;
  if (p.pickNo != null) return `#${p.pickNo}`;
  return '—';
};

const fmtDateLong = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${MON[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
};

// Frontend mirror of backend buildKTCTradeLink (utils/ktcLinks.js).
const buildKtcLink = (teamOne, teamTwo, { sf = true, tep = false } = {}) => {
  const one = (teamOne || []).filter(Boolean).join('|');
  const two = (teamTwo || []).filter(Boolean).join('|');
  if (!one && !two) return null;
  return `https://keeptradecut.com/trade-calculator?var=5&pickVal=0&teamOne=${one}&teamTwo=${two}&format=${sf ? '2' : '1'}&isStartup=0&tep=${tep ? '1' : '0'}`;
};

// Sleeper headshot with initial fallback.
const PlayerAvatar = ({ id, name, size = 24, rounded = '50%' }) => {
  const [err, setErr] = useState(false);
  const st = { width: size, height: size, fontSize: Math.round(size * 0.42), borderRadius: rounded };
  if (!id || err) return <span className="pav" style={st}>{(name || '?').slice(0, 1).toUpperCase()}</span>;
  return <img className="pav" style={st} alt="" src={`https://sleepercdn.com/content/nfl/players/thumb/${id}.jpg`} onError={() => setErr(true)} />;
};

const Info = ({ children }) => (<span className="info">i<span className="tip">{children}</span></span>);

// ---------------------------------------------------------------------------
// Radar (React SVG) — relabeled axes per CHANGES #8.
// Backend emits keys active/winnow/consolidate/draftiq/rblean/burst. We relabel
// for human readability: CONSOL→CONVICTION, RB→PATIENCE. Values are real.
// ---------------------------------------------------------------------------

const AXIS_RELABEL = {
  active: 'ACTIVE', winnow: 'WIN-NOW', consolidate: 'CONVICTION',
  draftiq: 'DRAFT IQ', rblean: 'PATIENCE', burst: 'BURST',
};

const relabelAxes = (axes) => (axes || []).map((a) => ({ ...a, label: AXIS_RELABEL[a.key] || a.label }));

const Radar = ({ axes, size = 236, labels = false }) => {
  if (!axes || axes.length < 3) return null;
  const c = size / 2, R = size * (labels ? 0.37 : 0.4), n = axes.length;
  const labelR = labels ? 1.14 : 1.2;
  const ang = (i) => ((-90 + i * 360 / n) * Math.PI) / 180;
  const pt = (i, v) => [c + R * v * Math.cos(ang(i)), c + R * v * Math.sin(ang(i))];
  const path = (vals) => vals.map((v, i) => { const [x, y] = pt(i, v); return `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`; }).join(' ') + 'Z';
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block', overflow: 'visible' }}>
      {[0.25, 0.5, 0.75, 1].map((r, k) => <path key={k} d={path(axes.map(() => r))} fill="none" stroke={r === 0.5 ? 'rgba(255,122,0,.5)' : 'rgba(255,255,255,.1)'} strokeWidth={r === 0.5 ? 1.3 : 1} strokeDasharray={r === 0.5 ? '3 3' : 'none'} />)}
      {axes.map((a, i) => { const [x, y] = pt(i, 1); return <line key={i} x1={c} y1={c} x2={x} y2={y} stroke="rgba(255,255,255,.1)" strokeWidth="1" />; })}
      <path d={path(axes.map((a) => Math.max(0, Math.min(1, a.val))))} fill="rgba(255,122,0,.22)" stroke="#FF7A00" strokeWidth="2" strokeLinejoin="round" />
      {axes.map((a, i) => { const [x, y] = pt(i, Math.max(0, Math.min(1, a.val))); return <circle key={i} cx={x} cy={y} r="2.6" fill="#FF7A00" />; })}
      {labels && axes.map((a, i) => { const [x, y] = pt(i, labelR); return <text key={i} x={x.toFixed(1)} y={(y + 3).toFixed(1)} textAnchor={x < c - 4 ? 'end' : x > c + 4 ? 'start' : 'middle'} style={{ fontSize: 9, fontWeight: 600, fill: 'rgba(255,255,255,.55)', letterSpacing: '.03em' }}>{a.label}</text>; })}
    </svg>
  );
};

// ---------------------------------------------------------------------------
// ARCHETYPE MODAL — explains all 6 archetype types.
// ---------------------------------------------------------------------------

const ARCHETYPE_GLOSSARY = [
  { name: 'The Instigator', desc: 'Initiates most of their own trades. First to the phone, always has an offer cooking. Exploit: make them wait; urgency costs them.' },
  { name: 'The Grinder',    desc: 'High volume, mostly responsive. Trades to trade. Keep sending offers — volume gets the yes eventually.' },
  { name: 'The Juggler',    desc: 'Many leagues, many deals, all at once. Attention is spread thin; the freshest offer gets the most focus.' },
  { name: 'The Reader',     desc: 'Deliberate. Low frequency, high conviction. They\'ve already decided before they reply.' },
  { name: 'The Chameleon',  desc: 'Adapts style to whoever they\'re dealing with. Hard to read. Watch what they do, not what they say.' },
  { name: 'The Operator',   desc: 'The default. No strong behavioral signal. Price it honestly; there\'s no tell to press.' },
];

const ArchetypeModal = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
        animation: 'ftd-am-fade .2s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'rgba(30,30,30,0.98)',
          borderRadius: '20px',
          padding: '32px',
          maxWidth: '480px',
          width: '100%',
          position: 'relative',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute', top: '16px', right: '16px',
            background: 'rgba(255,255,255,0.08)', border: 'none',
            borderRadius: '50%', width: '30px', height: '30px',
            cursor: 'pointer', color: 'rgba(255,255,255,0.6)',
            font: 'inherit', fontSize: '16px', lineHeight: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          ×
        </button>
        <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', color: 'rgba(255,122,0,1)', marginBottom: '16px' }}>
          Archetypes
        </div>
        <div style={{ fontSize: '22px', fontWeight: 600, letterSpacing: '-.02em', color: '#fff', marginBottom: '20px', lineHeight: 1.1 }}>
          The six trading types.
        </div>
        <div>
          {ARCHETYPE_GLOSSARY.map((a, i) => (
            <div
              key={a.name}
              style={{
                padding: '12px 0',
                borderBottom: i < ARCHETYPE_GLOSSARY.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
              }}
            >
              <div style={{ fontSize: '15px', fontWeight: 600, color: '#fff', marginBottom: '2px' }}>{a.name}</div>
              <div style={{ fontSize: '14px', color: '#6E6E73', lineHeight: 1.5 }}>{a.desc}</div>
            </div>
          ))}
        </div>
      </div>
      <style>{`@keyframes ftd-am-fade{from{opacity:0}to{opacity:1}}`}</style>
    </div>
  );
};

// ---------------------------------------------------------------------------
// VERDICT HERO — archetype + plain-English chips (#9) + radar.
// ---------------------------------------------------------------------------

// Map jargon fingerprint phrases to plain English (#9). The backend's
// archetype.fingerprints[] are short jargon tags; rewrite the common ones.
const plainFingerprint = (raw) => {
  if (!raw) return raw;
  const s = String(raw);
  // activity percentile -> "trades constantly / often"
  let m = s.match(/(\d+)(?:st|nd|rd|th)?[- ]?pct activity/i);
  if (m) { const p = +m[1]; return p >= 85 ? 'Trades constantly' : p >= 65 ? 'Trades often' : 'Trades occasionally'; }
  // positional sigma -> "leans into / rarely buys"
  m = s.match(/([+-]?\d+(?:\.\d+)?)σ\s*(QB|RB|WR|TE)/i);
  if (m) { const z = +m[1]; const pos = m[2].toUpperCase(); const nm = { QB: 'QBs', RB: 'RBs', WR: 'WRs', TE: 'TEs' }[pos]; return z >= 0 ? `Loads up on ${nm}` : `Rarely buys ${nm}`; }
  // net future firsts
  m = s.match(/([+-]?\d+)\s*future\s*1sts?/i);
  if (m) { const n = +m[1]; return n > 0 ? 'Hoards future first-rounders' : n < 0 ? 'Ships future first-rounders' : 'Even on future firsts'; }
  // consolidation ratio "1.12 out : 1 in"
  m = s.match(/(\d+(?:\.\d+)?)\s*out\s*:\s*1\s*in/i);
  if (m) { const r = +m[1]; return r >= 1.25 ? 'Combines pieces into one' : r <= 0.8 ? 'Splits one into many' : 'Trades roughly 1-for-1'; }
  if (/blind|0 qb/i.test(s)) return 'QB blind spot in Superflex';
  return s; // leave already-plain tags as-is
};

const VerdictHero = ({ archetype, fingerprint }) => {
  const [modalOpen, setModalOpen] = React.useState(false);
  // Held-beat reveal: kicker -> card -> headline -> chips, instead of an
  // instant paint. Keyed off archetype so a fresh mount (tab switch back to
  // Read) replays the sequence.
  const shown = useReveal(!!archetype);
  if (!archetype || archetype.insufficientSample) {
    return (
      <section className={`verdict ft-fade ${shown ? 'ft-play' : ''}`}>
        <div className="vleft">
          <div className="vkick"><span className="seq">01</span> · Verdict</div>
          <div className="archetype" style={{ fontSize: 26 }}>No read yet</div>
          <div className="tagline">{archetype?.gate || 'Not enough trades to draw an archetype.'}</div>
        </div>
        <div className="vright"><span className="radar-cap">Fingerprint</span></div>
      </section>
    );
  }
  const conf = fingerprint?.confidence || archetype.confidence || 'low';
  const filled = CONF_FILL[conf] || 1;
  const chips = (archetype.fingerprints || []).map(plainFingerprint).filter((c, i, arr) => c && arr.indexOf(c) === i).slice(0, 4);
  return (
    <>
      <ArchetypeModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <section className={`verdict ft-fade ${shown ? 'ft-play' : ''}`}>
        <div className={`vleft ft-seq ${shown ? 'ft-play' : ''}`}>
          <div className="vkick"><span className="seq">01</span> · Verdict</div>
          <div className="archetype" style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
            {archetype.name}
            <button
              onClick={() => setModalOpen(true)}
              aria-label="What's this archetype?"
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#86868B', fontSize: '16px', fontWeight: 400,
                padding: '0 2px', lineHeight: 1, font: 'inherit',
                letterSpacing: 0, flexShrink: 0,
              }}
            >
              ?
            </button>
          </div>
        {archetype.tagline && <div className="tagline">{archetype.tagline}</div>}
        <div className="conf-row">
          <span className="conf-label">Confidence</span>
          <span className="pips">{[0, 1, 2, 3].map((i) => <i key={i} className={`pip${i < filled ? ' on' : ''}`} />)}</span>
          <span className="conf-word">{conf.charAt(0).toUpperCase() + conf.slice(1)} · {filled} of 4</span>
        </div>
        {chips.length > 0 && (
          <div className="fchips">{chips.map((c, i) => <span className="fchip" key={i}><span className="d" />{c}</span>)}</div>
        )}
      </div>
      <div className="vright">
        <span className="radar-cap">Fingerprint</span>
        <Radar axes={relabelAxes(fingerprint?.axes)} size={236} labels />
      </div>
    </section>
    </>
  );
};

// ---------------------------------------------------------------------------
// THE BLUEPRINT — the bodygraph hero. Nine behavioral traits as Human-Design-
// style centers: filled when defined (a reliable, scoutable habit), hollow when
// open (no fixed tendency — pressurable), faint when unknown. A channel lights
// when both centers it joins are defined, and the lit channels name the read.
// Geometry + channel topology live here; the backend (engine.buildBlueprint)
// owns the per-center semantics and plain-English descriptions.
// ---------------------------------------------------------------------------
const BP_CENTERS = {
  build:       { x: 180, y: 46,  shape: 'tri-up',    r: 21, lab: 'right' },
  draftiq:     { x: 180, y: 108, shape: 'tri-down',  r: 21, lab: 'right' },
  initiation:  { x: 180, y: 176, shape: 'square',    r: 23, lab: 'left'  },
  identity:    { x: 180, y: 282, shape: 'diamond',   r: 25, lab: 'left'  },
  edge:        { x: 262, y: 258, shape: 'tri-left',  r: 18, lab: 'right' },
  position:    { x: 84,  y: 330, shape: 'tri-right', r: 19, lab: 'left'  },
  temperament: { x: 276, y: 330, shape: 'tri-left',  r: 19, lab: 'above' },
  activity:    { x: 180, y: 404, shape: 'square',    r: 25, lab: 'left'  },
  drive:       { x: 180, y: 486, shape: 'square',    r: 23, lab: 'below' },
};
// [from, to, name] — name only on the channels worth surfacing as a read.
const BP_CHANNELS = [
  ['build', 'draftiq', null], ['draftiq', 'initiation', null],
  ['initiation', 'identity', 'Conviction'], ['initiation', 'edge', 'Closer'],
  ['initiation', 'temperament', null], ['initiation', 'position', null],
  ['initiation', 'activity', 'Instigator'],
  ['identity', 'activity', null], ['identity', 'edge', null],
  ['activity', 'drive', 'Motor'], ['activity', 'temperament', 'Wildcard'],
  ['activity', 'position', 'Specialist'],
  ['drive', 'temperament', null], ['drive', 'position', null],
];
const BP_ORDER = ['initiation', 'activity', 'drive', 'temperament', 'identity', 'edge', 'draftiq', 'position', 'build'];
const BP_PIPS = { high: 3, medium: 2, med: 2, low: 1, insufficient: 1 };

const bpShape = (s, x, y, r) => {
  const h = r * 0.78;
  switch (s) {
    case 'square': return [[x - h, y - h], [x + h, y - h], [x + h, y + h], [x - h, y + h]];
    case 'diamond': return [[x, y - r], [x + r, y], [x, y + r], [x - r, y]];
    case 'tri-up': return [[x, y - r], [x + r * 0.92, y + r * 0.66], [x - r * 0.92, y + r * 0.66]];
    case 'tri-down': return [[x, y + r], [x + r * 0.92, y - r * 0.66], [x - r * 0.92, y - r * 0.66]];
    case 'tri-left': return [[x - r, y], [x + r * 0.7, y - r * 0.92], [x + r * 0.7, y + r * 0.92]];
    case 'tri-right': return [[x + r, y], [x - r * 0.7, y - r * 0.92], [x - r * 0.7, y + r * 0.92]];
    default: return [];
  }
};

const Bodygraph = ({ centers, selected, onSelect, compact }) => {
  const def = (k) => !!centers[k]?.defined;
  const focus = selected && selected !== 'overview';
  return (
    <svg viewBox="0 0 360 560" style={{ display: 'block', width: '100%', maxWidth: compact ? 168 : 340, height: 'auto', overflow: 'visible' }} aria-label="The Blueprint">
      <circle cx="180" cy="38" r="20" fill="rgba(255,255,255,.035)" stroke="rgba(255,255,255,.08)" strokeWidth="1" />
      <path d="M153 84 C122 90, 110 118, 114 158 L122 300 C125 370, 128 440, 146 486 Q180 506 214 486 C232 440, 235 370, 238 300 L246 158 C250 118, 238 90, 207 84 Z" fill="rgba(255,255,255,.035)" stroke="rgba(255,255,255,.08)" strokeWidth="1" />
      {BP_CHANNELS.map(([a, b], i) => {
        const A = BP_CENTERS[a], B = BP_CENTERS[b];
        const lit = def(a) && def(b);
        return <line key={i} x1={A.x} y1={A.y} x2={B.x} y2={B.y} stroke={lit ? '#FF7A00' : 'rgba(255,255,255,.13)'} strokeWidth={lit ? 3 : 1.5} strokeLinecap="round" strokeDasharray={lit ? 'none' : '2 5'} opacity={focus ? 0.25 : 1} />;
      })}
      {Object.entries(BP_CENTERS).map(([k, c]) => {
        const st = centers[k]?.state || 'unknown';
        const pts = bpShape(c.shape, c.x, c.y, c.r).map((p) => p.join(',')).join(' ');
        const fill = st === 'defined' ? '#FF7A00' : 'transparent';
        const stroke = st === 'defined' ? 'none' : st === 'open' ? 'rgba(255,255,255,.42)' : 'rgba(255,255,255,.2)';
        const sw = st === 'defined' ? 0 : st === 'open' ? 1.6 : 1.2;
        const dash = st === 'unknown' ? '2 3' : 'none';
        let lx = c.x, ly = c.y, anchor = 'middle', vc = true;
        if (c.lab === 'left') { lx = c.x - c.r - 10; anchor = 'end'; }
        else if (c.lab === 'right') { lx = c.x + c.r + 10; anchor = 'start'; }
        else if (c.lab === 'below') { ly = c.y + c.r + 16; anchor = 'middle'; vc = false; }
        else if (c.lab === 'above') { ly = c.y - c.r - 10; anchor = 'middle'; vc = false; }
        const labFill = st === 'defined' ? '#FFB36B' : st === 'open' ? 'rgba(255,255,255,.5)' : 'rgba(255,255,255,.3)';
        const op = focus && selected !== k ? 0.28 : 1;
        return (
          <g key={k} opacity={op} style={{ cursor: onSelect ? 'pointer' : 'default', transition: 'opacity .25s' }} onClick={() => onSelect && onSelect(k)}>
            <polygon points={pts} fill={fill} stroke={stroke} strokeWidth={sw} strokeDasharray={dash} strokeLinejoin="round" />
            {!compact && <text x={lx} y={vc ? ly + 3.5 : ly} textAnchor={anchor} style={{ fontSize: 11, fontWeight: st === 'defined' ? 700 : 600, fill: labFill, letterSpacing: '.02em' }}>{centers[k]?.name || k}</text>}
          </g>
        );
      })}
    </svg>
  );
};

// The reader — flip through the chart one piece at a time (Overview + 9 traits)
// instead of a wall of nine descriptions. Click a chip or a center on the figure.
const BP_STATE_LABEL = { defined: 'Defined · reliable', open: 'Open · where he bends', unknown: 'Not enough data yet' };

const Blueprint = ({ blueprint, archetype, fingerprint, rarity }) => {
  const [sel, setSel] = useState('overview');
  // Held-beat reveal, same cadence as VerdictHero: card settles while the
  // kicker/headline/chips inside it cascade in. Replays on every fresh mount
  // (tab switch back to Read).
  const shown = useReveal(!!archetype);
  // Fall back to the prior verdict hero when there's no blueprint (older payload
  // or insufficient sample).
  if (!blueprint || !blueprint.available || !archetype || archetype.insufficientSample) {
    return <VerdictHero archetype={archetype} fingerprint={fingerprint} />;
  }
  const centers = blueprint.centers || {};
  const conf = archetype.confidence || 'low';
  const filled = BP_PIPS[conf] || 1;
  const [primaryName, secName] = String(archetype.name || '').split(' / ');
  const litChannels = BP_CHANNELS
    .filter(([a, b, name]) => name && centers[a]?.defined && centers[b]?.defined)
    .map(([, , name]) => name)
    .filter((n, i, arr) => arr.indexOf(n) === i);
  const order = ['overview', ...BP_ORDER.filter((k) => centers[k])];
  const cur = Math.max(0, order.indexOf(sel));
  const go = (d) => setSel(order[(cur + d + order.length) % order.length]);
  const c = sel !== 'overview' ? centers[sel] : null;
  return (
    <section className={`bp ft-fade ${shown ? 'ft-play' : ''}`}>
      <div className="bp-cover">
        <div className="bp-graph">
          <Bodygraph centers={centers} selected={sel} onSelect={setSel} />
          <div className="bp-legend">
            <span><i className="sw def" />Locked in</span>
            <span><i className="sw open" />Flexes</span>
          </div>
          <div className="bp-figcap">Nine ways he trades. <b>Solid</b> = locked in, he does it in every league; <b>hollow</b> = flexes by situation. Lines join the locked-in traits that define his style.</div>
        </div>
        <div className={`bp-read ft-seq ${shown ? 'ft-play' : ''}`}>
          <div className="bp-kick"><span className="seq">01</span> The Blueprint</div>
          <div className="bp-arch">{primaryName}{secName && <span className="sec"> / {secName}</span>}</div>
          {(archetype.tagline || blueprint.summary) && (
            <div>
              {archetype.tagline && <div className="bp-tag">{archetype.tagline}</div>}
              {blueprint.summary && <div className="bp-tag2">{blueprint.summary}</div>}
            </div>
          )}
          <div className="bp-conf">
            <span className="lab">Confidence</span>
            <span className="pips">{[0, 1, 2].map((i) => <i key={i} className={`pip${i < filled ? ' on' : ''}`} />)}</span>
            <span className="w">{conf.charAt(0).toUpperCase() + conf.slice(1)}</span>
          </div>
          {rarity && (
            <div className="bp-rarity">
              <span>{rarity.primaryPct}% of managers are {primaryName.replace('The ', '')}</span>
              {archetype.secondary && <span className="bp-rarity-combo">· {rarity.comboPct}% share your exact read</span>}
            </div>
          )}
          {litChannels.length > 0 && (
            <div>
              <div className="bp-sect">His signature <span className="bp-sect-note">— where two locked-in traits connect</span></div>
              <div className="bp-chs">
                {litChannels.map((n) => <span className="bp-ch" key={n}><span className="l" />The {n}</span>)}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bp-body">
        <div className="bp-rkick">Read each piece — tap a chip or a center</div>
        <div className="bp-rchips">
          {order.map((k) => {
            const st = k === 'overview' ? 'type' : centers[k].state;
            return (
              <button type="button" key={k} className={`bp-rchip${sel === k ? ' on' : ''}`} onClick={() => setSel(k)}>
                <span className={`d ${st}`} />{k === 'overview' ? 'Overview' : centers[k].name}
              </button>
            );
          })}
        </div>
        <div className="bp-rdetail">
          {sel === 'overview' ? (
            <>
              <span className="bp-rstate type"><i className="d" />How to read it</span>
              <div className="bp-rname">{primaryName}{secName && <span className="sec"> / {secName}</span>}</div>
              <div className="bp-rbody">We score nine ways a manager trades. <b>Solid</b> shapes are the ones he's locked into — he does them in every league. <b>Hollow</b> ones flex by situation. When two locked-in traits connect, they form a <b>signature</b>{litChannels.length ? ` — his: ${litChannels.join(', ')}` : ''}. Tap any trait to read it.</div>
            </>
          ) : (
            <>
              <span className={`bp-rstate ${c.state}`}><i className="d" />{BP_STATE_LABEL[c.state] || c.state}</span>
              <div className="bp-rname">{c.name}</div>
              <div className="bp-rbody">{c.desc}</div>
            </>
          )}
        </div>
        <div className="bp-rnav">
          <button type="button" className="bp-rarrow" onClick={() => go(-1)}>← Prev</button>
          <span className="bp-rcount num">{cur + 1} / {order.length}</span>
          <button type="button" className="bp-rarrow" onClick={() => go(1)}>Next →</button>
        </div>
      </div>
    </section>
  );
};

// The record — the verifiable spine: win / fair / loss on KTC value at-the-date.
const Record = ({ marketAcumen, total }) => {
  const ma = marketAcumen || {};
  const r = ma.record;
  if (!ma.available || !r) return null;
  const fairPct = Math.max(0, 100 - r.winRate - r.lossRate);
  const segs = [['won', r.winRate, 'Won'], ['fair', fairPct, 'Fair'], ['lost', r.lossRate, 'Lost']];
  const scored = ma.tradesScored;
  const ungraded = (total != null && scored != null) ? total - scored : null;
  return (
    <section className="record">
      <div className="rec-kick">Graded at KTC value on the trade date</div>
      <div className="rec-head">Wins <span className="num">{r.winRate}%</span> of his trades. Loses <span className="num">{r.lossRate}%</span>.</div>
      <div className="rec-bar">
        {segs.filter(([, p]) => p > 0).map(([cls, pct, lab]) => (
          <div key={cls} className={`rec-seg ${cls}`} style={{ width: `${pct}%` }}><span className="p num">{pct}%</span><span className="l">{lab}</span></div>
        ))}
      </div>
      <div className="rec-note">Purely a <b>KTC-value scoreboard</b> — a "loss" just means he came out behind the market on that deal, often on purpose: paying over value to land a player he wanted.</div>
      <div className="rec-foot">
        <div className="rec-stat"><div className="v num">{scored}{total != null ? ` / ${total}` : ''}</div><div className="k">graded <Info>{ungraded != null && ungraded > 0 ? <>{scored} of his {total} trades could be priced at KTC's value on the trade date. The other {ungraded} couldn't — rookie picks made before that class existed on KTC, or pick-only deals.</> : 'Trades we could price at KTC value on the day they happened.'}</Info></div></div>
        {ma.avgDeltaPct != null && <div className="rec-stat"><div className="v num">{signed(ma.avgDeltaPct, '%')}</div><div className="k">avg margin <Info>On a typical trade he lands {signed(ma.avgDeltaPct, '%')} versus a fair, even-value swap — so {ma.avgDeltaPct >= 0 ? 'a hair ahead' : 'a hair behind'}. Graded at KTC's price on the trade date.</Info></div></div>}
        {ma.volatility != null && <div className="rec-stat"><div className="v num">±{ma.volatility}</div><div className="k">swing <Info>How much his margins bounce deal to deal (±{ma.volatility} points typical). High = boom-or-bust; low = steady.</Info></div></div>}
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// READ TAB
// ---------------------------------------------------------------------------

const STANCE = { win_now: { cls: 'win', label: 'Win-now' }, rebuilding: { cls: 'reb', label: 'Rebuilding' }, neutral: { cls: 'neu', label: 'Neutral' } };
// Best completed finish in a league — the scout-relevant per-league story.
const FINISH = {
  champion: { label: 'Champion', cls: 'gold' },
  finalist: { label: 'Runner-up', cls: 'silver' },
  third: { label: 'Third place', cls: 'bronze' },
  made_playoffs: { label: 'Made playoffs', cls: 'po' },
  missed_playoffs: { label: 'Missed playoffs', cls: 'miss' },
};

const Vitals = ({ hud, drafterGrade, baselines, compact, only, picksBalance }) => {
  const a = hud?.activity || {}, con = hud?.consolidation || {}, cy = hud?.consistency || {}, roi = hud?.draftRoi || {}, ma = hud?.marketAcumen || {};
  // Activity "≈N× typical" (#18) — real multiple vs pool median.
  const median = a.poolMedian != null ? a.poolMedian : (baselines?.activityMedian);
  const mult = a.tradesPerLeagueSeason != null && median ? a.tradesPerLeagueSeason / median : null;
  const consVal = con.ratio != null ? `${con.ratio}` : '—';
  const cyDecisive = cy.agree > 0 && cy.modalStance && cy.modalStance !== 'neutral';
  const show = (k) => !only || only.includes(k);
  return (
    <div className={`vitals${compact ? ' strip' : ''}`}>
      {show('activity') && (
      <div className="vtile">
        <div className="vlab">Activity <Info>{a.tradesPerLeagueSeason != null ? <>About <b>{a.tradesPerLeagueSeason}</b> trades per league-season vs a league-average of {median ?? '—'} (pulled from all Fantasy Taco leagues). Collected counts, not modeled.</> : 'Insufficient sample.'}</Info></div>
        <div className="vbig brand num">{a.percentile != null ? ordinal(a.percentile) : '—'}</div>
        <div className="vtrack"><i className="b" style={{ width: `${a.percentile != null ? a.percentile : 0}%` }} /></div>
        <div className="vsub">{a.tradesPerLeagueSeason != null ? <>{a.tradesPerLeagueSeason} trades / league-season{mult ? <> · <span style={{ color: 'var(--bi)' }}>≈{mult.toFixed(1)}× typical</span></> : ''}</> : 'Insufficient sample'}</div>
      </div>)}
      {show('consolidation') && (() => {
        const total = con.twoSided || 0;
        const spreads = con.spreads || 0;
        const consols = con.consolidations || 0;
        const evens = Math.max(0, total - spreads - consols);
        const spPct = total ? Math.round((spreads / total) * 100) : 0;
        const evPct = total ? Math.round((evens / total) * 100) : 0;
        const coPct = total ? 100 - spPct - evPct : 0;
        return (
        <div className="vtile">
          <div className="vlab">Consolidation <Info><b>Consolidating</b> = sending several pieces to land one better player; <b>splitting</b> = trading one player for several. The number is how many assets he sends for every one he gets back — <b>{consVal}:1</b> here. Above 1 leans combiner; below 1 leans splitter.</Info></div>
          <div className="vbig num">{consVal}<span style={{ fontSize: 15, fontWeight: 500, color: 'var(--i3)' }}>:1</span></div>
          {total > 0 ? (
            <>
              <div className="cons-seg-bar">
                {spPct > 0 && <div className="cons-seg spread" style={{ width: `${spPct}%` }} />}
                {evPct > 0 && <div className="cons-seg even" style={{ width: `${evPct}%` }} />}
                {coPct > 0 && <div className="cons-seg consol" style={{ width: `${coPct}%` }} />}
              </div>
              <div className="cons-counts">
                <span><span className="cons-dot" style={{ background: '#FF9500' }} />{spPct}% split <span style={{ color: 'var(--i3)', fontWeight: 400 }}>({spreads})</span></span>
                <span><span className="cons-dot" style={{ background: 'var(--i4,#D2D2D7)' }} />{evPct}% even <span style={{ color: 'var(--i3)', fontWeight: 400 }}>({evens})</span></span>
                <span><span className="cons-dot" style={{ background: 'var(--ink)' }} />{coPct}% consol <span style={{ color: 'var(--i3)', fontWeight: 400 }}>({consols})</span></span>
              </div>
            </>
          ) : (
            <div className="vtrack"><span className="mid" /></div>
          )}
          <div className="vsub">{con.ratio == null ? 'Insufficient sample' : con.ratio >= 1.15 ? 'Bundles many into one' : con.ratio <= 0.85 ? 'Splits one into many' : 'Combines and splits about evenly'}</div>
        </div>
        );
      })()}
      {show('consistency') && (
      <div className="vtile">
        <div className="vlab">Consistency <Info>How many of their leagues share one decisive stance. {cy.agree ?? 0} of {cy.total ?? 0} agree — they play each league {cyDecisive ? 'similarly' : 'differently'}.</Info></div>
        <div className="vbig">{cy.label ? cy.label.charAt(0).toUpperCase() + cy.label.slice(1) : (cyDecisive ? `${cy.agree}/${cy.total}` : 'Low')}</div>
        <div className="vtrack"><i style={{ width: `${cy.total ? (cy.agree / cy.total) * 100 : 0}%` }} /></div>
        <div className="vsub">{cy.total ? `${cy.agree} of ${cy.total} leagues agree` : 'No clear lean'}</div>
      </div>)}
      {show('draftRoi') && (
      <div className="vtile">
        <div className="vlab">Draft ROI <Info>{roi.available ? <><b>{roi.hitRate != null ? Math.round(roi.hitRate * 100) : '—'}% hit rate</b> — {roi.hitCount} of {roi.picksGraded} rookie picks graded as hits, at today's FantasyCalc values. Hindsight, not at-the-date.</> : 'Lands with draft backfill.'}</Info></div>
        <div className="vbig">{drafterGrade || (roi.hitRate != null ? `${Math.round(roi.hitRate * 100)}%` : '—')}</div>
        <div className="vtrack"><i style={{ width: `${roi.hitRate != null ? roi.hitRate * 100 : 0}%` }} /></div>
        <div className="vsub">{roi.picksGraded != null ? `${roi.hitCount} of ${roi.picksGraded} picks hit` : 'Lands with backfill'}</div>
      </div>)}
      {show('market') && (
      <div className="vtile">
        <div className="vlab">Market <Info>Average trade margin{ma.tradesScored ? ` across ${ma.tradesScored} scored trades` : ''}. Graded at KTC's value <b>on the trade date</b>.</Info></div>
        <div className="vbig num">{ma.available && ma.avgDeltaPct != null ? signed(ma.avgDeltaPct, '%') : '—'}</div>
        <div className="vtrack"><span className="mid" />{ma.available && ma.avgDeltaPct != null && <i className="b" style={{ [ma.avgDeltaPct >= 0 ? 'left' : 'right']: '50%', width: `${Math.min(48, Math.abs(ma.avgDeltaPct) * 2)}%` }} />}</div>
        <div className="vsub">{!ma.available ? 'Needs 5 scored trades' : ma.avgDeltaPct >= 0 ? 'Slightly ahead' : 'Slightly underwater'}</div>
      </div>)}
      {show('picks') && picksBalance && (picksBalance.acquired > 0 || picksBalance.tradedAway > 0) && (() => {
        const pb = picksBalance;
        const max = Math.max(pb.acquired, pb.tradedAway, 1);
        return (
        <div className="vtile">
          <div className="vlab">Picks</div>
          <div className="picks-rows">
            <div className="pick-row">
              <div className="pr-bar" style={{ width: `${(pb.acquired / max) * 100}%` }} />
              <span className="pr-ct">{pb.acquired}</span>
            </div>
            <div className="pick-row">
              <div className="pr-bar gave" style={{ width: `${(pb.tradedAway / max) * 100}%` }} />
              <span className="pr-ct">{pb.tradedAway}</span>
            </div>
          </div>
          <div className={`picks-net-row num ${pb.net > 0 ? 'pos' : pb.net < 0 ? 'neg' : 'zero'}`}>{pb.net > 0 ? '+' : ''}{pb.net} net</div>
          <div className="vsub">acquired · traded away</div>
        </div>
        );
      })()}
    </div>
  );
};

// Positional bias — count lean (σ) PLUS the value tier he buys at each position.
// "Buys cheap TEs, not good ones": a position can run hot by volume yet come in
// well below his own per-acquisition average (the `volumeNotValue` flag).
const PositionalBias = ({ bias }) => {
  const dev = bias?.deviations;
  const overall = bias?.avgValueOverall;
  const order = ['TE', 'RB', 'QB', 'WR'];
  const rows = order.filter((p) => dev && dev[p]).map((p) => ({ p, ...dev[p] }));
  if (rows.length === 0) return <div className="empty">No read — insufficient sample</div>;
  const maxAbs = Math.max(1.6, ...rows.map((r) => Math.abs(r.z)));
  // Quality tier of what he acquires at a position, vs his own average buy.
  const tierOf = (v) => {
    if (v == null || !overall) return null;
    if (v >= overall * 1.25) return { lab: 'studs', cls: 'hi' };
    if (v <= overall * 0.75) return { lab: 'darts', cls: 'lo' };
    return { lab: 'mid', cls: 'mid' };
  };
  const nm = { QB: 'quarterbacks', RB: 'running backs', WR: 'receivers', TE: 'tight ends' };
  // One plain-English sentence per position: how often he buys it + what he pays.
  const say = (z, tier) => {
    const vol = z >= 1.3 ? 'Loads up' : z >= 0.5 ? 'Buys a lot' : z <= -1.3 ? 'Avoids' : z <= -0.5 ? 'Rarely buys' : 'About average';
    const t = tier && tier.lab;
    if (!t) return vol;
    if (z >= 0.5) return t === 'darts' ? `${vol} — but cheap ones` : t === 'studs' ? `${vol} — and pays up for studs` : `${vol} — mid-tier value`;
    if (z <= -0.5) return t === 'studs' ? `${vol} — but studs when he does` : t === 'darts' ? `${vol} — and only cheap ones` : `${vol} — mid-tier when he does`;
    return t === 'darts' ? 'About average — leans cheap' : t === 'studs' ? 'About average — leans studs' : 'About average';
  };
  const sorted = [...rows].sort((a, b) => b.z - a.z);
  const top = sorted[0], bottom = sorted[sorted.length - 1];
  let lead = null;
  if (top && top.z >= 1 && top.volumeNotValue) {
    lead = <><b>Collects cheap {nm[top.p]}</b> — he chases the position by volume, but the pieces are bottom-of-the-barrel.</>;
  } else if (top && top.z >= 1) {
    lead = <><b>Buys {nm[top.p]}</b>{top.avgValueAcquired && overall && top.avgValueAcquired >= overall * 1.25 ? <> at a premium</> : ''}{bottom && bottom.z <= -1 ? <> and looks away from {nm[bottom.p]}.</> : '.'}</>;
  } else if (bottom && bottom.z <= -1) {
    lead = <><b>Looks away from {nm[bottom.p]}.</b></>;
  }
  return (
    <>
      {lead && <div className="tcap" style={{ marginTop: 0, marginBottom: 16 }}>{lead}</div>}
      {rows.map(({ p, z, avgValueAcquired }) => {
        const tier = tierOf(avgValueAcquired);
        return (
          <div className="pb2-row" key={p}>
            <span className="pb2-pos">{p}</span>
            <span className="pb2-say">{say(z, tier)}</span>
            {avgValueAcquired != null && <span className="pb2-val num" title="Average KTC value of the players he acquires here">{nf(Math.round(avgValueAcquired))}</span>}
          </div>
        );
      })}
      <div className="tcap sub">Each line: how often he buys the position vs the average manager, and whether the players are studs or darts. The number is the average KTC value he pays there.</div>
    </>
  );
};

// Timeline seesaw (#11) — tilt toward the side they favor; plain caption.
const TimelineSeesaw = ({ tl, blindSpot }) => {
  if (!tl) return <div className="empty">No read — insufficient sample</div>;
  const net = tl.netFutureFirsts ?? 0;
  // net > 0 = hoards future (leans LATER); net < 0 = ships future (leans NOW).
  const leansNow = net < 0;
  // tilt the beam so the favored end sits low. positive deg = clockwise (right/NOW low).
  const tilt = Math.max(-7, Math.min(7, -net * 1.4));
  const W = 280, H = 120;
  const cx = W / 2, beamY = 56, beamLen = 86;
  const rad = (tilt * Math.PI) / 180;
  const lx = cx - beamLen * Math.cos(rad), ly = beamY - beamLen * Math.sin(rad);
  const rx = cx + beamLen * Math.cos(rad), ry = beamY + beamLen * Math.sin(rad);
  const favoredLabel = net === 0 ? 'now and later, evenly' : leansNow ? 'leans now' : 'leans later';
  return (
    <>
      <svg width="100%" height="120" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', margin: '6px 0 2px' }}>
        <text x={cx} y={16} fill="var(--ink)" fontSize="13" fontWeight="600" textAnchor="middle">now and later, evenly</text>
        <line x1="20" y1={beamY + 32} x2={W - 20} y2={beamY + 32} stroke="#D2D2D7" strokeWidth="1.4" />
        <text x="22" y={beamY + 50} fill="#86868B" fontSize="10" fontWeight="600" letterSpacing=".05em">FUTURE</text>
        <text x={W - 22} y={beamY + 50} fill="#86868B" fontSize="10" fontWeight="600" textAnchor="end" letterSpacing=".05em">NOW</text>
        <line x1={lx} y1={ly} x2={rx} y2={ry} stroke="var(--ink)" strokeWidth="2.4" strokeLinecap="round" />
        <path d={`M${cx} ${beamY + 4} L${cx - 8} ${beamY + 24} L${cx + 8} ${beamY + 24} Z`} fill="#86868B" />
        <circle cx={lx} cy={ly} r={leansNow ? 7 : 9} fill={leansNow ? '#C2C2C7' : '#FF7A00'} />
        <circle cx={rx} cy={ry} r={leansNow ? 9 : 7} fill={leansNow ? '#FF7A00' : '#C2C2C7'} />
        <text x={leansNow ? rx : lx} y={(leansNow ? ry : ly) - 14} fill="#C25E00" fontSize="9.5" fontWeight="700" textAnchor="middle" letterSpacing=".04em">{favoredLabel}</text>
      </svg>
      <div className="tcap">
        <b>{net === 0 ? 'Plays now and later about evenly.' : leansNow ? 'Leans win-now.' : 'Leans toward the future.'}</b><br />
        {net !== 0 ? <>Ships {Math.abs(net)} {leansNow ? 'more' : 'fewer'} future first{Math.abs(net) === 1 ? '' : 's'} than they land.</> : 'Even on future firsts.'}
        {tl.buysOlderBy != null && Math.abs(tl.buysOlderBy) >= 0.5 ? <> Buys players {Math.abs(tl.buysOlderBy)} yrs {tl.buysOlderBy > 0 ? 'older' : 'younger'} than they sell.</> : ' Buys about the same age they sell.'}
      </div>
      {blindSpot && blindSpot.type === 'qb_superflex' && (
        <div className="tcap" style={{ color: 'var(--bi)' }}><b>Blind spot —</b> 0 QBs across {blindSpot.tradesExamined} Superflex trades.</div>
      )}
    </>
  );
};

// "When they trade" heat strip (day of week) with burst note (#14).
const HeatStrip = ({ byDayOfWeek, burst }) => {
  if (!Array.isArray(byDayOfWeek) || byDayOfWeek.length !== 7) return <div className="empty">No read — insufficient sample</div>;
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const max = Math.max(...byDayOfWeek, 1);
  const hotIdx = byDayOfWeek.indexOf(max);
  return (
    <>
      <div className="heat">
        {byDayOfWeek.map((n, i) => {
          const hot = i === hotIdx && max > 0;
          return (
            <div className={`hcol${hot ? ' hot' : ''}`} key={i}>
              <span className="hnum num">{n}</span>
              <div className="hbar" style={{ height: `${Math.round((n / max) * 64) + 6}px`, opacity: hot ? 1 : 0.28 + 0.45 * (n / max) }} />
              <span className="hday">{days[i]}</span>
            </div>
          );
        })}
      </div>
      <div className="tcap"><b>Most trades land on {days[hotIdx]}.</b> {burst && burst.recentCount != null && burst.windowDays != null ? (burst.active ? <>On a tear lately — {burst.recentCount} trade{burst.recentCount === 1 ? '' : 's'} in the last {burst.windowDays} days.</> : <>Quieter lately — only {burst.recentCount} trade{burst.recentCount === 1 ? '' : 's'} in the last {burst.windowDays} days.</>) : ''}</div>
    </>
  );
};

const NetworkDonut = ({ pct }) => {
  const c = 46, r = 34, sw = 12, circ = 2 * Math.PI * r;
  return (
    <svg width="92" height="92" viewBox="0 0 92 92">
      <circle cx={c} cy={c} r={r} fill="none" stroke="rgba(255,255,255,.14)" strokeWidth={sw} />
      <circle cx={c} cy={c} r={r} fill="none" stroke="var(--b)" strokeWidth={sw} strokeDasharray={`${circ * pct} ${circ}`} strokeLinecap="round" transform={`rotate(-90 ${c} ${c})`} />
      <text x={c} y={c - 1} textAnchor="middle" style={{ fontSize: 17, fontWeight: 600, fill: '#fff' }}>{Math.round(pct * 100)}%</text>
      <text x={c} y={c + 13} textAnchor="middle" style={{ fontSize: 7.5, fontWeight: 600, fill: 'rgba(255,255,255,.55)', letterSpacing: '.06em' }}>TOP</text>
    </svg>
  );
};

// Build a plain-language "mirror" of the read from data already on the dossier
// payload — used on your own profile, where The Play is suppressed. Never
// invents: every line is gated on a real field, absent fields are dropped.
const buildSelfMirror = (dossier) => {
  const lines = [];
  const arch = dossier?.archetype;
  if (arch && !arch.insufficientSample && arch.name) {
    lines.push({ label: 'You read as', value: arch.tagline ? `${arch.name} — ${arch.tagline}` : arch.name });
  }
  // Strongest positional tell — mirror PositionalBias's lead logic.
  const dev = dossier?.traits?.positionalBias?.deviations;
  if (dev) {
    const nm = { QB: 'quarterbacks', RB: 'running backs', WR: 'receivers', TE: 'tight ends' };
    const rows = ['QB', 'RB', 'WR', 'TE'].filter((p) => dev[p]).map((p) => ({ p, z: dev[p].z }));
    if (rows.length) {
      const sorted = [...rows].sort((a, b) => b.z - a.z);
      const top = sorted[0], bottom = sorted[sorted.length - 1];
      let tell = null;
      if (top && top.z >= 1) tell = `Collects ${nm[top.p]}${bottom && bottom.z <= -1 ? `, looks away from ${nm[bottom.p]}` : ''}`;
      else if (bottom && bottom.z <= -1) tell = `Looks away from ${nm[bottom.p]}`;
      if (tell) lines.push({ label: 'Strongest tell', value: tell });
    }
  }
  // Window — mirror TimelineSeesaw's net-future-firsts read.
  const tl = dossier?.traits?.timelineTendency;
  if (tl && tl.netFutureFirsts != null) {
    const net = tl.netFutureFirsts;
    lines.push({ label: 'Window', value: net === 0 ? 'Now and later, evenly' : net < 0 ? 'Leans win-now' : 'Leans toward the future' });
  }
  return lines;
};

const ThePlay = ({ exploit, dossier }) => {
  if (!exploit) return <div className="play"><h3>No edge surfaced yet.</h3></div>;
  if (exploit.suppressed || exploit.noEdge) {
    // Self-scouting: don't leave a cryptic empty card. Explain why The Play is
    // hidden, then mirror back the read using fields already on the payload.
    if (exploit.suppressed === 'self_scouting') {
      const mirror = buildSelfMirror(dossier);
      return (
        <div className="play">
          <div className="pkick">The Opening</div>
          <h3>How rivals read you</h3>
          <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,.6)', marginTop: 10, lineHeight: 1.5 }}>
            The Opening is the angle an opponent would use to trade against this manager — it's hidden on your own scouting report. Open a leaguemate's profile to see theirs.
          </div>
          {mirror.length > 0 && (
            <div className="pgrid" style={{ marginTop: 18 }}>
              {mirror.map((line, i) => (
                <div className="pblock" key={i}>
                  <div className="ph">{line.label}</div>
                  <div className="pt">{line.value}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }
    const head = exploit.noEdge ? 'No edge — deal them straight.' : exploit.suppressed === 'co_owned_roster' ? 'Co-owned roster — read suppressed.' : 'Not enough to read yet.';
    return (
      <div className="play">
        <div className="pkick">The Opening</div>
        <h3>{head}</h3>
        {exploit.message && <div style={{ fontSize: 13, color: 'rgba(255,255,255,.55)', marginTop: 8, lineHeight: 1.5 }}>{exploit.message}</div>}
      </div>
    );
  }
  const m = exploit.moves || {};
  return (
    <div className="play">
      <div className="pkick">The Opening</div>
      {dossier?.tier === 'self' && <div className="pmirror">How rivals read you — you can't scout yourself.</div>}
      <h3>{exploit.headline}</h3>
      {exploit.caveat && <div style={{ fontSize: 12.5, color: 'rgba(255,160,90,.95)', marginTop: 8, lineHeight: 1.5 }}>{exploit.caveat}</div>}
      <div className="pgrid">
        {m.open && <div className="pblock"><div className="ph">Open</div><div className="pt">{m.open}</div></div>}
        {m.pry && <div className="pblock"><div className="ph">Pry</div><div className="pt">{m.pry}</div></div>}
        {m.frame && <div className="pblock"><div className="ph">Frame</div><div className="pt">{m.frame}</div></div>}
        {m.when && <div className="pblock"><div className="ph">When</div><div className="pt">{m.when}</div></div>}
      </div>
      {(exploit.gateStrip || []).length > 0 && (
        <div className="gatechips">
          {exploit.gateStrip.map((g, i) => <span className="gchip" key={i}><span className={`gd${g.pass ? '' : ' no'}`} />{g.detail || g.gate}</span>)}
        </div>
      )}
      {exploit.live === false && exploit.liveNote && <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.45)', marginTop: 14, lineHeight: 1.5 }}>{exploit.liveNote}</div>}
    </div>
  );
};

const Situation = ({ situation }) => {
  const leagues = situation?.leagues || [];
  if (leagues.length === 0) return <div className="situation"><div className="skick">Situation</div><div className="empty" style={{ marginTop: 8 }}>No dynasty rosters synced — no situation to read.</div></div>;
  const consistency = situation.consistency?.label;
  return (
    <div className="situation">
      <div className="skick">Situation</div>
      <div className="ssub">State per league — never averaged.<br />Bar = veteran share (players with 5+ yrs experience). Younger rosters lean rebuild. The pill is the read.</div>
      <div>
        {leagues.map((l) => {
          const s = STANCE[l.stance] || STANCE.neutral;
          const vet = l.rosterAgeMix?.vetShare;
          const rec = l.record || {};
          return (
            <div className="lrow" key={l.leagueId}>
              <div>
                <div className="lname">{l.name}</div>
                <div className="lmeta">{l.isSuperFlex ? 'SF' : '1QB'}{rec.wins != null ? ` · ${rec.wins}-${rec.losses}${rec.ties ? `-${rec.ties}` : ''}` : ''}{l.sharedWithViewer ? ' · shared with you' : ''}</div>
              </div>
              <span className={`stancepill ${s.cls}`}>{s.label}</span>
              <div className="vbarwrap">
                <div className="vbar"><i style={{ width: `${vet != null ? Math.round(vet * 100) : 0}%` }} /></div>
                <span className="vpct num">{vet != null ? `${Math.round(vet * 100)}% vets` : '—'}</span>
              </div>
            </div>
          );
        })}
      </div>
      {consistency && <div className="snote">Different in every league — consistency reads <b>{consistency}</b>.</div>}
    </div>
  );
};

// NFL exposure (#21) — his real stacks lead as cards (with the sell/unwind read);
// the long tail collapses into a compact tile grid. From getProfile rosters + player batch.
const NFLExposure = ({ teams }) => {
  if (!teams || teams.length === 0) return <div className="empty">No current rosters synced — no team exposure to read.</div>;
  // Rank by total exposure, then by how much of it actually starts (#91).
  const ranked = [...teams].sort((a, b) => (b.players - a.players) || (b.startable - a.startable) || a.team.localeCompare(b.team));
  // Feature the genuine stacks (≥5 players); always at least 2, at most 3.
  let featured = ranked.filter((t) => t.players >= 5);
  if (featured.length < 2) featured = ranked.slice(0, 2);
  if (featured.length > 3) featured = featured.slice(0, 3);
  const featSet = new Set(featured.map((t) => t.team));
  const rest = ranked.filter((t) => !featSet.has(t.team));
  const isStrong = (t) => (t.players ? t.startable / t.players : 0) >= 0.45;
  return (
    <div className="card">
      <div className="th-card-title" style={{ marginBottom: 6 }}>NFL exposure</div>
      <p style={{ fontSize: 13, lineHeight: 1.45, color: 'var(--i2)', margin: '0 0 18px', maxWidth: 560 }}>
        A heavy stack reads two ways. He's bullish on that team — sell him those pieces. Or he's overweight and due to unwind.
      </p>
      <div className="nflx-cards">
        {featured.map((t) => {
          const s = isStrong(t);
          return (
            <div className={`nflx-card ${s ? 'strong' : 'watch'}`} key={t.team}>
              <div className="nflx-card-top"><span className="nflx-team">{t.team}</span><span className={`nflx-angle ${s ? 's' : 'w'}`}>{s ? 'Conviction' : 'Watch'}</span></div>
              <div className="nflx-cnt"><b>{t.startable}</b> of {t.players} start</div>
              <div className="nflx-pips">{Array.from({ length: t.players }).map((_, i) => <span key={i} className={`nflx-pip${i < t.startable ? (s ? ' on' : ' on w') : ''}`} />)}</div>
              <div className="nflx-read">{s ? 'He believes in this team — expect to pay a premium to pry one loose. But a stack this heavy is also the first thing he’d unwind.' : `Overweight depth — ${t.players - t.startable} ride the bench. More clog than conviction.`}</div>
            </div>
          );
        })}
      </div>
      {rest.length > 0 && (
        <>
          <div className="nflx-rest-h">The rest</div>
          <div className="nflx-tiles">
            {rest.map((t) => (
              <div className="nflx-tile" key={t.team}>
                <div className="nflx-tile-top"><span className="nflx-tm">{t.team}</span><span className={`nflx-dot ${isStrong(t) ? 's' : 'w'}`} /></div>
                <span className="nflx-tile-cnt"><b>{t.startable}</b> start · {t.players} total</span>
              </div>
            ))}
          </div>
        </>
      )}
      <div className="nflx-legend">
        <span><span className="nflx-dot s" />Mostly starters</span>
        <span><span className="nflx-dot w" />Depth-heavy</span>
      </div>
    </div>
  );
};

// BLUEPRINT TAB — identity only: who he is (Blueprint, rendered above) + how to
// attack him (The Opening) + what he believes in / heavy bags (NFL exposure).
const ReadPanel = ({ data, nflTeams }) => {
  // Fresh mount each time the Read tab is (re)activated — the fade replays.
  const shown = useReveal(true);
  return (
    <div className={`panel ft-fade ${shown ? 'ft-play' : ''}`}>
      <div className="kicker" style={{ marginTop: 8 }}><span className="seq">02</span> · The opening <span className="ln" /></div>
      <ThePlay exploit={data.exploit} dossier={data} />

      <div className="kicker" style={{ marginTop: 32 }}><span className="seq">03</span> · What he believes in <span className="ln" /></div>
      <NFLExposure teams={nflTeams} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// TRADES TAB — his patterns (the tells, incl. the burst), then the at-the-date
// ledger with verdict + then→now aging.
// ---------------------------------------------------------------------------
const TradesPanel = ({ data, trades, playerData, loading }) => {
  const pb = data.trophyCase?.picksBalance;
  const net = data.traits?.network;
  const activityStats = {
    tradesPerSeason: data.hud?.activity?.tradesPerLeagueSeason,
    median: data.baselines?.activityMedian,
  };
  // Fresh mount each time this tab is (re)activated — the fade replays.
  const shown = useReveal(true);
  return (
  <div className={`panel ft-fade ${shown ? 'ft-play' : ''}`}>
    <div className="kicker"><span className="seq">01</span> · The record <span className="ln" /></div>
    <Record marketAcumen={data.hud?.marketAcumen} total={data.identity?.tradeCount} />
    {data.hud && <Vitals hud={data.hud} baselines={data.baselines} compact only={['activity', 'consolidation', 'market', 'picks']} picksBalance={pb} />}

    <HistoryPanel trades={trades} playerData={playerData} loading={loading} marketAcumen={data.hud?.marketAcumen} refreshedAt={data.computedAt} activityStats={activityStats} />

    <div className="kicker" style={{ marginTop: 32 }}><span className="seq">02</span> · His patterns <span className="ln" /></div>
    <div className="tells">
      <div className="tcard">
        <h3>Positional bias <Info>Two reads per position: <b>how often</b> he acquires it vs the average Fantasy Taco manager, and <b>what he pays</b> — the number on the right is the average KTC value of the players he gets there (higher = studs, lower = darts).</Info></h3>
        <PositionalBias bias={data.traits?.positionalBias} />
      </div>
      <div className="tcard">
        <h3>Timeline <Info>Future picks vs win-now players. The seesaw tilts toward the side they favor.</Info></h3>
        <TimelineSeesaw tl={data.traits?.timelineTendency} blindSpot={data.traits?.positionalBias?.blindSpot} />
      </div>
      <div className="tcard">
        <h3>When he trades <Info>Trades by day of week, all leagues. The burst note flags an active run.</Info></h3>
        <HeatStrip byDayOfWeek={data.timing?.byDayOfWeek || data.traits?.cadence?.byDayOfWeek} burst={data.timing?.burst || data.traits?.cadence?.burst} />
      </div>
    </div>

    <div className="kicker" style={{ marginTop: 32 }}><span className="seq">03</span> · Who he trades with <span className="ln" /></div>
    {net && net.partners && net.partners.length > 0 ? <NetworkGraph network={net} /> : <div className="empty">No trade partners surfaced yet.</div>}
  </div>
  );
};

// ---------------------------------------------------------------------------
// LEAGUES TAB — footprint: where he plays + who he trades with + exposure.
// Consolidates the old Network / Portfolio / Situation / NFL into one visual
// tab. Light on words by design.
// ---------------------------------------------------------------------------
// A hand-built Lombardi trophy — silver football on a tapered pedestal, with a
// soft gold glow. The hero of the shareable card.
// Hand-built Vince Lombardi trophy — a fat chrome football on a tall, concave
// tapered pedestal. Closer to the real thing than the old thin-leaf version.
const Lombardi = () => (
  <svg className="lombardi" viewBox="0 0 120 232" width="108" height="208" fill="none" aria-hidden="true">
    <defs>
      <linearGradient id="ftSilver" x1="0" y1="0" x2="1" y2=".5">
        <stop offset="0" stopColor="#ffffff" />
        <stop offset=".2" stopColor="#f1f3f6" />
        <stop offset=".44" stopColor="#c2c7ce" />
        <stop offset=".58" stopColor="#9b9fa8" />
        <stop offset=".78" stopColor="#d4d7dc" />
        <stop offset="1" stopColor="#74787f" />
      </linearGradient>
      <linearGradient id="ftSilverDk" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#b6bac1" />
        <stop offset=".5" stopColor="#83878f" />
        <stop offset="1" stopColor="#50535a" />
      </linearGradient>
      <radialGradient id="ftGlow" cx=".5" cy=".36" r=".62">
        <stop offset="0" stopColor="#FFC24B" stopOpacity=".34" />
        <stop offset="1" stopColor="#FFC24B" stopOpacity="0" />
      </radialGradient>
    </defs>
    <ellipse cx="60" cy="68" rx="58" ry="64" fill="url(#ftGlow)" />
    {/* football — fatter, rounder, upright */}
    <path d="M60 10 C85 24 85 56 60 84 C35 56 35 24 60 10 Z" fill="url(#ftSilver)" stroke="#eef1f4" strokeWidth=".8" />
    {/* right-half shade for roundness */}
    <path d="M60 11 C74 26 74 56 60 83 C69 56 71 26 60 11 Z" fill="#000" fillOpacity=".06" />
    {/* side seams */}
    <path d="M60 16 C52 38 52 58 60 80" stroke="#83878f" strokeWidth="1.1" strokeOpacity=".5" fill="none" />
    <path d="M60 16 C68 38 68 58 60 80" stroke="#83878f" strokeWidth="1.1" strokeOpacity=".38" fill="none" />
    {/* laces */}
    <path d="M60 26 V58" stroke="#787c85" strokeWidth="1.7" strokeLinecap="round" />
    <path d="M53 31 H67 M53 38 H67 M53 45 H67 M53 52 H67" stroke="#787c85" strokeWidth="1.7" strokeLinecap="round" />
    {/* chrome highlight */}
    <path d="M50 18 C44 38 44 60 51 80" stroke="#ffffff" strokeWidth="2.4" strokeOpacity=".5" strokeLinecap="round" fill="none" />
    {/* collar where the ball meets the stand */}
    <ellipse cx="60" cy="86" rx="9" ry="3" fill="url(#ftSilverDk)" />
    {/* stand — tall, slender, concave 3-sided tower (lit left / shadow right + ridge) */}
    <path d="M56 88 C53 128 47 170 38 204 L60 204 L60 88 Z" fill="url(#ftSilver)" />
    <path d="M64 88 C67 128 73 170 82 204 L60 204 L60 88 Z" fill="url(#ftSilverDk)" />
    <path d="M60 90 V204" stroke="#ffffff" strokeWidth="1" strokeOpacity=".42" />
    {/* base */}
    <path d="M33 204 H87 L93 220 H27 Z" fill="url(#ftSilverDk)" />
    <rect x="25" y="219" width="70" height="8" rx="2" fill="url(#ftSilverDk)" />
  </svg>
);

// The trophy case — a shareable, Lombardi-led card. Career hardware (top 2 =
// the championship game) flanks the trophy; the record line sits beneath.
const TrophyCase = ({ trophy, archetype, username }) => {
  const [copied, setCopied] = useState(false);
  // Reveal-gated count-up: numbers start at 0 and animate up to their real
  // value shortly after the card mounts, instead of popping in fully-formed.
  const shown = useReveal(!!trophy);
  const p = trophy?.podium || { first: trophy?.championships || 0, second: 0, third: 0 };
  const po = trophy?.playoffAppearances || 0;
  const dyn = trophy?.leagueCount?.dynasty ?? 0;
  const cg = trophy?.championshipGames ?? ((p.first || 0) + (p.second || 0));
  const titlesCU = useCountUp(shown ? (p.first || 0) : 0);
  const finalsCU = useCountUp(shown ? (p.second || 0) : 0);
  const cgCU = useCountUp(shown ? cg : 0);
  const poCU = useCountUp(shown ? po : 0);
  const winCU = useCountUp(shown ? (trophy?.winPercent ?? 0) : 0, { suffix: '%' });
  const dynCU = useCountUp(shown ? dyn : 0);
  const tickCls = (cu, extra) => `${extra} ft-tick${cu.flashing ? ` ${cu.direction}` : ''}`;
  if (!trophy) return null;
  const share = () => {
    try {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) { /* clipboard blocked — the card is still screenshot-ready */ }
  };
  return (
    <section className="trophy">
        <div className="trophy-glow" />
        <div className="trophy-head">
          <span className="trophy-brand">
            <span className="tg"><svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M3 14a9 9 0 0 1 18 0" stroke="#1D1D1F" strokeWidth="2.4" strokeLinecap="round" /><path d="M11 4l3 3-3 3" stroke="#FF7A00" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg></span>
            Manager Intel
          </span>
          <button type="button" className="trophy-share" onClick={share}>{copied ? 'Link copied' : 'Share'}</button>
        </div>
        <div className="trophy-id">
          {username && <span className="trophy-user">@{username}</span>}
          {archetype?.name && <span className="trophy-arch">{archetype.name}</span>}
        </div>
        <div className="trophy-hero">
          <div className="fin gold">
            <span className={tickCls(titlesCU, 'fcount num')}>{titlesCU.display}</span>
            <span className="flab">Titles</span>
          </div>
          <div className="trophy-stage">
            <Lombardi />
            <div className="trophy-cg"><b className={tickCls(cgCU, 'num')}>{cgCU.display}</b> {cg === 1 ? 'championship game' : 'championship games'}</div>
          </div>
          <div className="fin silver">
            <span className={tickCls(finalsCU, 'fcount num')}>{finalsCU.display}</span>
            <span className="flab">Finals</span>
          </div>
        </div>
        <div className="trophy-stats">
          <b className={tickCls(poCU, 'num')}>{poCU.display}</b> playoff {po === 1 ? 'run' : 'runs'}<span className="dot">·</span>
          <b className={tickCls(winCU, 'num')}>{winCU.display}</b> win rate<span className="dot">·</span>
          <b className={tickCls(dynCU, 'num')}>{dynCU.display}</b> dynasty {dyn === 1 ? 'league' : 'leagues'}
        </div>
        <div className="trophy-foot">fantasytaco.com · manager intel</div>
      </section>
  );
};

// Short per-year finish labels for the portfolio Gantt cells.
const SHORTFIN = { champion: '1st', finalist: '2nd', third: '3rd', made_playoffs: 'Playoffs', missed_playoffs: 'Missed' };

const REC = (r) => (r && (r.wins || r.losses || r.ties) ? `${r.wins || 0}-${r.losses || 0}${r.ties ? `-${r.ties}` : ''}` : null);

// Build the full franchise list from the all-seasons Dynasty YoY feed — every
// league he's ever rostered across the past 5 years, including ones he left or
// that folded (which the Manager Intel sync alone never sees). Playoff finishes
// are overlaid from careerInLeague where we have them (the leagues still synced).
const franchisesFromYoY = (dynastyYoY, leagues) => {
  const yoy = Array.isArray(dynastyYoY) ? dynastyYoY : [];
  if (!yoy.length) return null;
  // finish overlay: sleeperLeagueId (latest) -> { [season]: {result,wins,losses,ties} }
  const finishById = {};
  const sfById = {};
  (leagues || []).forEach((l) => {
    sfById[l.sleeperLeagueId] = !!l.isSuperFlex;
    const bs = l.careerInLeague?.bySeason || [];
    if (!bs.length) return;
    const m = {};
    bs.forEach((s) => { m[Number(s.season)] = s; });
    finishById[l.sleeperLeagueId] = m;
  });
  const fr = new Map();
  yoy.forEach((yr) => {
    const season = Number(yr.season);
    (yr.leaguesToDisplay || []).forEach((d) => {
      const key = d.leagueId;
      if (!key) return;
      if (!fr.has(key)) {
        fr.set(key, {
          key,
          name: d.metadata?.name || '—',
          avatarUrl: d.metadata?.avatar || null,
          superFlex: !!sfById[key],
          startYear: Number(d.firstYear) || season,
          joinType: 'established',
          endYear: null,
          endType: null,
          recByYear: {},
        });
      }
      const f = fr.get(key);
      const fy = Number(d.firstYear) || season;
      if (fy < f.startYear) f.startYear = fy;
      if (d.joinType === 'startup' || d.joinType === 'orphan') f.joinType = d.joinType;
      if (d.joinType === 'left' || d.joinType === 'folded') { f.endYear = season; f.endType = d.joinType; }
      f.recByYear[season] = d.userRecord || d.metadata?.userRecord || null;
    });
  });
  return { franchises: Array.from(fr.values()), finishById };
};

const END_SHORT = { left: 'Left', folded: 'Folded' };

// The portfolio as a year-by-year timeline: rows = franchises, columns = years.
// Completed seasons show a finish-colored medal where known; the founding year
// carries a New/Orphan cap; the current season is an "active" stripe; leagues he
// left or that folded end with a Left/Folded cap. Driven by the all-seasons
// Dynasty YoY feed so it covers every league over the past 5 years.
const PortfolioTimeline = ({ leagues, exit, dynastyYoY }) => {
  const built = franchisesFromYoY(dynastyYoY, leagues);
  const yoy = Array.isArray(dynastyYoY) ? dynastyYoY : [];

  // Fallback: no Dynasty YoY yet → original Manager-Intel-only view.
  if (!built || !built.franchises.length) {
    if (!leagues.length) return <div className="empty">No dynasty rosters synced.</div>;
    const foundedOf = (l) => { const s = l.careerInLeague?.bySeason || []; return s.length ? Number(s[0].season) : (Number(l.season) || 0); };
    const curYear = Math.max(...leagues.map((l) => Number(l.season) || 0));
    const minYear = Math.min(...leagues.map(foundedOf));
    const years = []; for (let y = minYear; y <= curYear; y += 1) years.push(y);
    const ordered = [...leagues].sort((a, b) => foundedOf(a) - foundedOf(b) || a.name.localeCompare(b.name));
    return (
      <div className="pf">
        <div className="pf-head"><div className="pf-h1">How the empire was built</div></div>
        <div className="gantt" style={{ gridTemplateColumns: `clamp(96px,22%,150px) repeat(${years.length}, 1fr)` }}>
          <div className="g-corner" />
          {years.map((y) => <div key={y} className={`g-yr${y === curYear ? ' now' : ''}`}>{y}</div>)}
          {ordered.map((l) => {
            const byYear = {}; (l.careerInLeague?.bySeason || []).forEach((s) => { byYear[Number(s.season)] = s; });
            const founded = foundedOf(l);
            const orphan = !!(l.joinType && l.joinType.toLowerCase().includes('orphan'));
            return (
              <React.Fragment key={l.leagueId}>
                <div className="g-lg" title={l.name}>{l.name}{l.isSuperFlex && <span className="g-sf">SF</span>}</div>
                {years.map((y) => {
                  if (y < founded) return <div key={y} className="g-cell"><span className="g-empty" /></div>;
                  const cap = y === founded ? (orphan ? { t: 'Orphan', cls: 'orphan' } : { t: 'New', cls: '' }) : null;
                  if (y === curYear) return <div key={y} className="g-cell"><div className="g-seg active">{cap && <span className={`g-cap ${cap.cls}`}>{cap.t}</span>}Active</div></div>;
                  const s = byYear[y];
                  if (!s) return <div key={y} className="g-cell"><span className="g-empty" /></div>;
                  const fin = FINISH[s.result] || FINISH.missed_playoffs;
                  return <div key={y} className="g-cell"><div className={`g-seg ${fin.cls}`} title={`${SHORTFIN[s.result] || ''} · ${s.wins}-${s.losses}`}>{cap && <span className={`g-cap ${cap.cls}`}>{cap.t}</span>}{SHORTFIN[s.result] || '—'}</div></div>;
                })}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  }

  const { franchises, finishById } = built;
  const curYear = Math.max(...yoy.map((y) => Number(y.season) || 0));
  const minYear = Math.min(...franchises.map((f) => f.startYear));
  const years = []; for (let y = minYear; y <= curYear; y += 1) years.push(y);
  const totalByYear = {}; yoy.forEach((y) => { totalByYear[Number(y.season)] = y.totalDynastyLeagues || 0; });
  // Active first (sorted by founding), then exited (most recent exit first).
  const ordered = [...franchises].sort((a, b) => {
    if (!a.endType !== !b.endType) return a.endType ? 1 : -1;
    if (a.endType && b.endType) return (b.endYear - a.endYear) || a.name.localeCompare(b.name);
    return a.startYear - b.startYear || a.name.localeCompare(b.name);
  });
  const activeNow = totalByYear[curYear] ?? franchises.filter((f) => !f.endType).length;
  const folded = exit?.foldedLeagues ?? franchises.filter((f) => f.endType === 'folded').length;
  const left = franchises.filter((f) => f.endType === 'left').length;

  return (
    <div className="pf">
      <div className="pf-head">
        <div className="pf-h1">How the empire was built</div>
        <div className="pf-meta"><b>{activeNow}</b> active in {curYear} · founded {minYear}{minYear !== curYear ? `–${curYear}` : ''}{folded ? <> · <b>{folded}</b> folded</> : null}{left ? <> · <b>{left}</b> left</> : null}</div>
      </div>
      <div className="pf-growth"><span className="pf-growth-l">Leagues by year</span>{years.map((y, i) => (
        <React.Fragment key={y}>{i > 0 && <span className="ar">→</span>}<span className={`g${y === curYear ? ' now' : ''}`}>{totalByYear[y] ?? 0}</span></React.Fragment>
      ))}</div>
      <div className="gantt" style={{ gridTemplateColumns: `clamp(96px,22%,150px) repeat(${years.length}, 1fr)` }}>
        <div className="g-corner" />
        {years.map((y) => <div key={y} className={`g-yr${y === curYear ? ' now' : ''}`}>{y}</div>)}
        {ordered.map((f) => {
          const fins = finishById[f.key] || {};
          const orphan = f.joinType === 'orphan';
          const startup = f.joinType === 'startup';
          return (
            <React.Fragment key={f.key}>
              <div className="g-lg" title={f.name}><LeagueAvatar url={f.avatarUrl} name={f.name} size={18} />{f.name}{f.superFlex && <span className="g-sf">SF</span>}</div>
              {years.map((y) => {
                if (y < f.startYear) return <div key={y} className="g-cell"><span className="g-empty" /></div>;
                const cap = y === f.startYear ? (orphan ? { t: 'Orphan', cls: 'orphan' } : startup ? { t: 'New', cls: '' } : null) : null;
                // Exit year → Left/Folded cap.
                if (f.endType && y === f.endYear) {
                  return <div key={y} className="g-cell"><div className={`g-seg ${f.endType}`} title={END_SHORT[f.endType]}>{cap && <span className={`g-cap ${cap.cls}`}>{cap.t}</span>}{END_SHORT[f.endType]}</div></div>;
                }
                if (y > (f.endYear || curYear)) return <div key={y} className="g-cell"><span className="g-empty" /></div>;
                // Playoff finish (only for still-synced leagues) → medal.
                const fin = fins[y];
                if (fin && fin.result) {
                  const m = FINISH[fin.result] || FINISH.missed_playoffs;
                  return <div key={y} className="g-cell"><div className={`g-seg ${m.cls}`} title={`${SHORTFIN[fin.result] || ''} · ${fin.wins}-${fin.losses}`}>{cap && <span className={`g-cap ${cap.cls}`}>{cap.t}</span>}{SHORTFIN[fin.result] || '—'}</div></div>;
                }
                // Current, still-active season → "Active" stripe.
                if (y === curYear && !f.endType) {
                  return <div key={y} className="g-cell"><div className="g-seg active">{cap && <span className={`g-cap ${cap.cls}`}>{cap.t}</span>}Active</div></div>;
                }
                // Otherwise a plain season cell with the record if we have it.
                const rec = REC(f.recByYear[y]);
                return <div key={y} className="g-cell"><div className="g-seg rec" title={rec || ''}>{cap && <span className={`g-cap ${cap.cls}`}>{cap.t}</span>}{rec || '·'}</div></div>;
              })}
            </React.Fragment>
          );
        })}
      </div>
      <div className="pf-legend">
        {[['Champion', 'gold'], ['Runner-up', 'silver'], ['Third', 'bronze'], ['Made playoffs', 'po'], ['Missed', 'miss'], ['In progress', 'active'], ['Left', 'left'], ['Folded', 'folded']].map(([lbl, cls]) => (
          <span key={lbl} className="pf-key"><span className={`pf-kd ${cls}`} />{lbl}</span>
        ))}
      </div>
    </div>
  );
};

// League avatar — Sleeper league logo with an initial fallback.
const LeagueAvatar = ({ url, name, size = 34 }) => {
  const [err, setErr] = useState(false);
  const st = { width: size, height: size, fontSize: Math.round(size * 0.4), borderRadius: 9 };
  const src = url ? (String(url).startsWith('http') ? url : `https://sleepercdn.com/avatars/thumbs/${url}`) : null;
  if (!src || err) return <span className="lav" style={st}>{(name || '?').slice(0, 1).toUpperCase()}</span>;
  return <img className="lav" style={st} alt="" src={src} onError={() => setErr(true)} />;
};

// A clickable league row: avatar + best finish summary; expands to a per-season
// history timeline (year · finish · record), the current season shown in progress.
const LeagueRow = ({ l }) => {
  const [open, setOpen] = useState(false);
  const c = l.careerInLeague;
  const fin = c?.bestFinish ? FINISH[c.bestFinish] : null;
  const orphan = !!(l.joinType && l.joinType.toLowerCase().includes('orphan'));
  const rec = c?.latest ? `${c.latest.wins}-${c.latest.losses}${c.latest.ties ? `-${c.latest.ties}` : ''}` : null;
  const bySeason = c?.bySeason || [];
  const hasHistory = bySeason.length > 0;
  return (
    <div className={`lgr${open ? ' open' : ''}`}>
      <button type="button" className="lgr-head" onClick={() => hasHistory && setOpen((o) => !o)} disabled={!hasHistory}>
        <LeagueAvatar url={l.avatarUrl} name={l.name} />
        <div className="lgr-main">
          <div className="lgr-name">{l.name}{l.isSuperFlex && <span className="lgc-sf">SF</span>}{orphan && <span className="lgc-orphan">orphan</span>}</div>
          <div className="lgr-sub">
            {c ? (
              <>
                <span className={`lgc-medal ${fin ? fin.cls : 'miss'}`} />
                <span className="lgr-fin">{fin ? fin.label : '—'}{c.bestFinishSeason ? ` · ${c.bestFinishSeason}` : ''}</span>
                <span className="lgr-dim">{c.seasons} {c.seasons === 1 ? 'season' : 'seasons'}{rec ? ` · ${rec} last` : ''}</span>
              </>
            ) : <span className="lgr-dim">New league{orphan ? ' · orphan takeover' : ''}</span>}
          </div>
        </div>
        {hasHistory && <span className="lgr-chev">{open ? '▲' : '▼'}</span>}
      </button>
      {open && hasHistory && (
        <div className="lgr-tl">
          <div className="lgr-tl-row">
            <span className="lgr-tl-yr num">{l.season}</span>
            <span className="lgc-medal active" />
            <span className="lgr-tl-fin">In progress</span>
            <span className="lgr-tl-rec" />
          </div>
          {[...bySeason].reverse().map((s) => {
            const f = FINISH[s.result] || FINISH.missed_playoffs;
            return (
              <div className="lgr-tl-row" key={s.season}>
                <span className="lgr-tl-yr num">{s.season}</span>
                <span className={`lgc-medal ${f.cls}`} />
                <span className="lgr-tl-fin">{f.label}</span>
                <span className="lgr-tl-rec num">{s.wins}-{s.losses}{s.ties ? `-${s.ties}` : ''}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const LeaguesPanel = ({ data, nflTeams, dynastyYoY, sleeperUsername }) => {
  const leagues = data.situation?.leagues || [];
  const exit = data.timing?.exitRisk || {};
  // Fresh mount each time this tab is (re)activated — the fade replays.
  const shown = useReveal(true);
  return (
    <div className={`panel ft-fade ${shown ? 'ft-play' : ''}`}>
      <TrophyCase trophy={data.trophyCase} archetype={data.archetype} username={sleeperUsername || data.identity?.sleeperUsername} />
      <div className="kicker" style={{ marginTop: 30 }}><span className="seq">02</span> · The portfolio <span className="ln" /></div>
      <PortfolioTimeline leagues={leagues} exit={exit} dynastyYoY={dynastyYoY} />
      {leagues.length === 0 ? <div className="empty">No dynasty rosters synced.</div> : (
        <div className="lgrs ft-stagger ft-play" style={{ marginTop: 16 }}>
          {leagues.map((l) => <LeagueRow l={l} key={l.leagueId} />)}
        </div>
      )}
      {exit.level && exit.basis !== 'no_yoy_data' && <div className="lg-exit"><b>{exit.level.charAt(0).toUpperCase() + exit.level.slice(1)} exit risk</b> — {exit.leaguesLeft ?? 0} of {exit.totalDynastyLeagues ?? leagues.length} dynasty leagues left.</div>}

    </div>
  );
};

// ---------------------------------------------------------------------------
// DRAFTS TAB
// ---------------------------------------------------------------------------

// One pick asset row inside an on-the-clock trade card.
const ClockAsset = ({ it }) => {
  if (it.kind === 'pick' || it.t === 'pick') {
    // When the pick became a known player (resolvePick attaches `player` +
    // `playerId`), surface that player as the subtitle under the pick chip —
    // instead of the generic word "pick". The chip keeps the pick label
    // ("1.09"); the subtitle falls back to "pick" when no player is known.
    const became = it.player || null;
    return (
      <div className="dr-asset">
        <span className="dr-asset-pickchip num">{it.label || 'PK'}</span>
        <div className="dr-asset-txt">
          <div className="dr-asset-name">{it.label || it.name || 'Pick'}</div>
          <div className="dr-asset-sub" title={became || 'pick'}>{became || 'pick'}</div>
        </div>
      </div>
    );
  }
  return (
    <div className="dr-asset">
      <PlayerAvatar id={it.id || it.playerId} name={it.name} size={38} />
      <div className="dr-asset-txt"><div className="dr-asset-name">{it.name || ''}</div><div className="dr-asset-sub">player</div></div>
    </div>
  );
};

const DraftListAvatar = ({ playerId }) => {
  const [failed, setFailed] = useState(false);
  if (!playerId || failed) return <div className="dr-list-av-ph" />;
  return (
    <img
      className="dr-list-av"
      src={`https://sleepercdn.com/content/nfl/players/thumb/${playerId}.jpg`}
      alt=""
      onError={() => setFailed(true)}
    />
  );
};

const DraftList = ({ d }) => {
  const picks = [...(d.picks || [])].sort((a, b) => {
    if ((a.round || 0) !== (b.round || 0)) return (a.round || 0) - (b.round || 0);
    const sa = a.slotInRound != null ? a.slotInRound : (a.draftSlot || 0);
    const sb = b.slotInRound != null ? b.slotInRound : (b.draftSlot || 0);
    return sa - sb;
  });
  const ungraded = d.graded === false || d.grade == null;
  if (!picks.length) return <div className="empty" style={{ fontSize: 14 }}>No picks recorded.</div>;
  return (
    <div className="dr-list">
      {picks.map((p, i) => {
        const verdict = !ungraded ? (p.verdict || 'fair') : null;
        return (
          <div className="dr-list-row" key={i}>
            <span className="dr-list-slot num">{slotLabel(p)}</span>
            <DraftListAvatar playerId={p.playerId} />
            {p.position && <span className="dr-list-pos">{p.position}</span>}
            <span className="dr-list-name">{p.playerName || '—'}</span>
            {verdict && <span className={`dr-list-v ${verdict}`}>{verdict}</span>}
            {p.currentValue != null && <span className="dr-list-val num">{nf(p.currentValue)}</span>}
          </div>
        );
      })}
    </div>
  );
};

const DraftBoard = ({ d }) => {
  const [view, setView] = useState('board');
  const picks = d.picks || [];
  // Ungraded drafts (the current rookie class — hasn't played) carry no grade:
  // the board renders all cells neutral, the manager's own picks keep the subtle
  // isMine ring but get no hit/bust coloring and no verdict label.
  const ungraded = d.graded === false || d.grade == null;
  // The manager's own graded picks carry verdict / passedOn. Key by playerId so
  // we can overlay the verdict onto the full board's matching cell.
  const verdictByPlayer = {};
  picks.forEach((p) => { if (p.playerId) verdictByPlayer[p.playerId] = p; });
  // Full board = every team's pick (FEATURE #28). Fall back to the manager's own
  // picks for older cached payloads that predate the board field.
  const hasBoard = Array.isArray(d.board) && d.board.length > 0;
  const source = hasBoard ? d.board : picks;
  // Column key must be draftSlot, not slotInRound: draftSlot is the pick's fixed
  // original-team seed (stable across every round), while slotInRound is the
  // chronological "Nth pick of the round" — correct for the "R.SS" label below,
  // but it reverses with the round on snake boards, so keying columns by it
  // would silently swap which team's picks land in a given column each round.
  const enriched = source.map((p) => {
    const s = p.draftSlot != null ? p.draftSlot : p.slotInRound;
    const mine = hasBoard ? !!p.isMine : true;
    const v = mine ? verdictByPlayer[p.playerId] : null;
    return { ...p, _r: p.round, _s: s, _slot: slotLabel(p), _mine: mine, _verdict: v?.verdict || null, _passedOn: v?.passedOn || null };
  }).filter((p) => p._r != null && p._s != null);
  let maxRound = 1, maxSlot = 1;
  enriched.forEach((p) => { if (p._r > maxRound) maxRound = p._r; if (p._s > maxSlot) maxSlot = p._s; });
  // Columns = teams. board/rounds is the true team count when available.
  const fromBoard = d.boardSize && maxRound ? Math.round(d.boardSize / maxRound) : 0;
  let cols;
  if (fromBoard > 0 && fromBoard <= 16 && fromBoard >= maxSlot) cols = fromBoard;
  else cols = Math.max(maxSlot, 12);
  const byRC = {};
  enriched.forEach((p) => { byRC[`${p._r}-${p._s}`] = p; });
  const colW = Math.max(96, Math.min(132, Math.floor(820 / (cols + 1))));
  const mv = d.movement || {};
  const trades = d.pickTrades || [];
  return (
    <div>
      <div className="dr-bhead">
        <div className="dr-gpill" style={{ background: ungraded ? 'var(--i3)' : gradeColor(d.grade) }}>{ungraded ? '—' : (d.grade || '—')}</div>
        <div>
          <div className="dr-bhead-title">{d.season} · {d.leagueName || d.league || 'draft'}</div>
          <div className="dr-bhead-sub">{d.draftType === 'startup' ? 'Startup draft' : 'Rookie draft'}{ungraded ? ' · not graded yet' : ''}</div>
        </div>
        <div className="dr-spacer" />
        {mv.net > 0 ? <span className="dr-pill dr-pill-up">▲ traded in +{mv.net}</span>
          : mv.net < 0 ? <span className="dr-pill dr-pill-down">▼ traded out {Math.abs(mv.net)}</span>
            : <span className="dr-pill dr-pill-flat">stood pat</span>}
        {ungraded
          ? <span className="dr-pill dr-pill-flat num">{d.pickCount ?? picks.length} {(d.pickCount ?? picks.length) === 1 ? 'pick' : 'picks'}</span>
          : <span className="dr-pill dr-pill-hit num">{(d.hitCount ?? d.hits ?? 0)} of {d.pickCount ?? picks.length} hit</span>}
      </div>

      {trades.length > 0 && (
        <>
          <div className="dr-clock-lbl">On the clock</div>
          <div className="dr-clock-scroll">
            {trades.map((tr, i) => (
              <div className="dr-trade" key={i}>
                <div className="dr-trade-body">
                  <div className="dr-trade-side">
                    <div className="dr-trade-side-lbl">Got</div>
                    {(tr.got || []).map((it, j) => <ClockAsset it={it} key={j} />)}
                  </div>
                  <div className="dr-swap">⇄</div>
                  <div className="dr-trade-side">
                    <div className="dr-trade-side-lbl">Gave</div>
                    {(tr.gave || []).map((it, j) => <ClockAsset it={it} key={j} />)}
                  </div>
                </div>
                <div className="dr-trade-foot">
                  <span className="dr-trade-date">{fmtDateLong(tr.tradedAt)}</span>
                  {tr.ktcUrl && <a className="dr-ktc" href={tr.ktcUrl} target="_blank" rel="noopener noreferrer">KTC ↗</a>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="dr-view-toggle">
        <button type="button" className={`dr-view-btn${view === 'board' ? ' on' : ''}`} onClick={() => setView('board')}>Board</button>
        <button type="button" className={`dr-view-btn${view === 'list' ? ' on' : ''}`} onClick={() => setView('list')}>List</button>
      </div>

      {view === 'list' ? <DraftList d={d} /> : (
        <>
          <div className="dr-board-scroll">
            <div className="dr-grid" style={{ gridTemplateColumns: `34px repeat(${cols}, minmax(${colW}px, 1fr))`, minWidth: 34 + cols * colW + cols * 6 }}>
              {Array.from({ length: maxRound }, (_, ri) => ri + 1).map((r) => (
                <React.Fragment key={r}>
                  <div className="dr-rowlbl num">R{r}</div>
                  {Array.from({ length: cols }, (_, ci) => ci + 1).map((c) => {
                    const p = byRC[`${r}-${c}`];
                    const slot = `${r}.${c < 10 ? `0${c}` : c}`;
                    if (!p) return <div className="dr-cell dr-cell-empty" key={c}><span className="dr-cell-slot num">{slot}</span></div>;
                    if (!p._mine) {
                      return (
                        <div className="dr-cell dr-cell-other" key={c}>
                          <div className="dr-cell-top">
                            <PlayerAvatar id={p.playerId} name={p.playerName} size={24} />
                            <span className="dr-cell-slot num">{p._slot}</span>
                          </div>
                          <div className="dr-cell-name">{p.playerName || 'Unknown'}</div>
                          {p.position && <div className="dr-cell-pos">{p.position}</div>}
                        </div>
                      );
                    }
                    if (ungraded) {
                      return (
                        <div className="dr-cell dr-cell-mine dr-cell-ungraded" key={c}>
                          <div className="dr-cell-top">
                            <PlayerAvatar id={p.playerId} name={p.playerName} size={24} />
                            <span className="dr-cell-slot num">{p._slot}</span>
                          </div>
                          <div className="dr-cell-name">{p.playerName || 'Unknown'}</div>
                          {p.position && <div className="dr-cell-pos">{p.position}</div>}
                        </div>
                      );
                    }
                    const verdict = p._verdict || p.verdict || 'fair';
                    const passedOn = p._passedOn || p.passedOn;
                    return (
                      <div className={`dr-cell dr-cell-mine dr-v-${verdict}`} key={c}>
                        <div className="dr-cell-top">
                          <PlayerAvatar id={p.playerId} name={p.playerName} size={24} />
                          <span className="dr-cell-slot num">{p._slot}</span>
                        </div>
                        <div className="dr-cell-name">{p.playerName || 'Unknown'}</div>
                        <div className="dr-cell-verdict">{verdict}</div>
                        {passedOn && passedOn.playerName && <div className="dr-cell-ghost">passed {passedOn.playerName}</div>}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="dr-legend">
            {[['hit / steal', 'var(--gsoft)'], ['fair', 'var(--surf2)'], ['reach', 'var(--bs)'], ['bust', 'rgba(255,59,48,.10)'], ['other teams', '#fff']].map(([lbl, col]) => (
              <div className="dr-leg" key={lbl}><span className="dr-leg-sw" style={{ background: col, boxShadow: lbl === 'other teams' ? 'inset 0 0 0 1px var(--hair)' : 'none' }} />{lbl}</div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const DraftsPanel = ({ pt }) => {
  // Career band (#5) — manager-wide, shown above the board.
  const roi = pt?.roi || {};
  const dna = pt?.dna || {};
  const imp = pt?.impatience || {};
  const drafts = useMemo(() => {
    if (!pt) return [];
    const all = [...(pt.currentDrafts || []), ...(pt.regrades || []), ...(pt.startupGrades || [])];
    // Organize the selector: graded rookie drafts first (the ones with a real
    // grade), then startups, then the ungraded current class — newest first
    // within each group. Keeps the grade-bearing boards up top.
    const rank = (d) => {
      const ungraded = d.graded === false || d.grade == null;
      if (ungraded) return 2;
      if (d.draftType === 'startup') return 1;
      return 0;
    };
    return all.sort((a, b) => rank(a) - rank(b) || (Number(b.season) || 0) - (Number(a.season) || 0));
  }, [pt]);
  const defIdx = useMemo(() => {
    // Prefer the richest graded rookie board as the default view (ungraded
    // current-class boards carry no verdicts, so they're a weaker default).
    let best = -1, idx = 0;
    drafts.forEach((d, i) => {
      if (d.draftType !== 'rookie' || d.graded === false || d.grade == null) return;
      const score = (d.pickCount || 0) + ((d.pickTrades || []).length || 0);
      if (score > best) { best = score; idx = i; }
    });
    // Nothing graded yet — fall back to the first (newest current) board.
    return best >= 0 ? idx : 0;
  }, [drafts]);
  const [sel, setSel] = useState(0);
  useEffect(() => { setSel(defIdx); }, [defIdx]);
  // Killer-miss callout from the steepest passedOn delta across all picks.
  // Computed before any early return so hook order stays stable.
  const killer = useMemo(() => {
    let worst = null, gap = 0;
    drafts.forEach((d) => (d.picks || []).forEach((p) => {
      if (p.passedOn && p.passedOn.currentValue != null && p.currentValue != null) {
        const g = p.passedOn.currentValue - p.currentValue;
        if (g > gap) { gap = g; worst = { took: p, pass: p.passedOn, season: d.season, league: d.leagueName || d.league || 'a draft', draftType: d.draftType }; }
      }
    }));
    return worst;
  }, [drafts]);
  // Fresh mount each time the Drafts tab is (re)activated — the fade replays.
  const shown = useReveal(true);

  if (!pt || !pt.available) {
    return (
      <div className={`panel ft-fade ${shown ? 'ft-play' : ''}`}>
        <div className="kicker" style={{ marginTop: 8 }}><span className="seq">D</span> · The paper trail <span className="ln" /></div>
        <div className="empty">Lands with draft backfill — every matured draft re-graded at today's values.</div>
      </div>
    );
  }
  const sel0 = drafts[sel] || drafts[0];

  return (
    <div className={`panel ft-fade ${shown ? 'ft-play' : ''}`}>
      <section className="dr-sig">
        <div className="dr-sig-kick">The paper trail — how he drafts</div>
        <div className="dr-sig-row">
          <div className="dr-sig-grade" style={{ color: pt.drafterGrade ? gradeColor(pt.drafterGrade) : 'var(--i3)' }}>{pt.drafterGrade || '—'}</div>
          <div className="dr-sig-tx">
            <div className="dr-sig-title">
              {pt.drafterGrade
                ? (DRAFT_READ[pt.drafterGrade.charAt(0).toUpperCase()] || 'Drafting read')
                : (roi.picksGraded > 0 ? 'Not enough drafts to grade yet' : 'Drafting read')}
            </div>
            <div className="dr-sig-note">
              {pt.drafterGrade ? (
                <>
                  {roi.hitRate != null ? `${Math.round(roi.hitRate * 100)}% hit rate` : ''}
                  {` · ${nf(roi.hitCount)} of ${nf(roi.picksGraded)} picks hit`}
                  {pt.gradedDraftCount ? ` across ${pt.gradedDraftCount} rookie ${pt.gradedDraftCount === 1 ? 'draft' : 'drafts'}` : ''}
                  {pt.gradedThrough ? ` through ${pt.gradedThrough}.` : '.'}
                </>
              ) : roi.picksGraded > 0 ? (
                // Thin sample: has some graded picks but under the grading gate —
                // explain the bar rather than show a bare dash next to a hit rate.
                pt.gradeGate ? `${pt.gradeGate}.` : `Only ${nf(roi.picksGraded)} graded ${roi.picksGraded === 1 ? 'pick' : 'picks'} so far — a bigger sample is needed to grade.`
              ) : (
                `No rookie drafts to grade yet${pt.gradedThrough ? ` through ${pt.gradedThrough}` : ''}.`
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CAREER BAND — across all drafts (grade relocated to the signature above) */}
      <section className="dr-career">
        <div className="dr-kicker">Across all drafts</div>
        <div className="dr-career-grid">
          <div className="dr-card">
            <div className="dr-card-lbl">Draft DNA</div>
            <div className="dr-dna-big">{dna.reachRate != null ? `${signed(dna.reachRate)} reach rate` : '—'}</div>
            <div className="dr-dna-note">{dna.reachRate == null ? (dna.reachNote || 'Insufficient sample.') : `${dna.reachRate > 0 ? 'Drafts slightly ahead of consensus' : dna.reachRate < 0 ? 'Drafts behind consensus' : 'Drafts roughly at consensus'} (${nf(dna.reachSample)}-pick sample).`}</div>
          </div>
          <div className="dr-card">
            <div className="dr-card-lbl">Impatience</div>
            <div className="dr-imp-big">{imp.draftedTotal != null ? `Sold ${nf(imp.draftedThenSoldInsideYear)} of ${nf(imp.draftedTotal)} drafted rookies within a year.` : 'No impatience signal yet.'}</div>
            {(imp.receipts || []).length > 0 && (
              <div className="dr-receipts">
                {imp.receipts.map((r, i) => (
                  <div className="dr-receipt" key={i}>
                    <PlayerAvatar id={r.playerId} name={r.playerName} size={30} />
                    <span className="dr-r-name">{r.playerName || 'Unknown'}</span>
                    <span className="dr-r-meta num">{r.draftedAt}</span>
                    <span className="dr-r-tag">sold</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        {killer && (
          <div className="dr-kill">
            <div className="dr-kill-lbl">The one that got away</div>
            <div className="dr-kill-where">{killer.season} · {killer.league} · {killer.draftType === 'startup' ? 'startup' : 'rookie'} draft</div>
            <div className="dr-kill-line">
              <span className="dr-kill-took">Took {killer.took.playerName} {slotLabel(killer.took)} (now {nf(killer.took.currentValue)})</span>
              {' · '}
              <span className="dr-kill-pass">Passed {killer.pass.playerName} (now {nf(killer.pass.currentValue)})</span>
            </div>
          </div>
        )}
      </section>

      {/* SELECTOR — grouped: graded rookie drafts (the meat) / startups / the
          ungraded current class. The group header carries the type, so chips
          drop their per-pill Startup/Ungraded tags. */}
      {drafts.length > 0 && (() => {
        const renderChip = (d, i) => {
          const ungraded = d.graded === false || d.grade == null;
          const hits = d.hitCount ?? d.hits ?? 0;
          const pc = d.pickCount ?? (d.picks || []).length;
          return (
            <button type="button" className={`dr-chip${i === sel ? ' on' : ''}`} key={i} onClick={() => setSel(i)}>
              <span className="dr-chip-g" style={{ color: ungraded ? 'var(--i3)' : gradeColor(d.grade), background: ungraded ? 'var(--surf2)' : `${gradeColor(d.grade)}22` }}>{ungraded ? '—' : (d.grade || '—')}</span>
              <span className="dr-chip-mid">
                <span className="dr-chip-lg">{d.leagueName || d.league || 'Draft'}</span>
                <span className="dr-chip-sub num">{d.season} · {ungraded ? `${pc} pick${pc === 1 ? '' : 's'}` : `${hits} hit${hits === 1 ? '' : 's'}`}</span>
              </span>
            </button>
          );
        };
        const groups = [['Rookie drafts', []], ['Startup drafts', []], ['This year · not graded yet', []]];
        drafts.forEach((d, i) => {
          const ungraded = d.graded === false || d.grade == null;
          const gi = ungraded ? 2 : d.draftType === 'startup' ? 1 : 0;
          groups[gi][1].push(renderChip(d, i));
        });
        return (
          <>
            <h3 className="dr-sec-hd">Select a draft</h3>
            {groups.filter(([, arr]) => arr.length).map(([label, arr]) => (
              <div className="dr-group" key={label}>
                <div className="dr-group-h">{label}</div>
                <div className="dr-chips">{arr}</div>
              </div>
            ))}
          </>
        );
      })()}

      {/* BOARD */}
      {sel0 && <DraftBoard d={sel0} />}

      {/* CURRENT CLASS — now surfaced as ungraded boards in the selector above;
          this is just a one-line note so the absence of grades is honest. */}
      {(pt.currentDrafts || []).length > 0 && (
        <div className="dr-cc-note num">
          {pt.currentClass?.season || pt.currentDrafts[0].season} class · not graded yet — the class hasn&apos;t played a down. Select it above to see the board.
        </div>
      )}

      <div className="dr-honesty">Collected, not modeled. Valued at today's FantasyCalc.</div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// NETWORK TAB
// ---------------------------------------------------------------------------

// The relationship graph — the Network tab's dark signature (the bodygraph's
// cousin). Additive: it sits ABOVE the existing partner list + donut + read,
// which are all preserved. The graph caps at the top 12 by volume for layout;
// the full list below is never truncated.
const NETNAME = (p) => p.managerName || p.sleeperUsername || p.displayName || p.teamName || 'Unknown';

const NetworkGraph = ({ network }) => {
  const partners = network.partners || [];
  if (partners.length === 0) return null;
  const shown = partners.slice(0, 12);
  const capped = partners.length - shown.length;
  const W = 600, H = 430, cx = 300, cy = 210, R = 158, n = shown.length;
  const ang = (i) => -Math.PI / 2 + i * (2 * Math.PI / n);
  const top = network.topPartner || partners[0];
  const topKey = top ? (top.sleeperUserId || NETNAME(top)) : null;
  const isTop = (p) => topKey && (p.sleeperUserId || NETNAME(p)) === topKey;
  const cp = network.counterparties || partners.length;
  const attributed = network.attributedTrades || partners.reduce((s, p) => s + (p.trades || 0), 0);
  const topShare = top && top.share != null ? top.share : (top && attributed ? top.trades / attributed : null);
  const read = topShare != null && topShare < 0.25 ? 'He spreads it wide.' : 'One table he keeps coming back to.';
  const multiCount = partners.filter((p) => (p.leagues || 1) >= 2).length;
  const short = (s) => (s.length > 13 ? `${s.slice(0, 12)}…` : s);
  return (
    <section className="net-sig">
      <div className="net-sig-kick">The network — who he deals with</div>
      <div className="net-sig-title"><span className="num">{cp}</span> trade partners. {read}</div>
      <div className="net-graphwrap">
        <svg viewBox={`0 0 ${W} ${H}`} aria-label="Trade network graph">
          {shown.map((p, i) => {
            const a = ang(i), x = cx + R * Math.cos(a), y = cy + R * Math.sin(a), t = isTop(p);
            return <line key={`e${i}`} x1={cx} y1={cy} x2={x} y2={y} stroke={t ? '#FF7A00' : 'rgba(255,255,255,.16)'} strokeWidth={t ? 3 : 1 + (p.trades || 1) * 0.35} strokeLinecap="round" />;
          })}
          {shown.map((p, i) => {
            const a = ang(i), x = cx + R * Math.cos(a), y = cy + R * Math.sin(a), t = isTop(p);
            const r = 9 + Math.sqrt(p.trades || 1) * 4;
            const multi = (p.leagues || 1) >= 2; // shares more than one league with him
            return (
              <g key={`n${i}`}>
                {t && <circle cx={x} cy={y} r={r + 5} fill="none" stroke="rgba(255,122,0,.35)" strokeWidth="2" />}
                {multi && !t && <circle cx={x} cy={y} r={r + 4} fill="none" stroke="#FF7A00" strokeWidth="2" strokeDasharray="3 3" />}
                <circle cx={x} cy={y} r={r} fill={t ? '#FF7A00' : 'rgba(255,255,255,.14)'} stroke={t ? 'none' : 'rgba(255,255,255,.3)'} strokeWidth="1.5" />
                <text x={x} y={y + 4} textAnchor="middle" style={{ fontSize: t ? 15 : 12, fontWeight: 700, fill: t ? '#fff' : 'rgba(255,255,255,.85)' }}>{p.trades}</text>
                <text x={x} y={y + r + 14} textAnchor="middle" style={{ fontSize: 10, fontWeight: t ? 700 : 600, fill: t ? '#FFB36B' : 'rgba(255,255,255,.6)' }}>{short(NETNAME(p))}</text>
              </g>
            );
          })}
          <circle cx={cx} cy={cy} r="30" fill="#fff" />
          <text x={cx} y={cy - 1} textAnchor="middle" style={{ fontSize: 14, fontWeight: 700, fill: '#1D1D1F' }}>FT</text>
          <text x={cx} y={cy + 12} textAnchor="middle" style={{ fontSize: 9, fontWeight: 600, fill: '#86868B' }}>you</text>
        </svg>
      </div>
      <div className="net-legend">
        <span className="net-leg"><span className="net-leg-dot" />node size = trades</span>
        <span className="net-leg"><span className="net-leg-ring" />shares 2+ leagues</span>
      </div>
      <div className="net-list">
        {shown.slice(0, 8).map((p, i) => {
          const lg = p.leagues || 1;
          return (
            <div className="net-row" key={i}>
              <span className="net-row-n">{NETNAME(p)}</span>
              <span className="net-row-t"><b className="num">{p.trades}</b> {p.trades === 1 ? 'trade' : 'trades'}</span>
              <span className={`net-row-lg${lg >= 2 ? ' multi' : ''}`}>{lg} {lg === 1 ? 'league' : 'leagues'}</span>
            </div>
          );
        })}
      </div>
      <div className="net-insight">{multiCount > 0
        ? <><b>{multiCount}</b> {multiCount === 1 ? 'partner shares' : 'partners share'} more than one of his leagues; the rest are one-league relationships.</>
        : <>Every partner is a one-league relationship — his volume comes from working many tables, not the same few rivals.</>}</div>
      {capped > 0 && <div className="net-sig-note">Graph shows the top {shown.length} by volume — all {partners.length} partners counted.</div>}
    </section>
  );
};

const NetworkPanel = ({ network }) => {
  if (!network || !network.partners || network.partners.length === 0) {
    return (
      <div className="panel">
        <div className="kicker" style={{ marginTop: 8 }}><span className="seq">N</span> · The network <span className="ln" /></div>
        <div className="empty">No trade partners surfaced yet.</div>
      </div>
    );
  }
  const partners = network.partners;
  const max = Math.max(...partners.map((p) => p.trades), 1);
  const attributed = network.attributedTrades || partners.reduce((s, p) => s + (p.trades || 0), 0);
  const top = network.topPartner;
  const topShare = top && top.share != null ? top.share : (top && attributed ? top.trades / attributed : null);
  // The PERSON's real Sleeper account name (FEATURE #27). Fall back to the
  // per-league team name only when the account lookup didn't resolve.
  const name = (p) => p.managerName || p.sleeperUsername || p.displayName || p.teamName || 'Unknown manager';
  // Secondary line: most-recent team name (when it differs from the person) +
  // how many of the viewer's leagues they share with this person.
  const subtitle = (p) => {
    const team = p.teamName || p.displayName;
    const showTeam = team && team !== name(p);
    const lg = p.leagues ? `in ${p.leagues} ${p.leagues === 1 ? 'league' : 'leagues'}` : null;
    return [showTeam ? team.trim() : null, lg].filter(Boolean).join(' · ');
  };
  return (
    <div className="panel">
      <NetworkGraph network={network} />
      <div className="net">
        <div className="partners">
          <h4>Top trade partners <Info><b>{network.counterparties} counterparties</b> total. Bar = share of their {attributed} attributed deals. Named by the person's Sleeper account, not their team.</Info></h4>
          <div>
            {partners.map((p, i) => (
              <div className={`prow${i === 0 ? ' top' : ''}`} key={p.sleeperUserId || i}>
                <span className="prk num">{i + 1}</span>
                <div className="pmid2">
                  <div className="pnm">{name(p)}</div>
                  {subtitle(p) && <div className="psub">{subtitle(p)}</div>}
                  <div className="ptrack"><i style={{ width: `${(p.trades / max) * 100}%` }} /></div>
                </div>
                <span className="pct num">{p.trades}{i === 0 && topShare != null ? ` · ${Math.round(topShare * 100)}%` : ''}</span>
              </div>
            ))}
          </div>
          <div className="snote">Counted per manager (Sleeper ID); rolled-over league seasons collapse to one.</div>
        </div>
        {topShare != null && (
          <div className="donutcard">
            <div className="dk">Concentration</div>
            <NetworkDonut pct={topShare} />
            <div className="dcap"><b>{network.counterparties} counterparties.</b> Top partner is {Math.round(topShare * 100)}% of deals{topShare < 0.25 ? ' — they spread it around.' : ' — watch the pair.'}</div>
          </div>
        )}
      </div>
      {top && (
        <div className="honesty">
          <b>Read.</b> {topShare != null && topShare < 0.25 ? 'A low concentration says no single rival has a lock on them.' : 'One partner takes a meaningful share of their deals.'} {name(top)} is the closest thing to a regular — {top.trades} deals across {top.leagues} {top.leagues === 1 ? 'league' : 'leagues'}.
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// HISTORY TAB
// ---------------------------------------------------------------------------

// Clean column chart: every month from Jan '25 → current month gets an equal
// slot, bars rise from one common floor, height ∝ that month's trade count.
// In-season bars are filled (--ft-ink); offseason bars are outlined/orange.
// A dashed horizontal line marks the average ("avg N/mo") across the columns.
const MonthChart = ({ months }) => {
  const [tip, setTip] = useState(null);
  const keys = Object.keys(months);
  if (keys.length === 0) return null;
  const maxVal = Math.max(...keys.map((k) => months[k]), 1);
  const VBW = 760, VBH = 190, padL = 8, padR = 8, padT = 10, padB = 26;
  const plotW = VBW - padL - padR, plotH = VBH - padT - padB;
  const floorY = padT + plotH;
  const n = keys.length, slot = plotW / n;
  const barW = Math.max(3, Math.min(26, slot * 0.66));
  const yFor = (v) => floorY - (maxVal > 0 ? (v / maxVal) * plotH : 0);
  const axisKeys = [keys[0], keys[Math.floor(n / 2)], keys[n - 1]];
  return (
    <div className="th-chart">
      {tip && <div className="th-tip" style={{ left: `${(tip.cx / VBW) * 100}%`, top: tip.top }}>{tip.label}</div>}
      <svg viewBox={`0 0 ${VBW} ${VBH}`} preserveAspectRatio="none" role="img" aria-label="Monthly trade counts">
        <line x1={padL} x2={VBW - padR} y1={floorY} y2={floorY} stroke="var(--hair)" strokeWidth="1" />
        {keys.map((k, i) => {
          const [yr, mm] = k.split('-');
          const mIdx = parseInt(mm, 10) - 1;
          const v = months[k];
          const cx = padL + slot * i + slot / 2;
          const x = cx - barW / 2;
          const inSeason = isInSeason(mIdx);
          const h = (v / maxVal) * plotH;
          const label = `${MON[mIdx]} '${yr.slice(2)} · ${v} ${v === 1 ? 'trade' : 'trades'}`;
          return (
            <g key={k}>
              {v > 0 && <rect className={`th-bar ${inSeason ? 'th-bar--in' : 'th-bar--off'}`} x={x} y={floorY - h} width={barW} height={h} rx="2.5" />}
              <rect x={padL + slot * i} y={padT} width={slot} height={plotH} fill="transparent" style={{ cursor: 'default' }}
                onMouseEnter={() => setTip({ cx, top: yFor(v) - 12, label })}
                onMouseMove={() => setTip({ cx, top: yFor(v) - 12, label })}
                onMouseLeave={() => setTip(null)} />
              {axisKeys.includes(k) && <text x={cx} y={VBH - 8} textAnchor="middle" fill="#86868B" fontSize="12">{MON[mIdx]} '{yr.slice(2)}</text>}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

const HistoryAsset = ({ a }) => {
  if (a.pick) {
    const sub = a.pickNo
      ? (a.player ? `${a.pickNo} · ${a.player}` : a.pickNo)
      : (a.player || null);
    return <span className="th-pick">{a.pick}{sub ? ` (${sub})` : ''}</span>;
  }
  return (
    <div className="th-asset">
      <PlayerAvatar id={a.sid} name={a.name} size={30} />
      <div>
        <div className="th-asset-name">{a.name}</div>
        <div className="th-asset-pos">{(a.pos || '')}{a.team ? ` · ${a.team}` : ''}</div>
      </div>
    </div>
  );
};

const HistoryPanel = ({ trades, playerData, loading, marketAcumen, refreshedAt, activityStats }) => {
  const [filter, setFilter] = useState('__all');

  // Normalize getTrades rows into { date, league, season, sf, got[], gave[], ktcUrl }.
  const rows = useMemo(() => {
    return (trades || []).map((t) => {
      const got = [], gave = [], gotKtc = [], gaveKtc = [];
      Object.entries(t.adds || {}).forEach(([pid, rid]) => {
        if (rid === t.userRosterId) { const p = playerData[pid] || {}; got.push({ name: p.name || pid, pos: p.position, team: p.team, sid: pid }); if (p.ktcId) gotKtc.push(p.ktcId); }
      });
      Object.entries(t.drops || {}).forEach(([pid, rid]) => {
        if (rid === t.userRosterId) { const p = playerData[pid] || {}; gave.push({ name: p.name || pid, pos: p.position, team: p.team, sid: pid }); if (p.ktcId) gaveKtc.push(p.ktcId); }
      });
      (t.draftPicksTraded || []).forEach((pk) => {
        const label = `${pk.season} ${pk.round}${pk.round === 1 ? 'st' : pk.round === 2 ? 'nd' : pk.round === 3 ? 'rd' : 'th'}`;
        const pkId = getKTCPickIdFromPick(pk); // so the KTC link includes the picks, not just players
        const pickObj = {
          pick: label,
          pickNo: pk.resolvedPickNo || null,      // e.g. "1.04"
          player: pk.resolvedPlayer || null,       // e.g. "Jaxson Dart"
          playerId: pk.resolvedPlayerId || null,
        };
        if (pk.toRosterId === t.userRosterId) { got.push(pickObj); if (pkId) gotKtc.push(pkId); }
        else if (pk.fromRosterId === t.userRosterId) { gave.push(pickObj); if (pkId) gaveKtc.push(pkId); }
      });
      const sf = !!t.league?.settings?.isSuperFlex;
      const tep = !!t.league?.settings?.tePremium;
      // teamOne = GOT side, teamTwo = GAVE side (KTC's calculator is symmetric).
      const ktcUrl = (gotKtc.length || gaveKtc.length) ? buildKtcLink(gotKtc, gaveKtc, { sf, tep }) : null;
      return {
        date: t.tradedAt,
        league: t.league?.name || 'League',
        season: t.league?.season,
        sf,
        got, gave, ktcUrl,
      };
    }).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [trades, playerData]);

  const months = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      const d = new Date(r.date);
      if (Number.isNaN(d.getTime())) return;
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
      m[key] = (m[key] || 0) + 1;
    });
    const keys = Object.keys(m).sort();
    if (keys.length === 0) return m;
    // Continuous, evenly-spaced axis: Jan '25 (or earliest trade, whichever is
    // earlier) through the current month. Zero months render as empty slots so
    // the time axis never jumps.
    const [fy, fm] = keys[0].split('-').map(Number);
    let sy = 2025, sm = 1;
    if (fy < sy || (fy === sy && fm < sm)) { sy = fy; sm = fm; }
    const now = new Date();
    const ey = now.getUTCFullYear(), em = now.getUTCMonth() + 1;
    const filled = {};
    let y = sy, mo = sm;
    while (y < ey || (y === ey && mo <= em)) {
      const key = `${y}-${String(mo).padStart(2, '0')}`;
      filled[key] = m[key] || 0;
      mo += 1; if (mo > 12) { mo = 1; y += 1; }
    }
    return filled;
  }, [rows]);


  const leagues = useMemo(() => Array.from(new Set(rows.map((r) => r.league))).sort(), [rows]);
  const filtered = filter === '__all' ? rows : rows.filter((r) => r.league === filter);
  const first = rows[rows.length - 1]?.date, last = rows[0]?.date;
  // End of the tracked window = when Manager Intel last refreshed (the data's
  // as-of), not the last trade — falls back to the last trade if unknown.
  const end = (refreshedAt && !Number.isNaN(new Date(refreshedAt).getTime())) ? refreshedAt : last;

  // At-the-date verdict layer — match the server-graded recent trades onto rows
  // by league + calendar date. Rows without a grade simply show no pill (full
  // per-trade grading is a backend fast-follow). Nothing existing is removed.
  const ma = marketAcumen || {};
  const gradeByKey = useMemo(() => {
    const m = {};
    (ma.ledger || ma.recent || []).forEach((t) => { if (t.date && t.league) m[`${t.league}|${t.date}`] = t; });
    return m;
  }, [ma]);
  const verdictFor = (t) => {
    const d = new Date(t.date);
    if (Number.isNaN(d.getTime())) return null;
    return gradeByKey[`${t.league}|${d.toISOString().slice(0, 10)}`] || null;
  };
  const avg = ma.avgDeltaPct;
  const read = avg == null ? '' : Math.abs(avg) < 3 ? 'Roughly a wash.' : avg > 0 ? 'He comes out ahead.' : 'He comes out behind.';
  // Fresh mount each time the Trades tab is (re)activated — the fade replays;
  // the row stagger plays once rows are actually ready to show.
  const shownPanel = useReveal(true);
  const shownRows = useReveal(rows.length > 0);

  return (
    <div className={`panel ft-fade ${shownPanel ? 'ft-play' : ''}`}>
      <div className="th-sig">
        <div className="th-sig-kick">The ledger — every trade, graded when it happened</div>
        <div className="th-sig-title"><span className="num">{rows.length}</span> {rows.length === 1 ? 'trade' : 'trades'}{read ? `. ${read}` : ''}</div>
        {ma.tradesScored != null && <div className="th-sig-sub">Each deal scored at KTC's value on the date it happened — not today's hindsight.</div>}
        {first && <div className="th-sig-meta">Tracked {fmtDateLong(first)} → {fmtDateLong(end)} <span style={{ color: 'var(--i3)' }}>· as of last refresh</span></div>}
      </div>

      {loading && rows.length === 0 ? (
        <div className="empty">Loading trade history…</div>
      ) : rows.length === 0 ? (
        <div className="th-empty">No trades in synced leagues.</div>
      ) : (
        <>
          <div className="th-card">
            <div className="th-card-top">
              <span className="th-card-title">Trades by month</span>
              <div className="th-legend">
                <span className="th-legend-item"><span className="th-swatch th-swatch--in" />In-season</span>
                <span className="th-legend-item"><span className="th-swatch th-swatch--off" />Offseason</span>
              </div>
            </div>
            <MonthChart months={months} />
            {(activityStats?.tradesPerSeason != null || activityStats?.median != null) && (
              <div className="th-rate-note">
                {activityStats.tradesPerSeason != null && <span><b className="num">{activityStats.tradesPerSeason}</b> trades/league-season</span>}
                {activityStats.tradesPerSeason != null && activityStats.median != null && <span className="th-rate-sep">·</span>}
                {activityStats.median != null && <span>platform median: <b className="num">{activityStats.median}</b></span>}
              </div>
            )}
          </div>

          <div className="th-filters">
            <button type="button" className={`th-chip${filter === '__all' ? ' on' : ''}`} onClick={() => setFilter('__all')}>All<span className="th-chip-ct">{rows.length}</span></button>
            {leagues.map((lg) => (
              <button type="button" className={`th-chip${filter === lg ? ' on' : ''}`} key={lg} onClick={() => setFilter(lg)}>{lg}<span className="th-chip-ct">{rows.filter((r) => r.league === lg).length}</span></button>
            ))}
          </div>

          <div className={`th-list ft-stagger ${shownRows ? 'ft-play' : ''}`}>
            {filtered.length === 0 ? <div className="th-empty">No trades in this league.</div> : filtered.map((t, i) => {
              const v = verdictFor(t);
              const vp = v == null ? null : v.deltaPct >= 5 ? { cls: 'won', lab: 'Won' } : v.deltaPct <= -5 ? { cls: 'lost', lab: 'Lost' } : { cls: 'fair', lab: 'Fair' };
              return (
              <div className="th-trade" key={i}>
                <div className="th-trade-meta">
                  <span className="th-date">{fmtDateLong(t.date)}</span>
                  <span className="th-dot" />
                  <span className="th-league">{t.league}</span>
                  {t.sf && <span className="th-sf">SF</span>}
                  {vp && <span className={`th-vpill ${vp.cls}`} title={`Graded at KTC value on ${fmtDateLong(t.date)} — ${signed(Math.round(v.deltaPct * 10) / 10, '%')} vs a fair, even-value swap.`}>{vp.lab} {signed(Math.round(v.deltaPct * 10) / 10, '%')}</span>}
                  {v && v.lowConfidence && <span className="th-approx" title={`Couldn't price ${v.unpricedPicks} traded ${v.unpricedPicks === 1 ? 'pick' : 'picks'} at the time (rookie picks before that class existed on KTC) — verdict is approximate.`} style={vp ? { marginLeft: 8 } : { marginLeft: 'auto' }}>≈ est</span>}
                  {t.ktcUrl && <a className="th-ktc" href={t.ktcUrl} target="_blank" rel="noopener noreferrer" style={(vp || (v && v.lowConfidence)) ? { marginLeft: 10 } : undefined}>KTC ↗</a>}
                </div>
                <div className="th-flow">
                  <div>
                    <div className="th-side-lbl th-side-lbl--got">Got</div>
                    <div className="th-assets">{t.got.length ? t.got.map((a, j) => <HistoryAsset a={a} key={j} />) : <span className="th-asset-pos">Nothing</span>}</div>
                  </div>
                  <div className="th-arrow">⇄</div>
                  <div>
                    <div className="th-side-lbl th-side-lbl--gave">Gave</div>
                    <div className="th-assets">{t.gave.length ? t.gave.map((a, j) => <HistoryAsset a={a} key={j} />) : <span className="th-asset-pos">Nothing</span>}</div>
                  </div>
                </div>
                {v && (v.agingDelta != null || v.gaveAgingDelta != null) && Math.max(Math.abs(v.agingDelta || 0), Math.abs(v.gaveAgingDelta || 0)) >= 25 && (() => {
                  const aged = (n) => <b className={n > 0 ? 'up' : 'dn'}>{n > 0 ? '+' : '−'}{Math.abs(n).toLocaleString()} KTC</b>;
                  const net = (v.agingDelta || 0) - (v.gaveAgingDelta || 0);
                  return (
                    <div className="th-aged">
                      Since then: he got {v.agingDelta != null ? aged(v.agingDelta) : '—'}, gave up {v.gaveAgingDelta != null ? aged(v.gaveAgingDelta) : '—'}
                      {v.agingDelta != null && v.gaveAgingDelta != null && Math.abs(net) >= 25 && <> — aged <b className={net > 0 ? 'up' : 'dn'}>{net > 0 ? 'in his favor' : 'against him'}</b>.</>}
                    </div>
                  );
                })()}
              </div>
            ); })}
          </div>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// PORTFOLIO TAB — reuses the existing DynastyYoYSection.
// ---------------------------------------------------------------------------

const PortfolioPanel = ({ dynastyYoY, leagues = [], mergedLeagues = [], status, sleeperUsername, onUpdate, loading, dossier }) => {
  const sLeagues = dossier?.situation?.leagues || [];
  const dynCount = dossier?.identity?.dynastyLeagueCount ?? sLeagues.length;
  const allSF = sLeagues.length > 0 && sLeagues.every((l) => l.isSuperFlex);
  const exit = dossier?.timing?.exitRisk || {};
  const title = dynCount
    ? `${dynCount} active dynasty ${dynCount === 1 ? 'league' : 'leagues'}${allSF ? '. All superflex' : ''}${exit.basis !== 'no_yoy_data' && exit.leaguesLeft === 0 ? '. None abandoned' : ''}.`
    : 'His league footprint.';
  return (
    <div className="panel">
      <section className="pf-sig">
        <div className="pf-sig-kick">The empire — every league he plays</div>
        <div className="pf-sig-title">{title}</div>
        <div className="pf-sig-sub">
          Every league across all formats — broader than the dynasty leagues the rest of this scouting report analyzes.
          {exit.level && exit.basis !== 'no_yoy_data' ? ` ${exit.level.charAt(0).toUpperCase() + exit.level.slice(1)} exit risk — ${exit.leaguesLeft ?? 0} of ${exit.totalDynastyLeagues ?? dynCount} dynasty leagues left.` : ''}
        </div>
      </section>
      {loading && (!dynastyYoY || dynastyYoY.length === 0) ? (
        <div className="empty">Loading portfolio…</div>
      ) : (
        <DynastyYoYSection
          dynastyYoY={dynastyYoY}
          leagues={leagues}
          mergedLeagues={mergedLeagues}
          selectedLeagueId="all"
          sleeperUsername={sleeperUsername}
          dynastyYoYStatus={status}
          onDynastyYoYUpdate={onUpdate}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tier teaser (visitor / free)
// ---------------------------------------------------------------------------

const Teaser = ({ tier, archetype }) => (
  <div className="ftd">
    <style>{CSS}</style>
    <div className="card dark" style={{ padding: 30, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="vkick" style={{ marginBottom: 8 }}>Scout Mode</div>
        <div style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-.02em' }}>{tier === 'free' && archetype && !archetype.insufficientSample ? archetype.name : 'Scouting reads'}</div>
        <div style={{ fontSize: 14, color: 'rgba(255,255,255,.6)', marginTop: 8 }}>
          {tier === 'free' ? 'Unlock the radar, draft hindsight, and the one play that beats them.' : 'Available to managers in your leagues.'}
        </div>
      </div>
      <button style={{ background: '#fff', color: 'var(--ft-ink,#1D1D1F)', border: 'none', borderRadius: 980, padding: '11px 26px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Unlock Scout Mode</button>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// DossierRail — pinned fingerprint + tab jump links in the shell rail.
// ---------------------------------------------------------------------------

export const DossierRail = ({ data }) => {
  if (!data || (data.tier !== 'pro' && data.tier !== 'self') || !data.fingerprint) return null;
  const conf = data.fingerprint.confidence || data.archetype?.confidence || 'low';
  const filled = CONF_FILL[conf] || 1;
  const centers = data.blueprint?.centers;
  return (
    <div className="ftd rail">
      <style>{CSS}</style>
      <div className="card dark" style={{ padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {centers ? <Bodygraph centers={centers} compact /> : <Radar axes={relabelAxes(data.fingerprint.axes)} size={120} />}
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-.01em', marginTop: 10 }}>{data.archetype?.name || '—'}</div>
        {data.archetype?.tagline && <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.55)', marginTop: 1 }}>{data.archetype.tagline}</div>}
        <div className="conf">{[0, 1, 2, 3].map((i) => <i key={i} className={i < filled ? 'f' : ''} />)} {conf} confidence</div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

// 4-tab IA (internal ids kept stable for lazy-load: history=Trades, portfolio=Leagues).
const TABS = ['read', 'history', 'drafts', 'portfolio'];

export default function DossierSection({ sleeperUsername, onData, leagues: legacyLeagues }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [activeTab, setActiveTab] = useState('read');

  // Secondary datasets (fail soft — their tabs show empty states on error).
  const [trades, setTrades] = useState([]);
  const [tradesLoading, setTradesLoading] = useState(false);
  const [playerData, setPlayerData] = useState({});
  const [nflTeams, setNflTeams] = useState([]);
  const [profileLeagues, setProfileLeagues] = useState([]);
  const [dynastyYoY, setDynastyYoY] = useState([]);
  const [dynastyStatus, setDynastyStatus] = useState('not_started');
  const [dynastyLoading, setDynastyLoading] = useState(false);

  const loadedTabs = useRef({ history: false, portfolio: false });

  // Primary dossier fetch. Polls if the profile is still computing.
  useEffect(() => {
    let cancelled = false;
    let pollTimer = null;
    setLoading(true); setData(null); setErrored(false);
    setActiveTab('read');
    setProfileLeagues([]);
    loadedTabs.current = { history: false, portfolio: false };

    let computingRetries = 0;
    const fetchDossier = () => {
      managerIntelAPI.getDossier(sleeperUsername)
        .then((res) => {
          if (cancelled) return;
          if (res.data?.status === 'computing' && computingRetries < 60) {
            computingRetries++;
            pollTimer = setTimeout(fetchDossier, 2000);
            return;
          }
          setData(res.data); setLoading(false); if (onData) onData(res.data);
        })
        .catch(() => { if (!cancelled) { setErrored(true); setLoading(false); if (onData) onData(null); } });
    };

    fetchDossier();
    return () => { cancelled = true; clearTimeout(pollTimer); if (onData) onData(null); };
  }, [sleeperUsername, onData]);

  // NFL exposure source: legacy profile leagues[].currentRoster + player batch.
  // Loaded once the dossier confirms a full-access tier. `legacyLeagues` comes
  // from the parent's own getProfile() fetch — the parent already pays for that
  // call, so we read its result instead of issuing a second, redundant one.
  useEffect(() => {
    if (!data || (data.tier !== 'pro' && data.tier !== 'self')) return;
    let cancelled = false;
    const leagues = legacyLeagues || [];
    // Stash the raw league array so the Portfolio tab can resolve
    // DynastyYoY leagueIds → names/records (same source the legacy profile uses).
    setProfileLeagues(leagues);
    // Dedupe rolled-over leagues: drop any league whose sleeperLeagueId is a
    // previousLeagueId of another (keep the newest version).
    const hasNext = new Set();
    leagues.forEach((l) => { if (l.settings?.previousLeagueId) hasNext.add(l.settings.previousLeagueId); });
    const merged = leagues.filter((l) => !hasNext.has(l.sleeperLeagueId));
    const ids = new Set();
    merged.forEach((l) => (l.currentRoster || []).forEach((pid) => ids.add(pid)));
    if (ids.size === 0) { setNflTeams([]); return; }
    playersAPI.getBatch(Array.from(ids))
      .then((pres) => {
        if (cancelled) return;
        const players = pres.data.players || {};
        setPlayerData((prev) => ({ ...prev, ...players }));
        // Group players by NFL team across merged leagues; startable = top-value at position.
        const teamMap = {};
        const seen = new Set();
        merged.forEach((l) => (l.currentRoster || []).forEach((pid) => {
          const p = players[pid];
          if (!p || !p.team || p.team === 'FA') return;
          const key = `${pid}_${p.team}`;
          if (seen.has(key)) return; seen.add(key);
          if (!teamMap[p.team]) teamMap[p.team] = { team: p.team, players: 0, startable: 0 };
          teamMap[p.team].players += 1;
          // "startable" heuristic: a meaningful dynasty asset (KTC value above a floor).
          if ((p.ktc_value_sf || p.ktc_value || 0) >= 2000) teamMap[p.team].startable += 1;
        }));
        const arr = Object.values(teamMap).sort((a, b) => b.players - a.players).slice(0, 12);
        setNflTeams(arr);
      })
      .catch(() => { if (!cancelled) setNflTeams([]); });
    return () => { cancelled = true; };
  }, [data, legacyLeagues]);

  // History tab — lazy load trades + resolve player names.
  useEffect(() => {
    if (activeTab !== 'history' || loadedTabs.current.history) return;
    if (!data || (data.tier !== 'pro' && data.tier !== 'self')) return;
    loadedTabs.current.history = true;
    let cancelled = false;
    setTradesLoading(true);
    managerIntelAPI.getTrades(sleeperUsername)
      .then(async (res) => {
        if (cancelled) return;
        const rows = res.data.trades || [];
        setTrades(rows);
        const ids = new Set();
        rows.forEach((t) => { Object.keys(t.adds || {}).forEach((id) => ids.add(id)); Object.keys(t.drops || {}).forEach((id) => ids.add(id)); });
        const missing = Array.from(ids).filter((id) => !playerData[id]);
        if (missing.length > 0) {
          const pres = await playersAPI.getBatch(missing);
          if (!cancelled) setPlayerData((prev) => ({ ...prev, ...(pres.data.players || {}) }));
        }
        if (!cancelled) setTradesLoading(false);
      })
      .catch(() => { if (!cancelled) { setTrades([]); setTradesLoading(false); } });
    return () => { cancelled = true; };
  }, [activeTab, data, sleeperUsername, playerData]);

  // Portfolio tab — lazy load dynasty YoY + status.
  useEffect(() => {
    if (activeTab !== 'portfolio' || loadedTabs.current.portfolio) return;
    if (!data || (data.tier !== 'pro' && data.tier !== 'self')) return;
    loadedTabs.current.portfolio = true;
    let cancelled = false;
    setDynastyLoading(true);
    Promise.all([
      managerIntelAPI.getDynastyYoY(sleeperUsername).catch(() => ({ data: { dynastyYoY: [] } })),
      managerIntelAPI.getDynastyYoYStatus(sleeperUsername).catch(() => ({ data: { status: 'not_started' } })),
    ]).then(([yoyRes, statusRes]) => {
      if (cancelled) return;
      setDynastyYoY(yoyRes.data.dynastyYoY || yoyRes.data || []);
      setDynastyStatus(statusRes.data.status || 'complete');
      setDynastyLoading(false);
    });
    return () => { cancelled = true; };
  }, [activeTab, data, sleeperUsername]);

  const refreshDynasty = async () => {
    try {
      const res = await managerIntelAPI.getDynastyYoY(sleeperUsername);
      setDynastyYoY(res.data.dynastyYoY || []);
      setDynastyStatus('complete');
    } catch (e) { /* leave as-is */ }
  };

  // Merge rolled-over league seasons into one entry per chain, building the
  // originalLeagueIds trail DynastyYoYSection uses to resolve names/records.
  // Mirrors ManagerIntelProfile's mergedLeagues so the Portfolio tab populates.
  const mergedProfileLeagues = useMemo(() => {
    const merged = [];
    const processedSleeperIds = new Set();

    const hasNextVersion = new Set();
    profileLeagues.forEach((league) => {
      if (league.settings?.previousLeagueId) {
        hasNextVersion.add(league.settings.previousLeagueId);
      }
    });

    const sortedLeagues = [...profileLeagues].sort((a, b) => parseInt(b.season) - parseInt(a.season));

    sortedLeagues.forEach((league) => {
      const sleeperLeagueId = league.sleeperLeagueId;
      if (processedSleeperIds.has(sleeperLeagueId)) return;

      // A rolled-over predecessor is shown via its newer version, not here.
      if (hasNextVersion.has(sleeperLeagueId)) {
        processedSleeperIds.add(sleeperLeagueId);
        return;
      }

      const originalLeagueIds = [league.id];
      let currentLeague = league;
      let avatarUrl = league.avatarUrl;
      let previousSeasonRecord = null;

      while (currentLeague.settings?.previousLeagueId) {
        const prevId = currentLeague.settings.previousLeagueId;
        const prevLeague = profileLeagues.find((l) => l.sleeperLeagueId === prevId);
        if (prevLeague) {
          originalLeagueIds.push(prevLeague.id);
          processedSleeperIds.add(prevLeague.sleeperLeagueId);
          if (!avatarUrl && prevLeague.avatarUrl) avatarUrl = prevLeague.avatarUrl;
          const prevRecord = prevLeague.record;
          if (!previousSeasonRecord && prevRecord && ((prevRecord.wins || 0) + (prevRecord.losses || 0) + (prevRecord.ties || 0)) > 0) {
            previousSeasonRecord = prevRecord;
          }
          currentLeague = prevLeague;
        } else {
          break;
        }
      }

      processedSleeperIds.add(sleeperLeagueId);
      merged.push({ ...league, originalLeagueIds, avatarUrl, previousSeasonRecord });
    });

    return merged;
  }, [profileLeagues]);

  if (errored) return null;
  if (loading || !data) return (
    <div className="ftd">
      <style>{CSS}</style>
      <div className="tabbar" style={{ borderBottom: '1px solid var(--hair)', marginTop: 26, display: 'flex', gap: 4 }}>
        {['Blueprint', 'Trades', 'Drafts', 'Trophy case'].map((label) => (
          <div key={label} className="sk" style={{ width: label.length * 8 + 24, height: 32, borderRadius: 8, margin: '0 4px 12px' }} />
        ))}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
        <div className="sk" style={{ height: 120, borderRadius: 16 }} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="sk" style={{ height: 80, borderRadius: 16 }} />
          <div className="sk" style={{ height: 80, borderRadius: 16 }} />
        </div>
        <div className="sk" style={{ height: 160, borderRadius: 16 }} />
        <div className="sk" style={{ height: 90, borderRadius: 16 }} />
      </div>
    </div>
  );
  const tier = data.tier;
  if (tier === 'visitor' || tier === 'free') return <Teaser tier={tier} archetype={data.archetype} />;

  const pt = data.paperTrail;
  const partnerCount = data.traits?.network?.counterparties;
  const tradeCount = data.identity?.tradeCount;
  const drafterGrade = pt?.drafterGrade;
  const portfolioCount = data.identity?.currentLeagueCount;

  const leagueCount = data.identity?.dynastyLeagueCount ?? data.situation?.leagues?.length ?? null;
  const tabMeta = {
    read: { label: 'Blueprint', badge: null },
    history: { label: 'Trades', badge: tradeCount, title: `${tradeCount ?? 0} trades` },
    drafts: { label: 'Drafts', badge: drafterGrade, title: 'Drafter grade' },
    portfolio: { label: 'Trophy case', badge: leagueCount, title: 'Trophy case — career, leagues and partners' },
  };

  return (
    <div className="ftd">
      <style>{CSS}</style>

      <div className="tabbar" role="tablist">
        {TABS.map((id) => {
          const m = tabMeta[id];
          return (
            <button
              type="button"
              id={`ftd-tab-${id}`}
              key={id}
              role="tab"
              aria-selected={activeTab === id}
              className={`tab${activeTab === id ? ' on' : ''}`}
              onClick={() => setActiveTab(id)}
            >
              {m.label}{m.badge != null && m.badge !== '' && <span className="ct num" title={m.title}>{m.badge}</span>}
            </button>
          );
        })}
      </div>

      {activeTab === 'read' && (
        <>
          <Blueprint blueprint={data.blueprint} archetype={data.archetype} fingerprint={data.fingerprint} rarity={data.archetypeRarity} />
          <ReadPanel data={data} nflTeams={nflTeams} />
        </>
      )}
      {activeTab === 'history' && <TradesPanel data={data} trades={trades} playerData={playerData} loading={tradesLoading} />}
      {activeTab === 'drafts' && <DraftsPanel pt={pt} />}
      {activeTab === 'portfolio' && (
        <LeaguesPanel
          data={data}
          nflTeams={nflTeams}
          dynastyYoY={dynastyYoY}
          leagues={profileLeagues}
          mergedLeagues={mergedProfileLeagues}
          status={dynastyStatus}
          sleeperUsername={sleeperUsername}
          onUpdate={refreshDynasty}
          loading={dynastyLoading}
        />
      )}
    </div>
  );
}
